import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface IconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
  'data-testid'?: string;
}

export const CircleIcon = (props: IconProps) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <circle cx="16" cy="16" r="11" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export const DashedCircleIcon = (props: IconProps) => (
  <svg
    data-testid="itemNotDone"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"
      stroke="currentColor"
      strokeWidth={props.strokeWidth || 2}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeDasharray={`${props.strokeDasharray || '4 4'}`}
    />
  </svg>
);

export const BranchIcon = ({ className, ...props }: IconProps) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
    className={className || ''}
  >
    <path
      d="M6.75 7.875a2.625 2.625 0 1 0 0-5.25 2.625 2.625 0 0 0 0 5.25zM17.25 7.875a2.625 2.625 0 1 0 0-5.25 2.625 2.625 0 0 0 0 5.25zM6.75 21.375a2.625 2.625 0 1 0 0-5.25 2.625 2.625 0 0 0 0 5.25z"
      stroke="currentColor"
      strokeWidth={props.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7.875 12.375c4.5 0 9 .75 9-3.75m-9.75 6.75v-6.75"
      stroke="currentColor"
      strokeWidth={props.strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const LoadingIcon = forwardRef<SVGSVGElement, IconProps>(
  ({ className, ...props }, ref) => (
    <svg
      {...props}
      ref={ref}
      data-testid="loadingIcon"
      className={`animate-spin ${className}`}
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="6"
        cy="6"
        r="5"
        className="opacity-25"
        stroke="currentColor"
        strokeWidth={1.5}
      />
      <path
        d="M1 6C1.00003 3.5 2.6667 1 6 1"
        stroke="currentColor"
        strokeWidth={1.5}
      />
    </svg>
  ),
);

export const GithubIcon = (
  <svg
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2C10.6868 2 9.38642 2.25866 8.17317 2.7612C6.95991 3.26375 5.85752 4.00035 4.92893 4.92893C3.05357 6.8043 2 9.34784 2 12C2 16.42 4.87 20.17 8.84 21.5C9.34 21.58 9.5 21.27 9.5 21V19.31C6.73 19.91 6.14 17.97 6.14 17.97C5.68 16.81 5.03 16.5 5.03 16.5C4.12 15.88 5.1 15.9 5.1 15.9C6.1 15.97 6.63 16.93 6.63 16.93C7.5 18.45 8.97 18 9.54 17.76C9.63 17.11 9.89 16.67 10.17 16.42C7.95 16.17 5.62 15.31 5.62 11.5C5.62 10.39 6 9.5 6.65 8.79C6.55 8.54 6.2 7.5 6.75 6.15C6.75 6.15 7.59 5.88 9.5 7.17C10.29 6.95 11.15 6.84 12 6.84C12.85 6.84 13.71 6.95 14.5 7.17C16.41 5.88 17.25 6.15 17.25 6.15C17.8 7.5 17.45 8.54 17.35 8.79C18 9.5 18.38 10.39 18.38 11.5C18.38 15.32 16.04 16.16 13.81 16.41C14.17 16.72 14.5 17.33 14.5 18.26V21C14.5 21.27 14.66 21.59 15.17 21.5C19.14 20.16 22 16.42 22 12C22 10.6868 21.7413 9.38642 21.2388 8.17317C20.7362 6.95991 19.9997 5.85752 19.0711 4.92893C18.1425 4.00035 17.0401 3.26375 15.8268 2.7612C14.6136 2.25866 13.3132 2 12 2Z"
      fill="currentColor"
    />
  </svg>
);

export const FacebookIcon = (
  <svg
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.6406 1H3.35687C2.73179 1 2.13231 1.24831 1.69031 1.69031C1.24831 2.13231 1 2.73179 1 3.35687L1 20.6406C1 21.2657 1.24831 21.8652 1.69031 22.3072C2.13231 22.7492 2.73179 22.9975 3.35687 22.9975H10.0961V15.5188H7.00267V11.9987H10.0961V9.31584C10.0961 6.26418 11.9128 4.57852 14.6954 4.57852C16.028 4.57852 17.4215 4.81617 17.4215 4.81617V7.81137H15.8861C14.3733 7.81137 13.9014 8.75019 13.9014 9.71307V11.9987H17.2786L16.7385 15.5188H13.9014V22.9975H20.6406C21.2657 22.9975 21.8652 22.7492 22.3072 22.3072C22.7492 21.8652 22.9975 21.2657 22.9975 20.6406V3.35687C22.9975 2.73179 22.7492 2.13231 22.3072 1.69031C21.8652 1.24831 21.2657 1 20.6406 1Z"
      fill="currentColor"
    />
  </svg>
);

export const InstagramIcon = (
  <svg
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.465 1.066C8.638 1.012 9.012 1 12 1C14.988 1 15.362 1.013 16.534 1.066C17.706 1.119 18.506 1.306 19.206 1.577C19.939 1.854 20.604 2.287 21.154 2.847C21.714 3.396 22.146 4.06 22.422 4.794C22.694 5.494 22.88 6.294 22.934 7.464C22.988 8.639 23 9.013 23 12C23 14.988 22.987 15.362 22.934 16.535C22.881 17.705 22.694 18.505 22.422 19.205C22.146 19.9391 21.7133 20.6042 21.154 21.154C20.604 21.714 19.939 22.146 19.206 22.422C18.506 22.694 17.706 22.88 16.536 22.934C15.362 22.988 14.988 23 12 23C9.012 23 8.638 22.987 7.465 22.934C6.295 22.881 5.495 22.694 4.795 22.422C4.06092 22.146 3.39582 21.7133 2.846 21.154C2.28638 20.6047 1.85331 19.9399 1.577 19.206C1.306 18.506 1.12 17.706 1.066 16.536C1.012 15.361 1 14.987 1 12C1 9.012 1.013 8.638 1.066 7.466C1.119 6.294 1.306 5.494 1.577 4.794C1.85372 4.06008 2.28712 3.39531 2.847 2.846C3.39604 2.2865 4.06047 1.85344 4.794 1.577C5.494 1.306 6.294 1.12 7.464 1.066H7.465ZM16.445 3.046C15.285 2.993 14.937 2.982 12 2.982C9.063 2.982 8.715 2.993 7.555 3.046C6.482 3.095 5.9 3.274 5.512 3.425C4.999 3.625 4.632 3.862 4.247 4.247C3.88205 4.60205 3.60118 5.03428 3.425 5.512C3.274 5.9 3.095 6.482 3.046 7.555C2.993 8.715 2.982 9.063 2.982 12C2.982 14.937 2.993 15.285 3.046 16.445C3.095 17.518 3.274 18.1 3.425 18.488C3.601 18.965 3.882 19.398 4.247 19.753C4.602 20.118 5.035 20.399 5.512 20.575C5.9 20.726 6.482 20.905 7.555 20.954C8.715 21.007 9.062 21.018 12 21.018C14.938 21.018 15.285 21.007 16.445 20.954C17.518 20.905 18.1 20.726 18.488 20.575C19.001 20.375 19.368 20.138 19.753 19.753C20.118 19.398 20.399 18.965 20.575 18.488C20.726 18.1 20.905 17.518 20.954 16.445C21.007 15.285 21.018 14.937 21.018 12C21.018 9.063 21.007 8.715 20.954 7.555C20.905 6.482 20.726 5.9 20.575 5.512C20.375 4.999 20.138 4.632 19.753 4.247C19.3979 3.88207 18.9657 3.60121 18.488 3.425C18.1 3.274 17.518 3.095 16.445 3.046ZM10.595 15.391C11.3797 15.7176 12.2534 15.7617 13.0669 15.5157C13.8805 15.2697 14.5834 14.7489 15.0556 14.0422C15.5278 13.3356 15.7401 12.4869 15.656 11.6411C15.572 10.7953 15.197 10.005 14.595 9.405C14.2112 9.02148 13.7472 8.72781 13.2363 8.54515C12.7255 8.36248 12.1804 8.29536 11.6405 8.34862C11.1006 8.40187 10.5792 8.57418 10.1138 8.85313C9.64845 9.13208 9.25074 9.51074 8.9493 9.96185C8.64786 10.413 8.45019 10.9253 8.37052 11.462C8.29084 11.9986 8.33115 12.5463 8.48854 13.0655C8.64593 13.5847 8.91648 14.0626 9.28072 14.4647C9.64496 14.8668 10.0938 15.1832 10.595 15.391ZM8.002 8.002C8.52702 7.47697 9.15032 7.0605 9.8363 6.77636C10.5223 6.49222 11.2575 6.34597 12 6.34597C12.7425 6.34597 13.4777 6.49222 14.1637 6.77636C14.8497 7.0605 15.473 7.47697 15.998 8.002C16.523 8.52702 16.9395 9.15032 17.2236 9.8363C17.5078 10.5223 17.654 11.2575 17.654 12C17.654 12.7425 17.5078 13.4777 17.2236 14.1637C16.9395 14.8497 16.523 15.473 15.998 15.998C14.9377 17.0583 13.4995 17.654 12 17.654C10.5005 17.654 9.06234 17.0583 8.002 15.998C6.94166 14.9377 6.34597 13.4995 6.34597 12C6.34597 10.5005 6.94166 9.06234 8.002 8.002ZM18.908 7.188C19.0381 7.06527 19.1423 6.91768 19.2143 6.75397C19.2863 6.59027 19.3248 6.41377 19.3274 6.23493C19.33 6.05609 19.2967 5.87855 19.2295 5.71281C19.1622 5.54707 19.0624 5.39651 18.936 5.27004C18.8095 5.14357 18.6589 5.04376 18.4932 4.97652C18.3275 4.90928 18.1499 4.87598 17.9711 4.87858C17.7922 4.88119 17.6157 4.91965 17.452 4.9917C17.2883 5.06374 17.1407 5.1679 17.018 5.298C16.7793 5.55103 16.6486 5.88712 16.6537 6.23493C16.6588 6.58274 16.7992 6.91488 17.0452 7.16084C17.2911 7.40681 17.6233 7.54723 17.9711 7.5523C18.3189 7.55737 18.655 7.42669 18.908 7.188Z"
      fill="currentColor"
    />
  </svg>
);

export const YoutubeIcon = (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    data-icon="youtube"
    className={`w-6 h-6`}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M941.3 296.1a112.3 112.3 0 00-79.2-79.3C792.2 198 512 198 512 198s-280.2 0-350.1 18.7A112.12 112.12 0 0082.7 296C64 366 64 512 64 512s0 146 18.7 215.9c10.3 38.6 40.7 69 79.2 79.3C231.8 826 512 826 512 826s280.2 0 350.1-18.8c38.6-10.3 68.9-40.7 79.2-79.3C960 658 960 512 960 512s0-146-18.7-215.9zM423 646V378l232 133-232 135z"></path>
  </svg>
);

export const RobloxIcon = (
  <svg
    role="img"
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Roblox</title>
    <path
      fill={'currentColor'}
      d="m13.383 14.341-3.726-.958.959-3.726 3.726.959-.96 3.726zM4.913 0 0 19.088 19.088 24 24 4.912 4.912 0z"
    />
  </svg>
);

export const TikTokIcon = (
  <svg
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    fill={'currentColor'}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.5901 6.69C18.5933 6.47079 17.6915 5.94144 17.0142 5.17789C16.3369 4.41435 15.9189 3.45587 15.8201 2.44V2H12.3701V15.67C12.3708 16.2766 12.1807 16.868 11.8266 17.3604C11.4725 17.8529 10.9724 18.2215 10.3972 18.414C9.82199 18.6065 9.2008 18.6131 8.62162 18.4329C8.04244 18.2527 7.53464 17.8948 7.17015 17.41C6.84359 16.9808 6.64363 16.4689 6.59287 15.932C6.54212 15.3951 6.64259 14.8548 6.88292 14.372C7.12326 13.8892 7.49388 13.4834 7.95286 13.2003C8.41184 12.9172 8.94088 12.7681 9.48014 12.77C9.77839 12.7683 10.0751 12.8121 10.3601 12.9V9.4C10.0285 9.35893 9.69425 9.34222 9.36015 9.35C8.12946 9.38288 6.93506 9.77393 5.92323 10.4753C4.91139 11.1766 4.12604 12.1578 3.66332 13.2986C3.20061 14.4395 3.08062 15.6905 3.31806 16.8986C3.5555 18.1066 4.14006 19.2191 5.00015 20.1C5.88163 20.9962 7.00964 21.6101 8.24084 21.8638C9.47204 22.1175 10.7509 21.9994 11.9148 21.5246C13.0787 21.0498 14.0753 20.2397 14.7778 19.1973C15.4803 18.1548 15.857 16.9271 15.8601 15.67V8.67C17.2516 9.66395 18.9202 10.1956 20.6301 10.19V6.79C20.2942 6.79139 19.9591 6.75787 19.6301 6.69H19.5901Z"
      fill="currentColor"
    />
  </svg>
);

export const LinkedinIcon = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`w-6 h-6`}
    viewBox="0 0 24 24"
    fill={'currentCOlor'}
    strokeWidth="2"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M1 2.838A1.838 1.838 0 0 1 2.838 1H21.16A1.837 1.837 0 0 1 23 2.838V21.16A1.838 1.838 0 0 1 21.161 23H2.838A1.838 1.838 0 0 1 1 21.161V2.838zm8.708 6.55h2.979v1.496c.43-.86 1.53-1.634 3.183-1.634 3.169 0 3.92 1.713 3.92 4.856v5.822h-3.207v-5.106c0-1.79-.43-2.8-1.522-2.8-1.515 0-2.145 1.089-2.145 2.8v5.106H9.708V9.388zm-5.5 10.403h3.208V9.25H4.208v10.54zM7.875 5.812a2.063 2.063 0 1 1-4.125 0 2.063 2.063 0 0 1 4.125 0z"
    />
  </svg>
);
export const LinkIcon = (
  <svg
    className={`w-6 h-6`}
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2}
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244"
    />
  </svg>
);

export const SolidGradientCheckCircleIcon = ({ className = '' }: IconProps) => (
  <svg
    data-testid="itemDone"
    id="itemDone"
    className={className}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM11.7071 6.70711C12.0976 6.31658 12.0976 5.68342 11.7071 5.29289C11.3166 4.90237 10.6834 4.90237 10.2929 5.29289L7 8.58579L5.70711 7.29289C5.31658 6.90237 4.68342 6.90237 4.29289 7.29289C3.90237 7.68342 3.90237 8.31658 4.29289 8.70711L6.29289 10.7071C6.68342 11.0976 7.31658 11.0976 7.70711 10.7071L11.7071 6.70711Z"
      fill="url(#horizontal_gradient)"
    />
    <defs>
      <linearGradient
        id="horizontal_gradient"
        x1="1.5"
        y1="5.42857"
        x2="16.1911"
        y2="-4.96379"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--color-secondary)" />
        <stop offset="1" stopColor="var(--color-primary)" />
      </linearGradient>
    </defs>
  </svg>
);

export const OutlineGradientCheckCircleIcon = ({
  className = '',
}: IconProps) => (
  <svg
    id="itemNotDone"
    className={className}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.5 9L8.16667 10.6667L11.5 7.33333M16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z"
      stroke="url(#horizontal_gradient)"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient
        id="horizontal_gradient"
        x1="1.5"
        y1="5.42857"
        x2="16.1911"
        y2="-4.96379"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="var(--primary)" />
        <stop offset="1" stopColor="var(--primary)" />
      </linearGradient>
    </defs>
  </svg>
);

export const QuizActivityIcon = ({ className = '' }: IconProps) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 16 14"
    fill="none"
    id="QuizActivityIcon"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.08536 1.58513C4.18472 1.4785 4.23881 1.33746 4.23624 1.19174C4.23367 1.04601 4.17464 0.906972 4.07158 0.803913C3.96852 0.700853 3.82948 0.641819 3.68375 0.639248C3.53803 0.636677 3.39699 0.690769 3.29036 0.790129L1.81286 2.26763L1.46036 1.91513C1.35373 1.81577 1.21269 1.76168 1.06697 1.76425C0.921242 1.76682 0.782203 1.82585 0.679143 1.92891C0.576083 2.03197 0.517049 2.17101 0.514478 2.31674C0.511907 2.46246 0.565999 2.6035 0.665359 2.71013L1.41536 3.46013C1.52083 3.56547 1.6638 3.62463 1.81286 3.62463C1.96192 3.62463 2.10489 3.56547 2.21036 3.46013L4.08536 1.58513ZM14.9379 11.5054H6.31286L6.23636 11.5106C6.09517 11.5301 5.96662 11.6023 5.87668 11.7129C5.78675 11.8235 5.74213 11.964 5.75184 12.1062C5.76155 12.2484 5.82488 12.3816 5.92902 12.4789C6.03315 12.5762 6.17034 12.6303 6.31286 12.6304H14.9379L15.0144 12.6251C15.1555 12.6057 15.2841 12.5334 15.374 12.4229C15.464 12.3123 15.5086 12.1717 15.4989 12.0295C15.4892 11.8874 15.4258 11.7542 15.3217 11.6569C15.2176 11.5596 15.0804 11.5054 14.9379 11.5054ZM14.9379 6.62513H6.31286L6.23636 6.63038C6.09517 6.64981 5.96662 6.7221 5.87668 6.83265C5.78675 6.94321 5.74213 7.08378 5.75184 7.22597C5.76155 7.36816 5.82488 7.50135 5.92902 7.59865C6.03315 7.69595 6.17034 7.75009 6.31286 7.75013H14.9379L15.0144 7.74488C15.1555 7.72545 15.2841 7.65316 15.374 7.5426C15.464 7.43205 15.5086 7.29148 15.4989 7.14929C15.4892 7.0071 15.4258 6.8739 15.3217 6.77661C15.2176 6.67931 15.0804 6.62517 14.9379 6.62513ZM14.9379 1.75013H6.31286L6.23636 1.75538C6.09517 1.77481 5.96662 1.8471 5.87668 1.95765C5.78675 2.06821 5.74213 2.20878 5.75184 2.35097C5.76155 2.49316 5.82488 2.62635 5.92902 2.72365C6.03315 2.82095 6.17034 2.87509 6.31286 2.87513H14.9379L15.0144 2.86988C15.1555 2.85045 15.2841 2.77816 15.374 2.6676C15.464 2.55705 15.5086 2.41648 15.4989 2.27429C15.4892 2.1321 15.4258 1.9989 15.3217 1.90161C15.2176 1.80431 15.0804 1.75017 14.9379 1.75013ZM4.08536 11.3351C4.14062 11.2836 4.18495 11.2215 4.2157 11.1525C4.24644 11.0835 4.26297 11.009 4.2643 10.9335C4.26564 10.858 4.25174 10.783 4.22345 10.7129C4.19516 10.6429 4.15305 10.5793 4.09964 10.5259C4.04622 10.4724 3.9826 10.4303 3.91256 10.402C3.84252 10.3737 3.76749 10.3599 3.69197 10.3612C3.61644 10.3625 3.54196 10.379 3.47296 10.4098C3.40396 10.4405 3.34186 10.4849 3.29036 10.5401L1.81286 12.0176L1.46036 11.6651C1.35373 11.5658 1.21269 11.5117 1.06697 11.5142C0.921242 11.5168 0.782203 11.5759 0.679143 11.6789C0.576083 11.782 0.517049 11.921 0.514478 12.0667C0.511907 12.2125 0.565999 12.3535 0.665359 12.4601L1.41536 13.2101C1.52083 13.3155 1.6638 13.3746 1.81286 13.3746C1.96192 13.3746 2.10489 13.3155 2.21036 13.2101L4.08536 11.3351ZM4.08536 5.66513C4.1907 5.7706 4.24986 5.91357 4.24986 6.06263C4.24986 6.21169 4.1907 6.35466 4.08536 6.46013L2.21036 8.33513C2.10489 8.44047 1.96192 8.49963 1.81286 8.49963C1.6638 8.49963 1.52083 8.44047 1.41536 8.33513L0.665359 7.58513C0.610094 7.53363 0.565768 7.47153 0.535024 7.40253C0.50428 7.33353 0.487748 7.25905 0.486416 7.18352C0.485083 7.10799 0.498976 7.03297 0.527267 6.96293C0.555558 6.89289 0.597666 6.82926 0.651081 6.77585C0.704495 6.72244 0.76812 6.68033 0.838161 6.65204C0.908202 6.62375 0.983224 6.60985 1.05875 6.61119C1.13428 6.61252 1.20876 6.62905 1.27776 6.65979C1.34676 6.69054 1.40886 6.73486 1.46036 6.79013L1.81286 7.14263L3.29036 5.66513C3.39583 5.55979 3.5388 5.50062 3.68786 5.50062C3.83692 5.50062 3.97989 5.55979 4.08536 5.66513Z"
      fill="currentColor"
    />
  </svg>
);

export const TextActivityIcon = ({ className = '' }: IconProps) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 14 12"
    fill="none"
    id="TextActivityIcon"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.75 11.0625C4.75 10.9133 4.80926 10.7702 4.91475 10.6648C5.02024 10.5593 5.16332 10.5 5.3125 10.5H13.1875C13.3367 10.5 13.4798 10.5593 13.5852 10.6648C13.6907 10.7702 13.75 10.9133 13.75 11.0625C13.75 11.2117 13.6907 11.3548 13.5852 11.4602C13.4798 11.5657 13.3367 11.625 13.1875 11.625H5.3125C5.16332 11.625 5.02024 11.5657 4.91475 11.4602C4.80926 11.3548 4.75 11.2117 4.75 11.0625ZM0.25 7.6875C0.25 7.53832 0.309263 7.39524 0.414753 7.28975C0.520242 7.18426 0.663316 7.125 0.8125 7.125H13.1875C13.3367 7.125 13.4798 7.18426 13.5852 7.28975C13.6907 7.39524 13.75 7.53832 13.75 7.6875C13.75 7.83668 13.6907 7.97976 13.5852 8.08525C13.4798 8.19074 13.3367 8.25 13.1875 8.25H0.8125C0.663316 8.25 0.520242 8.19074 0.414753 8.08525C0.309263 7.97976 0.25 7.83668 0.25 7.6875ZM4.75 4.3125C4.75 4.16332 4.80926 4.02024 4.91475 3.91475C5.02024 3.80926 5.16332 3.75 5.3125 3.75H13.1875C13.3367 3.75 13.4798 3.80926 13.5852 3.91475C13.6907 4.02024 13.75 4.16332 13.75 4.3125C13.75 4.46168 13.6907 4.60476 13.5852 4.71025C13.4798 4.81574 13.3367 4.875 13.1875 4.875H5.3125C5.16332 4.875 5.02024 4.81574 4.91475 4.71025C4.80926 4.60476 4.75 4.46168 4.75 4.3125ZM0.25 0.9375C0.25 0.788316 0.309263 0.645242 0.414753 0.539753C0.520242 0.434263 0.663316 0.375 0.8125 0.375H13.1875C13.3367 0.375 13.4798 0.434263 13.5852 0.539753C13.6907 0.645242 13.75 0.788316 13.75 0.9375C13.75 1.08668 13.6907 1.22976 13.5852 1.33525C13.4798 1.44074 13.3367 1.5 13.1875 1.5H0.8125C0.663316 1.5 0.520242 1.44074 0.414753 1.33525C0.309263 1.22976 0.25 1.08668 0.25 0.9375Z"
      fill="currentColor"
    />
  </svg>
);

export const VideoActivityIcon = ({ className = '' }: IconProps) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 16 14"
    fill="none"
    id="VideoActivityIcon"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.25 1V13M11.75 1V13M1.25 4H4.25M11.75 4H14.75M1.25 7H14.75M1.25 10H4.25M11.75 10H14.75M2 13H14C14.4142 13 14.75 12.6642 14.75 12.25V1.75C14.75 1.33579 14.4142 1 14 1H2C1.58579 1 1.25 1.33579 1.25 1.75V12.25C1.25 12.6642 1.58579 13 2 13Z"
      stroke="currentColor"
    />
  </svg>
);

export const TrueOrFalseIcon = ({ className = '' }: IconProps) => (
  <svg
    className={`w-4 h-4 ${className}`}
    viewBox="0 0 20 18"
    fill="none"
    id="TrueOrFalseIcon"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.16667 6.11947L5 5.28613M5 5.28613L5.83333 4.4528M5 5.28613L4.16667 4.4528M5 5.28613L5.83333 6.11947M8.75 5.28613C8.75 7.3572 7.07107 9.03613 5 9.03613C2.92893 9.03613 1.25 7.3572 1.25 5.28613C1.25 3.21506 2.92893 1.53613 5 1.53613C7.07107 1.53613 8.75 3.21506 8.75 5.28613Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.75 12.2861L14.5833 13.1195L16.25 11.4528M18.75 12.2861C18.75 14.3572 17.0711 16.0361 15 16.0361C12.9289 16.0361 11.25 14.3572 11.25 12.2861C11.25 10.2151 12.9289 8.53613 15 8.53613C17.0711 8.53613 18.75 10.2151 18.75 12.2861Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DragIcon = ({ className = '', ...props }: IconProps) => (
  <svg
    className={twMerge('h-6 w-6 text-base-content', className)}
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g opacity="0.4">
      <path
        d="M4.08388 2.33333C4.08388 2.6555 4.34505 2.91667 4.66722 2.91667C4.98938 2.91667 5.25055 2.6555 5.25055 2.33333C5.25055 2.01117 4.98938 1.75 4.66722 1.75C4.34505 1.75 4.08388 2.01117 4.08388 2.33333Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M8.75026 2.33333C8.75026 2.6555 9.01143 2.91667 9.3336 2.91667C9.65576 2.91667 9.91693 2.6555 9.91693 2.33333C9.91693 2.01117 9.65576 1.75 9.3336 1.75C9.01143 1.75 8.75026 2.01117 8.75026 2.33333Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M4.08388 7.00008C4.08388 7.32225 4.34505 7.58341 4.66722 7.58341C4.98938 7.58341 5.25055 7.32225 5.25055 7.00008C5.25055 6.67792 4.98938 6.41675 4.66722 6.41675C4.34505 6.41675 4.08388 6.67792 4.08388 7.00008Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M8.75026 7.00008C8.75026 7.32225 9.01143 7.58341 9.3336 7.58341C9.65576 7.58341 9.91693 7.32225 9.91693 7.00008C9.91693 6.67792 9.65576 6.41675 9.3336 6.41675C9.01143 6.41675 8.75026 6.67792 8.75026 7.00008Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M4.08388 11.6666C4.08388 11.9888 4.34505 12.2499 4.66722 12.2499C4.98938 12.2499 5.25055 11.9888 5.25055 11.6666C5.25055 11.3444 4.98938 11.0833 4.66722 11.0833C4.34505 11.0833 4.08388 11.3444 4.08388 11.6666Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
      <path
        d="M8.75026 11.6666C8.75026 11.9888 9.01143 12.2499 9.3336 12.2499C9.65576 12.2499 9.91693 11.9888 9.91693 11.6666C9.91693 11.3444 9.65576 11.0833 9.3336 11.0833C9.01143 11.0833 8.75026 11.3444 8.75026 11.6666Z"
        stroke="currentColor"
        strokeWidth="1.16667"
      />
    </g>
  </svg>
);

export const ChevronDown = (size = '6', rotate = '0') => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`h-${size} w-${size} transition duration-150 ease-in`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    style={{ transform: `rotate(${rotate}deg)` }}
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

export const CtrlPlayFullLogoColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    version="1.1"
    id="Layer_1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    x="0px"
    y="0px"
    viewBox="0 0 1083 585"
    enableBackground="new 0 0 1083 585"
    xmlSpace="preserve"
  >
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M787.614014,24.223637 
	C799.949097,32.138874 807.691345,42.889683 810.448120,56.940098 
	C814.482056,77.500114 811.393616,96.442719 795.157166,110.997940 
	C788.964111,116.549751 780.944153,120.063705 772.953369,125.002991 
	C787.662109,151.348740 802.966553,178.761444 818.733948,207.003311 
	C813.592957,207.003311 809.350830,207.003311 805.108704,207.003311 
	C794.276733,207.003311 783.433044,206.709610 772.618469,207.143005 
	C768.078613,207.324921 765.881836,205.464401 763.954163,201.878769 
	C753.216919,181.906525 742.368652,161.993912 731.552063,142.064346 
	C731.092224,141.217041 730.570374,140.403275 730.075623,139.575012 
	C728.194031,136.425613 726.968323,131.559128 724.275879,130.529800 
	C720.232239,128.983887 715.160400,130.127472 710.131836,130.127472 
	C710.131836,155.664520 710.131836,181.070862 710.131836,206.738953 
	C694.334045,206.738953 678.929688,206.738953 663.261841,206.738953 
	C663.261841,142.334824 663.261841,77.935379 663.261841,13.197685 
	C664.681824,13.132441 666.122559,13.009277 667.563293,13.008544 
	C691.060303,12.996576 714.559692,13.206060 738.053528,12.931960 
	C755.416382,12.729388 771.974426,15.462188 787.614014,24.223637 
M729.468262,95.000633 
	C736.494995,93.998901 743.752136,93.782883 750.493164,91.806641 
	C760.003723,89.018448 764.482788,82.318840 764.821777,72.636711 
	C765.107117,64.485710 761.275818,57.794586 753.182617,54.787807 
	C749.180176,53.300823 744.829102,52.177845 740.591797,52.011967 
	C730.460083,51.615353 720.302368,51.883907 710.294312,51.883907 
	C710.294312,66.031334 710.294312,80.412453 710.294312,95.000641 
	C716.548340,95.000641 722.512817,95.000641 729.468262,95.000633 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M639.997803,315.037537 
	C645.673279,301.540619 651.398987,288.475464 656.922424,275.325317 
	C663.377258,259.957947 669.655518,244.516388 676.054749,228.999710 
	C690.620972,228.999710 704.946045,229.116608 719.266846,228.920685 
	C723.030151,228.869217 723.390564,231.630493 724.331421,233.832947 
	C729.239258,245.321320 734.013184,256.867279 738.788513,268.411865 
	C745.215637,283.949860 751.605225,299.503418 758.004517,315.052979 
	C767.389038,337.856415 776.728394,360.678558 786.164734,383.460510 
	C791.247803,395.732452 796.502991,407.933044 801.639709,420.182892 
	C801.941040,420.901459 801.877808,421.772949 802.014038,422.797607 
	C784.377197,422.797607 766.927490,422.797607 749.440613,422.797607 
	C744.808960,410.896149 740.238037,398.963196 735.430725,387.126221 
	C735.011658,386.094299 733.040771,385.095184 731.785461,385.088715 
	C708.458801,384.967773 685.131348,385.000000 661.219849,385.000000 
	C656.455994,397.554688 651.648926,410.223206 646.869995,422.817566 
	C629.470520,422.817566 612.356262,422.817566 594.743835,422.817566 
	C609.908875,386.800293 624.933777,351.116028 639.997803,315.037537 
M676.454590,342.037598 
	C675.983276,343.547577 675.511963,345.057526 674.988281,346.735107 
	C690.432129,346.735107 705.640015,346.735107 721.410706,346.735107 
	C713.681335,325.342651 706.100769,304.361938 698.393188,283.029663 
	C695.854919,289.905060 693.413940,296.487061 690.994568,303.076965 
	C686.308167,315.841278 681.633972,328.610138 676.454590,342.037598 
z"
    />
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M479.315002,192.256424 
	C468.690491,200.825470 456.860413,206.314713 443.972473,208.821533 
	C434.236755,210.715240 424.163025,212.027466 414.283173,211.790283 
	C391.722565,211.248672 370.387665,205.368134 352.227417,191.515381 
	C331.522949,175.721848 318.808868,154.679276 315.070618,128.688766 
	C313.930145,120.759354 312.835938,112.671341 313.148529,104.712051 
	C314.298035,75.445564 324.855835,50.164936 347.614807,31.083561 
	C362.478882,18.621319 380.005157,11.778699 399.285370,8.900130 
	C424.456696,5.142008 448.468811,8.245501 470.954041,20.159155 
	C478.335327,24.070084 484.747528,29.809940 491.251648,34.462204 
	C479.858765,45.841125 469.189392,56.497440 458.470856,67.202873 
	C454.899353,64.416893 450.767303,60.346478 445.900696,57.557926 
	C431.888611,49.529026 416.843445,48.516521 401.786621,53.821075 
	C380.806732,61.212349 369.672668,76.967873 365.987823,98.489738 
	C362.580048,118.393494 367.286377,136.298889 380.290771,151.543137 
	C387.580414,160.088333 397.669342,164.810776 408.732483,166.965546 
	C419.180389,169.000473 429.373840,168.107162 439.328705,164.052139 
	C447.698730,160.642700 454.297241,155.159103 459.999451,147.793396 
	C470.991089,158.092880 482.020538,168.427795 493.922394,179.580185 
	C489.027344,183.839981 484.318115,187.938049 479.315002,192.256424 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M426.808685,236.009735 
	C437.458466,240.893951 446.600830,247.014252 452.648010,257.062805 
	C456.973633,264.250702 459.048859,271.947723 460.119751,280.216492 
	C461.334229,289.593628 460.508057,298.578033 458.115967,307.670227 
	C453.487640,325.262573 441.820404,336.115875 425.656036,343.004639 
	C408.298096,350.401978 389.998749,350.364807 371.644470,350.009186 
	C368.321960,349.944794 364.997101,350.000000 361.274353,350.000000 
	C361.274353,374.188843 361.274353,398.259216 361.274353,422.664520 
	C345.144928,422.664520 329.407349,422.664520 313.334442,422.664520 
	C313.334442,358.475372 313.334442,294.080231 313.334442,229.223160 
	C314.509003,229.157822 315.932159,229.010483 317.355377,229.009659 
	C341.683441,228.995789 366.017609,228.677368 390.337067,229.135376 
	C402.631775,229.366928 414.827362,231.383972 426.808685,236.009735 
M370.533752,268.000549 
	C367.429077,268.000549 364.324371,268.000549 361.265259,268.000549 
	C361.265259,282.947021 361.265259,297.340576 361.265259,312.000610 
	C363.492737,312.000610 365.468445,312.081512 367.435791,311.987122 
	C377.842163,311.487671 388.240601,312.955048 398.603485,309.282867 
	C407.584900,306.100189 413.315216,297.826599 412.996307,289.251801 
	C412.618225,279.086212 408.286987,272.550415 398.355133,270.274689 
	C389.692139,268.289673 380.487885,268.666992 370.533752,268.000549 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M95.222580,342.605560 
	C93.345329,333.732819 91.364189,325.253143 90.105576,316.667542 
	C89.225540,310.664398 88.807457,304.491791 89.090424,298.437286 
	C90.377167,270.905670 97.209618,244.707535 114.956215,223.349014 
	C121.945595,214.937119 117.871223,208.157486 117.721581,200.673370 
	C117.562851,192.735077 115.504868,184.848053 115.117317,176.897659 
	C114.631096,166.922913 114.743065,156.898560 115.051277,146.909637 
	C115.791534,122.917892 120.920609,99.819771 130.049179,77.631081 
	C139.161819,55.481068 152.479218,36.237514 170.336182,20.222168 
	C172.978241,17.852589 175.846573,15.670151 178.868881,13.824727 
	C179.941040,13.170074 182.222794,13.175527 183.208191,13.896583 
	C199.495331,25.814861 212.072937,40.937481 222.159760,58.395287 
	C230.505142,72.839096 236.222748,88.267906 240.519196,104.221855 
	C242.739426,112.466263 244.107422,121.039986 244.870712,129.553665 
	C246.031982,142.506256 247.258347,155.572220 246.750397,168.523102 
	C246.196793,182.637924 244.095627,196.740463 241.852768,210.717361 
	C240.922409,216.515076 244.083038,219.675217 246.892807,223.332336 
	C259.111694,239.236099 267.069122,257.120483 269.788513,276.869171 
	C271.578827,289.870850 271.933685,303.160278 271.770233,316.306427 
	C271.675110,323.957153 269.474396,331.619049 267.861816,339.199524 
	C266.899872,343.721466 261.670471,346.635803 256.743713,345.997437 
	C252.756454,345.480804 249.634079,341.573273 248.949768,337.231293 
	C246.226974,319.954712 239.795975,304.158997 229.485672,289.987305 
	C228.438049,288.547302 227.381607,287.105896 226.221741,285.757660 
	C225.137909,284.497803 223.910965,283.361053 222.484985,281.902008 
	C211.289932,294.356384 198.895828,304.163574 180.907700,304.107361 
	C163.048325,304.051514 150.408432,294.993927 139.460800,282.010071 
	C125.772377,295.119385 118.743553,311.751038 114.035789,329.610352 
	C113.461365,331.789520 113.311035,334.083191 113.000511,336.328796 
	C111.725746,345.547882 102.855423,349.247803 95.222580,342.605560 
M223.517136,207.976212 
	C223.715363,206.191757 223.895737,204.405106 224.114746,202.623215 
	C225.404861,192.126526 227.323761,181.660309 227.822906,171.126587 
	C228.306564,160.919632 227.784515,150.603333 226.871582,140.409607 
	C225.991348,130.580933 224.577072,120.730896 222.456543,111.097351 
	C218.425613,92.784912 210.909668,75.741898 200.872833,59.973087 
	C195.169846,51.013157 188.286621,42.804447 181.287491,33.386246 
	C177.754150,37.256680 174.979645,39.904480 172.659927,42.903988 
	C162.078903,56.585762 153.394943,71.348061 146.876129,87.434509 
	C139.013779,106.836418 134.770096,126.956261 134.057327,147.810547 
	C133.722443,157.608856 133.880997,167.430145 134.053528,177.236847 
	C134.131073,181.644089 134.501083,186.090744 135.244980,190.432739 
	C136.986420,200.597092 139.078812,210.701065 140.879776,220.855957 
	C141.039932,221.758972 140.299347,223.003464 139.650070,223.833603 
	C137.841370,226.146225 135.966187,228.426056 133.930283,230.538620 
	C121.917931,243.003311 115.891640,258.550934 111.133278,274.735596 
	C110.453934,277.046234 110.363609,279.530029 110.000099,281.933594 
	C109.673149,284.095490 109.345406,286.257233 109.018005,288.419067 
	C119.115707,272.517395 132.068024,260.125732 148.661209,250.873016 
	C150.118134,255.100235 151.557663,259.282501 153.000900,263.463501 
	C156.978897,274.987671 170.697617,286.168152 182.479156,285.001556 
	C192.359909,284.023163 205.274918,274.483765 209.036087,262.300079 
	C210.236496,258.411591 211.767166,254.625092 213.222855,250.580383 
	C228.942154,260.588135 242.089539,272.202545 251.852036,287.583527 
	C251.821594,284.297821 251.657272,281.099030 250.958145,278.021698 
	C246.711975,259.331604 239.457336,242.096497 225.672485,228.291504 
	C222.204102,224.818054 219.550003,221.723648 221.884262,216.400513 
	C222.887680,214.112274 222.687408,211.296234 223.517136,207.976212 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M831.739014,274.241364 
	C836.792664,282.869812 841.578369,291.230072 846.448669,299.737946 
	C859.603210,276.938934 872.702026,254.196732 885.889099,231.505753 
	C886.509705,230.437851 887.909119,229.116379 888.955933,229.107590 
	C906.567566,228.959656 924.180786,229.001831 943.117859,229.001831 
	C939.453918,234.666794 936.420105,239.492752 933.254272,244.230423 
	C912.373840,275.478027 891.433838,306.685974 870.648315,337.996490 
	C869.489746,339.741669 869.088867,342.281219 869.080139,344.454010 
	C868.975952,370.434418 869.012878,396.415405 869.012878,422.698181 
	C853.094727,422.698181 837.354858,422.698181 820.999634,422.698181 
	C820.999634,421.230957 820.999634,419.638000 820.999634,418.045044 
	C820.999634,394.046967 821.080078,370.048248 820.888245,346.051697 
	C820.866577,343.340515 819.866699,340.280396 818.381531,337.991272 
	C796.476379,304.228058 774.400085,270.575836 752.410767,236.867126 
	C750.856323,234.484177 749.663391,231.865326 748.109131,228.999878 
	C766.773132,228.999878 785.048340,228.965820 803.322754,229.076843 
	C804.536926,229.084229 806.307312,229.868439 806.870483,230.840714 
	C815.165344,245.163422 823.299133,259.579376 831.739014,274.241364 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M68.999840,396.000000 
	C76.143059,396.000000 82.786308,396.000000 89.713982,396.000000 
	C89.713982,404.958679 89.713982,413.697113 89.713982,422.719482 
	C63.696472,422.719482 37.627216,422.719482 11.278482,422.719482 
	C11.278482,286.367401 11.278482,149.975479 11.278482,13.291710 
	C37.282551,13.291710 63.351826,13.291710 89.711060,13.291710 
	C89.711060,21.952602 89.711060,30.691164 89.711060,39.836033 
	C72.704323,39.836033 55.630585,39.836033 38.280540,39.836033 
	C38.280540,158.632431 38.280540,277.025970 38.280540,396.000000 
	C48.305550,396.000000 58.402714,396.000000 68.999840,396.000000 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M1007.014160,396.000000 
	C1020.485779,396.000000 1033.460571,396.000000 1046.716919,396.000000 
	C1046.716919,277.372742 1046.716919,158.978668 1046.716919,40.180878 
	C1029.719604,40.180878 1012.645813,40.180878 995.287720,40.180878 
	C995.287720,31.046089 995.287720,22.307533 995.287720,13.283065 
	C1021.300232,13.283065 1047.369507,13.283065 1073.719971,13.283065 
	C1073.719971,149.631104 1073.719971,286.023621 1073.719971,422.708435 
	C1047.718872,422.708435 1021.649353,422.708435 995.289429,422.708435 
	C995.289429,414.049622 995.289429,405.310974 995.289429,396.000000 
	C998.975586,396.000000 1002.746399,396.000000 1007.014160,396.000000 
z"
    />
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M552.000000,156.000000 
	C552.000000,121.526917 552.000000,87.553825 552.000000,53.180550 
	C533.719971,53.180550 515.646729,53.180550 497.288055,53.180550 
	C497.288055,39.714054 497.288055,26.643040 497.288055,13.284967 
	C549.289978,13.284967 601.355774,13.284967 653.710754,13.284967 
	C653.710754,26.285601 653.710754,39.356445 653.710754,52.830635 
	C635.706970,52.830635 617.633789,52.830635 599.171997,52.830635 
	C599.171997,104.296509 599.171997,155.363876 599.171997,206.716751 
	C583.379944,206.716751 567.975586,206.716751 552.000000,206.716751 
	C552.000000,190.023819 552.000000,173.261902 552.000000,156.000000 
z"
    />
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M868.000000,104.000000 
	C868.000000,124.475273 868.000000,144.450546 868.000000,164.825409 
	C892.954041,164.825409 917.692627,164.825409 942.714233,164.825409 
	C942.714233,178.959274 942.714233,192.697495 942.714233,206.720459 
	C902.038818,206.720459 861.303589,206.720459 820.284058,206.720459 
	C820.284058,142.379715 820.284058,77.980171 820.284058,13.290124 
	C835.950989,13.290124 851.688477,13.290124 868.000000,13.290124 
	C868.000000,43.313854 868.000000,73.406929 868.000000,104.000000 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M590.809387,422.998779 
	C550.366577,423.000458 510.407776,423.000458 470.224976,423.000458 
	C470.224976,358.249359 470.224976,293.846619 470.224976,229.221573 
	C486.091095,229.221573 501.828522,229.221573 517.970276,229.221573 
	C517.970276,280.101746 517.970276,330.839508 517.970276,381.970428 
	C543.111023,381.970428 567.852356,381.970428 593.009155,381.970428 
	C593.009155,395.311615 593.032043,408.262329 592.936340,421.212158 
	C592.931946,421.811188 591.865295,422.402313 590.809387,422.998779 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M578.000000,542.999451 
	C605.808838,542.999451 633.117615,542.999451 660.712646,542.999451 
	C660.712646,552.291443 660.712646,561.364685 660.712646,570.720093 
	C582.043701,570.720093 503.307556,570.720093 424.285828,570.720093 
	C424.285828,561.724609 424.285828,552.651428 424.285828,542.999451 
	C475.314606,542.999451 526.407288,542.999451 578.000000,542.999451 
z"
    />
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M913.038452,98.508804 
	C908.202820,94.883842 906.616455,90.524742 908.147156,85.527832 
	C909.340454,81.632339 913.648132,79.021881 919.019836,79.005325 
	C926.488464,78.982300 933.957214,78.999535 942.000427,78.999535 
	C942.000427,71.203400 941.998352,63.613308 942.000977,56.023212 
	C942.003357,49.169228 945.695801,44.929741 951.595093,45.000225 
	C957.303467,45.068424 961.900391,49.926991 961.978333,56.204597 
	C962.070862,63.655254 962.000183,71.107941 962.000183,78.999870 
	C969.949463,78.999870 977.559937,78.841530 985.161011,79.041260 
	C994.016479,79.273941 998.746765,87.771255 993.818726,95.017120 
	C992.441223,97.042618 989.124573,98.668640 986.596863,98.822067 
	C978.645996,99.304672 970.647339,98.999802 962.000427,98.999802 
	C962.000427,106.523499 962.007568,113.935867 961.998596,121.348213 
	C961.988525,129.705353 958.285156,133.305023 950.033386,133.000000 
	C946.126770,132.855621 942.037048,128.729904 942.014648,124.753334 
	C941.966980,116.306755 942.000000,107.859726 942.000000,98.829941 
	C932.192871,98.829941 922.757690,98.829941 913.038452,98.508804 
z"
    />
    <path
      fill="#FBFBFB"
      opacity="1.000000"
      stroke="none"
      d="
M197.976654,338.894012 
	C196.637421,346.308258 195.347733,353.288300 193.994751,360.256042 
	C191.566711,372.760284 189.123672,385.261719 186.641815,397.755371 
	C185.136292,405.334076 183.570801,412.901215 181.959824,420.458099 
	C181.777084,421.315338 181.124634,422.072449 180.439713,423.338715 
	C176.586411,405.296143 172.649963,387.876709 169.205139,370.360626 
	C167.082275,359.566315 165.243256,348.670868 164.156082,337.733429 
	C163.367783,329.802826 170.045883,321.842560 176.798508,321.003143 
	C188.066010,319.602509 194.689896,323.774292 197.008469,333.748230 
	C197.371338,335.309204 197.669571,336.885223 197.976654,338.894012 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M465.146149,500.358093 
	C458.224274,504.089569 451.484436,504.214172 444.755920,500.942413 
	C439.056519,498.171143 434.804871,489.654694 436.035675,483.836334 
	C438.471100,472.322845 445.844208,468.573303 452.328491,469.075104 
	C457.182648,469.450684 461.983856,470.510040 466.946014,471.286438 
	C466.920410,471.496033 467.052307,470.415253 467.204590,469.167603 
	C469.309479,469.167603 471.420166,469.167603 473.007507,469.167603 
	C473.007507,481.608673 473.411041,493.968597 472.845795,506.284088 
	C472.540100,512.945251 467.521149,516.242065 461.158783,517.373413 
	C452.725800,518.872925 444.808929,518.124084 437.634033,512.440735 
	C439.059174,510.389801 440.240936,508.689056 441.422729,506.988342 
	C441.413818,507.454254 441.404907,507.920166 441.395996,508.386108 
	C446.671265,509.257416 451.919006,510.519928 457.229309,510.893860 
	C462.944397,511.296295 467.716034,505.360077 466.865448,499.676697 
	C466.378998,499.805878 465.892548,499.935059 465.146149,500.358093 
M448.789001,477.025757 
	C443.784332,481.436584 442.366455,487.245667 445.221832,491.640442 
	C448.317535,496.405121 456.076965,498.446136 460.789764,495.735352 
	C466.204376,492.620911 468.272369,485.709229 465.265289,480.777130 
	C461.769836,475.044037 455.645447,473.539337 448.789001,477.025757 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M568.998169,469.000244 
	C579.057861,467.970184 587.825684,475.987488 586.761108,488.999634 
	C582.186829,488.999634 577.565430,488.999634 572.944031,488.999634 
	C568.456970,488.999634 563.969910,488.999634 559.486023,488.999634 
	C559.437317,494.716492 565.147278,499.852905 570.706482,498.901642 
	C573.326416,498.453339 576.188232,497.618652 578.234009,496.042297 
	C581.213623,493.746338 582.538635,495.760437 583.873718,497.512085 
	C585.408569,499.525848 584.031921,500.901520 582.237183,501.985382 
	C573.471558,507.279114 562.940979,505.923798 555.731628,499.426208 
	C551.181946,495.325653 549.521362,485.775391 552.458923,479.025330 
	C555.517334,471.997650 560.897583,468.675140 568.998169,469.000244 
M565.638367,475.880798 
	C561.833557,477.113373 559.080872,479.209290 559.089417,483.764954 
	C565.759033,483.764954 572.283325,483.764954 578.839905,483.764954 
	C577.804321,476.974823 574.150452,474.583221 565.638367,475.880798 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M773.694214,504.551453 
	C771.181946,504.235046 767.663574,507.031006 766.921692,503.401306 
	C762.182190,503.936737 757.474609,504.930634 752.776062,504.889862 
	C745.914978,504.830383 742.277222,500.227112 742.099365,494.674347 
	C741.931335,489.428925 745.668152,484.309753 752.937317,484.013916 
	C755.097900,483.925964 757.265076,484.000336 759.429260,484.000336 
	C761.415649,484.000336 763.402039,484.000305 767.018555,484.000305 
	C765.486267,481.062805 764.808350,478.425812 763.135376,476.836151 
	C759.714111,473.585358 755.181641,474.354523 746.359558,478.911987 
	C745.192688,477.244324 744.010010,475.554077 742.744568,473.745514 
	C749.412292,467.993591 757.213257,468.131714 764.724426,469.447571 
	C770.703125,470.494965 773.872986,476.101318 773.975464,483.197021 
	C774.076355,490.187103 773.976257,497.180084 773.694214,504.551453 
M765.999023,493.009613 
	C765.999023,491.754944 765.999023,490.500275 765.999023,488.543549 
	C761.446411,489.073822 757.317078,489.117371 753.491638,490.217468 
	C751.979858,490.652222 750.276550,493.443390 750.285522,495.153137 
	C750.292175,496.438171 752.744324,498.540466 754.366638,498.830292 
	C759.098450,499.675598 763.538269,498.834930 765.999023,493.009613 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M228.018356,490.521667 
	C231.931976,498.545166 238.441437,501.083740 245.494659,496.910126 
	C249.361343,494.622162 251.701157,494.835968 253.827469,498.667450 
	C250.038773,505.085693 238.739410,507.455780 229.960434,503.456177 
	C222.648972,500.125122 218.473236,489.484314 220.569946,482.883179 
	C224.184143,471.504517 232.790848,466.262909 244.002792,469.096863 
	C252.019638,471.123230 257.523804,481.110046 255.599731,488.999756 
	C247.921310,488.999756 240.131882,488.944427 232.344635,489.049438 
	C230.898849,489.068970 229.461761,489.733917 228.018356,490.521667 
M236.822464,475.001282 
	C232.258575,476.225616 228.679352,478.419586 227.907715,483.855652 
	C234.997589,483.855652 241.750916,483.855652 248.510315,483.855652 
	C246.975952,477.654694 243.886169,475.139984 236.822464,475.001282 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M507.331757,504.999878 
	C500.274536,503.945435 495.292786,499.214325 496.095612,494.102905 
	C497.103729,487.684357 500.463867,483.838684 508.493835,483.988770 
	C512.137207,484.056885 515.782898,484.000305 521.250061,484.000305 
	C519.437805,480.836456 518.597168,478.090302 516.765259,476.453766 
	C513.573486,473.602325 508.687164,474.589478 500.192474,478.987762 
	C499.193878,477.187195 498.190063,475.377228 497.185791,473.567871 
	C504.727600,467.513763 518.919067,466.800629 523.956665,472.652863 
	C525.934204,474.950195 527.616150,478.237030 527.818909,481.184235 
	C528.352051,488.934906 527.999084,496.746552 527.999084,505.418213 
	C525.165833,504.847504 522.836792,504.378387 520.507751,503.909271 
	C520.635193,503.694794 520.762634,503.480316 520.890076,503.265839 
	C516.518433,503.843842 512.146729,504.421875 507.331757,504.999878 
M504.026306,492.891571 
	C504.175903,498.187653 505.962769,499.467712 512.553101,498.999969 
	C517.795288,498.627869 520.262085,495.239258 519.566345,489.019684 
	C514.344849,489.315735 508.782501,487.643311 504.026306,492.891571 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M833.528198,475.008301 
	C828.981445,475.956512 824.849792,476.911926 820.044189,478.023163 
	C816.488708,474.362274 816.718201,471.819580 822.575012,470.310791 
	C829.773560,468.456360 837.566895,466.515991 843.789734,472.565338 
	C845.999939,474.713867 847.579773,478.405670 847.813049,481.515533 
	C848.383057,489.114716 847.999084,496.785492 847.999084,504.384491 
	C841.861084,505.478302 841.861084,505.478302 840.548645,502.288055 
	C834.685364,505.740906 828.484436,506.305603 822.276794,503.647156 
	C817.457275,501.583130 815.673828,497.226013 817.071106,491.266174 
	C818.163940,486.604980 821.657471,484.285919 826.283142,484.052551 
	C830.932983,483.817963 835.603882,484.000916 840.261719,484.000916 
	C840.574158,479.233612 838.088867,476.534973 833.528198,475.008301 
M838.517944,495.978149 
	C839.515076,493.873291 840.512207,491.768433 842.117554,488.379761 
	C836.051514,489.050690 831.660095,489.234467 827.437805,490.176575 
	C826.065308,490.482758 824.025635,493.001862 824.254150,494.100494 
	C824.627502,495.895203 826.618652,498.701202 827.913330,498.693665 
	C831.265381,498.674072 834.609802,497.352173 838.517944,495.978149 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M861.161255,471.975952 
	C865.851135,467.336731 880.155701,467.777466 885.990662,473.430573 
	C891.376709,478.648682 892.763062,490.409027 888.569702,496.957275 
	C883.543091,504.806702 875.695618,506.039673 867.567139,504.832550 
	C860.686035,503.810669 855.596680,498.946442 854.334717,492.266815 
	C852.930664,484.835358 853.210571,477.028961 861.161255,471.975952 
M878.203979,496.974792 
	C879.181641,496.094971 880.286804,495.320251 881.113831,494.316345 
	C884.043030,490.760925 884.441406,484.345917 881.778015,480.497375 
	C879.238647,476.828064 875.664368,474.295044 871.070984,475.124573 
	C866.084045,476.025146 862.602295,479.209564 861.291321,484.300476 
	C860.101929,488.919098 861.964783,492.703033 865.256409,495.643555 
	C868.834045,498.839569 872.971130,500.010712 878.203979,496.974792 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M397.160461,495.493408 
	C393.915771,484.869080 395.512604,477.272217 403.178650,471.385468 
	C408.767731,467.093597 422.330566,468.172913 427.790588,473.466766 
	C431.997009,477.545197 434.777069,485.333679 431.923523,493.201385 
	C429.360474,500.268127 423.175537,504.668335 416.298889,505.059143 
	C408.147034,505.522400 401.630341,503.088287 397.160461,495.493408 
M420.918243,496.602020 
	C424.117889,493.396912 425.375610,489.562531 425.037506,485.036255 
	C424.706177,480.600616 421.227142,476.460327 416.312866,475.108856 
	C412.923645,474.176758 406.853668,476.866058 404.982422,480.128815 
	C401.883789,485.531525 402.214630,490.335510 406.003326,494.955597 
	C409.140289,498.780884 413.984467,499.469055 420.918243,496.602020 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M305.999664,496.951355 
	C305.999664,499.753052 305.999664,502.065125 305.999664,504.685883 
	C303.428528,504.685883 301.022614,504.685883 298.298950,504.685883 
	C298.298950,493.030365 298.298950,481.175751 298.298950,468.826752 
	C300.988037,470.202026 304.979095,466.365112 306.373810,471.687103 
	C312.440613,468.235748 318.932861,467.827667 325.519440,470.227234 
	C331.341156,472.348267 332.328552,477.935181 332.814117,482.868195 
	C333.522980,490.069519 332.996307,497.392487 332.996307,504.795837 
	C330.181305,504.795837 327.781128,504.795837 325.001312,504.795837 
	C325.001312,498.156006 325.014771,491.711029 324.997467,485.266144 
	C324.980621,478.985657 321.597046,475.100220 316.099182,475.001617 
	C311.028351,474.910736 306.078003,480.024597 306.008118,485.489990 
	C305.961365,489.146667 305.999512,492.804443 305.999664,496.951355 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M684.117798,469.000122 
	C693.619751,469.000732 699.703796,472.310211 701.998535,478.478455 
	C705.515564,487.931641 704.144531,495.194519 697.084839,501.332489 
	C691.345154,506.322845 684.384766,506.131439 677.738708,503.884796 
	C671.773010,501.868195 668.109741,497.552460 666.886963,490.863647 
	C664.561829,478.144775 673.463989,468.310913 684.117798,469.000122 
M673.999573,485.735596 
	C673.999512,486.068481 673.995789,486.401459 674.000061,486.734283 
	C674.079102,492.879425 678.353516,498.073303 684.076904,498.979736 
	C688.366028,499.659027 694.257019,495.443176 695.815552,490.579071 
	C697.647949,484.860138 695.325073,479.246429 689.970947,476.454285 
	C682.970886,472.803802 677.144409,475.882904 673.999573,485.735596 
z"
    />
    <path
      fill="#F9F9F9"
      opacity="1.000000"
      stroke="none"
      d="
M653.001282,485.334106 
	C653.567627,478.881592 648.693359,475.237854 643.845825,475.067749 
	C638.938232,474.895538 634.071106,480.115021 634.016052,485.246826 
	C633.946838,491.698181 633.999329,498.150818 633.999329,504.796875 
	C631.216675,504.796875 628.813965,504.796875 626.198608,504.796875 
	C626.198608,492.916138 626.198608,481.178345 626.198608,468.884369 
	C628.954956,470.087921 632.689758,466.593933 634.020569,470.449829 
	C639.342468,469.960449 644.562378,469.050171 649.770874,469.111145 
	C656.170898,469.186096 660.441895,475.052094 660.927795,484.268158 
	C661.284485,491.033997 660.997742,497.833710 660.997742,504.804810 
	C658.221313,504.804810 655.819458,504.804810 653.001343,504.804810 
	C653.001343,498.358124 653.001343,492.076019 653.001282,485.334106 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M368.725525,472.232788 
	C375.387512,477.373291 377.614990,484.443146 374.722290,493.642456 
	C372.540466,500.580902 365.854950,504.805481 358.241272,505.048920 
	C349.960510,505.313721 343.336090,502.613190 340.010834,494.742859 
	C334.850159,482.528381 342.375702,468.234558 356.424408,468.924866 
	C360.719696,469.135925 364.933563,468.959320 368.725525,472.232788 
M363.910980,477.390015 
	C354.958038,473.289825 349.829224,474.893219 346.649841,482.882416 
	C344.840454,487.429169 345.699829,491.940033 349.167511,494.965820 
	C351.569092,497.061371 355.581482,499.052246 358.442841,498.563751 
	C368.309814,496.879303 371.413513,485.338043 363.910980,477.390015 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M792.671021,469.125732 
	C806.815002,468.947784 806.815002,468.947784 813.387146,476.152771 
	C811.660828,477.517822 809.966125,478.857849 808.225159,480.234497 
	C804.048401,477.161102 800.584656,473.593567 794.609985,475.836090 
	C789.956909,477.582581 787.891296,480.727295 787.070740,484.970154 
	C786.040833,490.295197 788.299500,494.615784 792.870850,497.226685 
	C797.052551,499.614990 801.339417,498.961670 805.409363,496.195496 
	C806.740967,495.290497 808.914612,494.457428 810.225769,494.926331 
	C813.184204,495.984375 814.441162,498.289459 811.006409,500.386566 
	C807.404175,502.585876 803.523865,504.329498 799.406006,506.457886 
	C799.571960,506.600067 800.359009,507.382599 801.250305,508.019348 
	C805.151978,510.806702 805.203857,516.759949 800.865967,518.407104 
	C797.971130,519.506226 794.403748,519.002991 791.153809,518.855164 
	C790.505859,518.825745 789.932556,517.155701 789.324829,516.242249 
	C790.498230,515.623779 791.892639,514.256592 792.804016,514.525635 
	C795.672241,515.372192 797.659241,515.450195 798.093018,511.444336 
	C796.369873,511.163208 794.703857,510.891418 792.846069,510.588348 
	C793.297180,508.864197 793.698059,507.331787 794.222107,505.328644 
	C784.556763,502.773193 779.049561,496.249359 779.296753,486.539917 
	C779.505371,478.346344 783.608032,471.767181 792.671021,469.125732 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M283.824646,497.022247 
	C285.200867,496.098663 286.186859,494.507080 287.177155,494.504395 
	C288.910004,494.499725 290.791473,495.240021 292.284393,496.184113 
	C292.693085,496.442535 292.346100,498.859467 291.652039,499.736938 
	C287.994110,504.360901 277.998657,506.966522 272.211914,504.670227 
	C264.214111,501.496552 260.560394,496.128967 259.809906,488.019379 
	C258.130829,469.874634 277.500366,465.267670 287.837921,471.074127 
	C289.937012,472.253143 291.294830,474.751862 292.994354,476.642212 
	C291.419159,477.819427 289.843964,478.996613 288.337585,480.122375 
	C280.373291,474.148224 275.302063,473.598480 270.476410,478.042175 
	C266.857056,481.375061 265.838287,488.749054 268.420654,492.921539 
	C271.703918,498.226532 277.053162,499.704224 283.824646,497.022247 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M736.987671,479.883057 
	C734.308105,486.278870 731.727600,492.319946 728.944580,498.266296 
	C726.956360,502.514435 725.411316,507.744446 717.996277,504.056641 
	C713.263672,493.194641 708.111206,481.369049 702.929504,469.476257 
	C710.820679,468.384674 711.249817,468.773224 714.022461,476.012848 
	C716.469177,482.401489 719.242432,488.665070 722.077515,495.479126 
	C725.583435,487.943604 728.890015,481.232208 731.841797,474.368286 
	C734.245972,468.777679 734.057739,468.696716 741.215820,469.346985 
	C739.773071,472.817474 738.386597,476.152618 736.987671,479.883057 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M218.998688,501.160645 
	C218.924240,502.234009 218.888031,503.428986 218.768494,503.437347 
	C214.366104,503.746521 209.778961,504.797516 205.595078,503.928040 
	C201.119537,502.997955 199.778748,498.840332 199.943924,494.236267 
	C200.169418,487.951324 200.000214,481.652222 200.000214,474.994873 
	C197.869888,474.994873 196.123260,474.994873 194.192307,474.994873 
	C194.192307,472.878754 194.192307,471.134186 194.192307,469.013123 
	C196.111618,469.013123 197.860321,469.013123 199.988495,469.013123 
	C199.988495,466.228088 199.988495,463.808960 199.988495,461.199890 
	C202.446976,461.199890 204.520126,461.199890 206.980942,461.199890 
	C206.980942,463.771576 206.980942,466.185974 206.980942,468.983643 
	C210.443176,468.983643 213.529434,468.983643 216.802689,468.983643 
	C216.802689,471.113159 216.802689,472.851715 216.802689,474.981750 
	C213.572449,474.981750 210.489243,474.981750 207.003891,474.981750 
	C207.003891,481.314758 206.798447,487.276184 207.072723,493.215454 
	C207.271118,497.511108 210.160049,499.322083 214.283234,498.294495 
	C216.581573,497.721710 218.651260,497.544159 218.998688,501.160645 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M388.999878,482.999939 
	C388.999878,490.476685 388.999878,497.453491 388.999878,504.713501 
	C386.701874,504.713501 384.632507,504.713501 382.278717,504.713501 
	C382.278717,488.399078 382.278717,471.985870 382.278717,455.285583 
	C384.287537,455.285583 386.357483,455.285583 388.999878,455.285583 
	C388.999878,464.286102 388.999878,473.393036 388.999878,482.999939 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M618.000366,479.029510 
	C618.000366,487.796814 618.000366,496.069733 618.000366,504.670471 
	C615.479431,504.670471 613.078064,504.670471 610.335754,504.670471 
	C610.335754,493.122589 610.335754,481.303345 610.335754,469.240631 
	C612.697266,469.240631 615.092590,469.240631 618.000366,469.240631 
	C618.000366,472.336060 618.000366,475.435608 618.000366,479.029510 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M483.000000,479.029968 
	C483.000000,475.555359 483.000000,472.575165 483.000000,469.298218 
	C485.275269,469.298218 487.345154,469.298218 489.710388,469.298218 
	C489.710388,480.906769 489.710388,492.655426 489.710388,504.702850 
	C487.731506,504.702850 485.661499,504.702850 483.000000,504.702850 
	C483.000000,496.403625 483.000000,487.964020 483.000000,479.029968 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M836.619019,459.212219 
	C838.996521,458.095093 841.048767,456.787384 843.100952,455.479675 
	C843.659851,455.929657 844.218689,456.379639 844.777588,456.829620 
	C843.573547,459.210724 842.954590,462.428894 841.027649,463.775604 
	C838.175110,465.769196 834.572205,465.161591 831.908752,462.396301 
	C829.714600,460.118286 827.993469,459.628601 825.918701,462.742920 
	C825.213867,463.800934 823.042419,463.881989 820.453247,464.791718 
	C821.609924,461.860779 822.200195,460.118011 822.971252,458.459259 
	C824.998047,454.098816 828.233337,454.403381 831.788818,456.338562 
	C833.322754,457.173492 834.793945,458.123749 836.619019,459.212219 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M617.716431,461.760742 
	C615.037903,461.952301 612.580322,462.159546 610.324524,461.580719 
	C609.615784,461.398865 608.916687,459.010315 609.133240,457.795868 
	C609.793701,454.091431 612.787354,453.165710 615.790344,454.129578 
	C619.481384,455.314270 620.096252,458.162231 617.716431,461.760742 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M490.983459,459.746643 
	C488.738953,461.102051 486.557190,462.229218 484.239166,462.835052 
	C483.749573,462.962982 482.250580,461.260193 482.098785,460.266235 
	C481.653931,457.353333 481.963623,454.436188 485.781189,454.039337 
	C489.543976,453.648193 491.284760,455.570465 490.983459,459.746643 
z"
    />
    <path
      fill="#F8F8F8"
      opacity="1.000000"
      stroke="none"
      d="
M212.046555,126.793182 
	C207.467560,137.554153 199.925430,144.222641 188.832413,147.105881 
	C170.675415,151.825165 150.663162,139.757126 147.911911,120.333778 
	C145.782150,105.298080 151.236328,93.846626 163.645782,86.060150 
	C180.980103,75.183525 204.460236,83.145706 211.932999,102.194839 
	C215.041229,110.118164 215.103668,118.303886 212.046555,126.793182 
M195.119629,122.355675 
	C200.259888,113.517876 195.504776,101.925552 185.167664,98.812553 
	C176.827896,96.301048 169.204147,101.090012 165.843475,107.767746 
	C162.121658,115.163055 164.741959,123.464859 171.289612,128.075897 
	C178.169128,132.920654 188.704407,132.629730 195.119629,122.355675 
z"
    />
  </svg>
);

export const CursorColorIcon = (props: IconProps) => (
  <svg
    {...props}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M29.2953 17.7102L19.07 20.6802C18.9235 20.7232 18.7873 20.7955 18.6695 20.8926C18.5521 20.9893 18.4557 21.109 18.3864 21.2445L13.7537 30.3538C13.3383 31.1701 12.1526 31.1593 11.9429 30.3371L5.94036 6.78421C5.84361 6.40486 6.00471 6.03631 6.28101 5.79421C6.57621 5.53636 7.00101 5.42431 7.38531 5.60206L29.6103 15.9484C30.3821 16.3066 30.1746 17.4541 29.2953 17.7102Z"
      fill="#FD7C59"
    />
    <path
      d="M25.1528 25.695L22.0591 28.215C21.5141 28.6596 20.7487 28.6294 20.3491 28.1484L15.9922 22.9009L21.0605 18.7677L25.4174 24.0169C25.8152 24.498 25.6955 25.2486 25.1528 25.695Z"
      fill="#FD7C59"
    />
    <path
      d="M18.5781 20.79L21.059 18.765L25.4159 24.0143C25.8155 24.4953 25.6976 25.2459 25.1531 25.6901L23.637 26.9267L18.5781 20.79Z"
      fill="#FD426A"
    />
    <path
      d="M6.28125 5.79504C6.57645 5.53719 7.00125 5.42514 7.38555 5.60289L29.6106 15.9484C30.3805 16.3084 30.1731 17.4541 29.2924 17.7101L19.0671 20.6801C18.9206 20.7232 18.7844 20.7954 18.6666 20.8925L6.28125 5.79504Z"
      fill="#FD426A"
    />
  </svg>
);

export const ActivityColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M25.0725 5.78H11.1529C8.96987 5.78 7.2002 7.54968 7.2002 9.73269V27.8624C7.2002 30.0454 8.96987 31.8151 11.1529 31.8151H25.0725C27.2555 31.8151 29.0252 30.0454 29.0252 27.8624V9.73269C29.0252 7.54968 27.2555 5.78 25.0725 5.78Z"
      fill="#FD7C59"
    />
    <path
      d="M17.9434 5.73166H25.1504C26.1781 5.73166 27.1637 6.13989 27.8903 6.86655C28.617 7.59322 29.0252 8.57878 29.0252 9.60644V27.9404C29.0252 28.4492 28.925 28.9531 28.7303 29.4232C28.5355 29.8933 28.2501 30.3204 27.8903 30.6802C27.5305 31.0401 27.1034 31.3255 26.6332 31.5202C26.1631 31.7149 25.6593 31.8151 25.1504 31.8151H17.9434V5.73166Z"
      fill="#FD426A"
    />
    <path
      d="M12.0454 8.26599H24.234C24.85 8.26599 25.4408 8.51072 25.8765 8.94633C26.3121 9.38195 26.5568 9.97277 26.5568 10.5888V26.9619C26.5568 27.578 26.3121 28.1688 25.8765 28.6044C25.4408 29.04 24.85 29.2847 24.234 29.2847H12.0454C11.7458 29.2847 11.4491 29.2257 11.1724 29.111C10.8956 28.9963 10.6441 28.8283 10.4323 28.6164C10.2205 28.4045 10.0525 28.1529 9.93798 27.8761C9.82342 27.5993 9.76452 27.3026 9.76465 27.003V10.5467C9.76465 9.94184 10.0049 9.36172 10.4327 8.934C10.8604 8.50628 11.4405 8.26599 12.0454 8.26599Z"
      fill="white"
    />
    <path
      d="M17.9434 8.26599H24.2165C24.8373 8.26599 25.4326 8.5126 25.8716 8.95158C26.3106 9.39055 26.5572 9.98593 26.5572 10.6067V26.943C26.5572 27.5638 26.3106 28.1592 25.8716 28.5982C25.4326 29.0371 24.8373 29.2838 24.2165 29.2838H17.9434V8.26599Z"
      fill="#D9ECFD"
    />
    <path
      d="M21.6446 3.9411H14.3383C13.7367 3.9411 13.249 4.4288 13.249 5.03042V7.69104C13.249 8.29265 13.7367 8.78035 14.3383 8.78035H21.6446C22.2463 8.78035 22.734 8.29265 22.734 7.69104V5.03042C22.734 4.4288 22.2463 3.9411 21.6446 3.9411Z"
      fill="#EDEDED"
    />
    <path
      d="M17.9922 3.9411H21.6216C21.9168 3.9411 22.1999 4.05837 22.4087 4.2671C22.6174 4.47583 22.7347 4.75894 22.7347 5.05413V7.66733C22.7347 7.96252 22.6174 8.24562 22.4087 8.45436C22.1999 8.66309 21.9168 8.78035 21.6216 8.78035H17.9922V3.9411Z"
      fill="#DCD8D4"
    />
    <path
      d="M23.9719 12.3129H15.7374C15.3253 12.3129 14.9912 12.647 14.9912 13.0591V13.4056C14.9912 13.8177 15.3253 14.1518 15.7374 14.1518H23.9719C24.384 14.1518 24.7181 13.8177 24.7181 13.4056V13.0591C24.7181 12.647 24.384 12.3129 23.9719 12.3129Z"
      fill="#FD426A"
    />
    <path
      d="M23.9719 18.0234H15.7374C15.3253 18.0234 14.9912 18.3575 14.9912 18.7696V19.1161C14.9912 19.5282 15.3253 19.8623 15.7374 19.8623H23.9719C24.384 19.8623 24.7181 19.5282 24.7181 19.1161V18.7696C24.7181 18.3575 24.384 18.0234 23.9719 18.0234Z"
      fill="#FD426A"
    />
    <path
      d="M23.9719 23.8303H15.7374C15.3253 23.8303 14.9912 24.1644 14.9912 24.5765V24.923C14.9912 25.3351 15.3253 25.6692 15.7374 25.6692H23.9719C24.384 25.6692 24.7181 25.3351 24.7181 24.923V24.5765C24.7181 24.1644 24.384 23.8303 23.9719 23.8303Z"
      fill="#FD426A"
    />
    <path
      d="M12.6206 14.1518C13.1284 14.1518 13.5401 13.7402 13.5401 13.2324C13.5401 12.7246 13.1284 12.3129 12.6206 12.3129C12.1128 12.3129 11.7012 12.7246 11.7012 13.2324C11.7012 13.7402 12.1128 14.1518 12.6206 14.1518Z"
      fill="#FD426A"
    />
    <path
      d="M12.6206 19.8623C13.1284 19.8623 13.5401 19.4506 13.5401 18.9428C13.5401 18.435 13.1284 18.0234 12.6206 18.0234C12.1128 18.0234 11.7012 18.435 11.7012 18.9428C11.7012 19.4506 12.1128 19.8623 12.6206 19.8623Z"
      fill="#FD426A"
    />
    <path
      d="M12.6206 25.6692C13.1284 25.6692 13.5401 25.2576 13.5401 24.7498C13.5401 24.242 13.1284 23.8303 12.6206 23.8303C12.1128 23.8303 11.7012 24.242 11.7012 24.7498C11.7012 25.2576 12.1128 25.6692 12.6206 25.6692Z"
      fill="#FD426A"
    />
    <path
      d="M17.9434 12.3129H24.0955C24.2607 12.3129 24.4191 12.3785 24.5359 12.4953C24.6527 12.6121 24.7183 12.7705 24.7183 12.9357V13.5285C24.7184 13.6104 24.7023 13.6914 24.671 13.767C24.6398 13.8426 24.5939 13.9114 24.536 13.9692C24.4782 14.0271 24.4095 14.073 24.3339 14.1044C24.2583 14.1357 24.1773 14.1518 24.0955 14.1518H17.9434V12.3129Z"
      fill="#D3006C"
    />
    <path
      d="M17.9434 18.0234H24.0955C24.2607 18.0234 24.4191 18.089 24.5359 18.2058C24.6527 18.3226 24.7183 18.481 24.7183 18.6462V19.239C24.7184 19.3208 24.7023 19.4019 24.671 19.4775C24.6398 19.5531 24.5939 19.6218 24.536 19.6797C24.4782 19.7376 24.4095 19.7835 24.3339 19.8148C24.2583 19.8462 24.1773 19.8623 24.0955 19.8623H17.9434V18.0234Z"
      fill="#D3006C"
    />
    <path
      d="M17.8945 23.8303H24.0926C24.2584 23.8303 24.4175 23.8962 24.5347 24.0134C24.652 24.1307 24.7179 24.2897 24.7179 24.4556V25.044C24.7179 25.1261 24.7017 25.2074 24.6703 25.2833C24.6388 25.3591 24.5928 25.4281 24.5347 25.4861C24.4767 25.5442 24.4077 25.5902 24.3319 25.6216C24.256 25.6531 24.1747 25.6692 24.0926 25.6692H17.8945V23.8303Z"
      fill="#D3006C"
    />
  </svg>
);

export const PencilColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.3656 29.0677L28.8926 12.9443L23.8009 7.7136L7.27393 23.837L12.3656 29.0677Z"
      fill="#FD7C59"
    />
    <path
      d="M12.3656 29.0681L28.8926 12.9447L26.3315 10.3137L9.80449 26.4371L12.3656 29.0681Z"
      fill="#FD426A"
    />
    <path
      d="M12.3656 29.0679L28.8926 12.9445L28.2507 12.2851L11.7237 28.4084L12.3656 29.0679Z"
      fill="#D3006C"
    />
    <path
      d="M12.3656 29.0677L28.8926 12.9443L23.8009 7.7136L7.27393 23.837L12.3656 29.0677Z"
      fill="#FD7C59"
    />
    <path
      d="M12.3656 29.0681L28.8926 12.9447L26.3315 10.3137L9.80449 26.4371L12.3656 29.0681Z"
      fill="#FD426A"
    />
    <path
      d="M12.3656 29.0679L28.8926 12.9445L28.2507 12.2851L11.7237 28.4084L12.3656 29.0679Z"
      fill="#D3006C"
    />
    <path
      d="M12.3741 29.0434L7.04464 30.7374L5.7499 31.1488L5.40252 31.2598C5.24896 31.3034 5.0897 31.3234 4.93015 31.3193C4.81628 31.3196 4.70584 31.2803 4.61766 31.2082C4.60617 31.1982 4.59554 31.1872 4.58589 31.1754C4.47496 31.0419 4.47548 30.8266 4.57131 30.5533L4.64474 30.3724L5.30774 28.7436L7.304 23.8368L9.34193 25.9307L10.7075 27.3338L11.7382 28.3927L12.3741 29.0434Z"
      fill="#EDEDED"
    />
    <path
      d="M31.477 10.3785L28.8542 12.9374L23.7695 7.71259L26.3913 5.15414C26.6078 4.9429 26.8637 4.77642 27.1445 4.66421C27.4252 4.55201 27.7253 4.49627 28.0276 4.50019C28.3299 4.50412 28.6284 4.56762 28.9062 4.68707C29.1839 4.80652 29.4355 4.97958 29.6464 5.19637L30.5651 6.14061L31.5182 7.1203C31.8786 7.48941 32.1041 7.9694 32.1583 8.48268C32.1583 8.50145 32.1619 8.52022 32.1635 8.53847C32.1923 8.87691 32.1456 9.21753 32.0269 9.53572C31.9082 9.85392 31.7204 10.1418 31.477 10.3785Z"
      fill="white"
    />
    <path
      d="M12.3736 29.044L7.04418 30.7379L5.74944 31.1493L5.40206 31.2604C5.2485 31.3039 5.08924 31.3239 4.92969 31.3198L6.84315 30.4965L7.46291 30.2301L11.7398 28.3907L12.3736 29.044Z"
      fill="#C6C0BA"
    />
    <path
      d="M11.7378 28.3903L7.46094 30.2297L6.84118 30.4962L4.92772 31.3194C4.81386 31.3198 4.70341 31.2805 4.61523 31.2084L6.23912 29.7131L9.80355 26.4335L10.6999 27.3402L11.7378 28.3903Z"
      fill="#DCD8D4"
    />
    <path
      d="M6.24059 29.7132L4.61669 31.2086C4.60519 31.1985 4.59456 31.1876 4.58492 31.1757C4.47398 31.0423 4.4745 30.8269 4.57033 30.5537L4.64377 30.3728L5.30677 28.744L5.57551 29.024L6.24059 29.7132Z"
      fill="#5E5C7A"
    />
    <path
      d="M6.84118 30.4963L4.92772 31.3195C4.81386 31.3199 4.70341 31.2806 4.61523 31.2085L6.23912 29.7132L6.84118 30.4963Z"
      fill="#404068"
    />
    <path
      d="M7.04899 30.7427L5.68187 31.1739L5.67249 31.177L5.45792 31.2448C5.43865 31.2505 5.41938 31.2552 5.40011 31.2599C5.24654 31.3034 5.08729 31.3235 4.92773 31.3193L6.84119 30.4961L6.90942 30.5769L7.04431 30.7375L7.04899 30.7427Z"
      fill="#25254F"
    />
    <path
      d="M32.1646 8.53838L28.2278 12.2825L26.3086 10.3101L30.5662 6.13895L31.5193 7.11811C31.8932 7.50172 32.1215 8.00411 32.1646 8.53838Z"
      fill="#D9ECFD"
    />
    <path
      d="M31.4773 10.3783L28.8545 12.9373L28.2295 12.2824L32.1605 8.48254C32.1605 8.50131 32.1642 8.52008 32.1658 8.53833C32.1943 8.87693 32.1473 9.21765 32.0282 9.53585C31.9092 9.85406 31.721 10.1418 31.4773 10.3783Z"
      fill="#BED7EA"
    />
  </svg>
);

export const StudentBookColorIcon = (props: IconProps) => (
  <svg
    {...props}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M29.6922 4.05167V23.758H29.7002V26.1417H29.6922V27.6836H10.1108C10.0209 27.6836 9.93251 27.6801 9.84462 27.6731C8.36204 27.5602 7.2002 26.5105 7.2002 25.2316V6.50367C7.2002 5.2142 8.3814 4.15804 9.88137 4.05919C9.95733 4.05418 10.0333 4.05167 10.1108 4.05167H29.6922Z"
      fill="#FD7C59"
    />
    <path
      d="M9.80389 3.59991C9.8142 3.59991 9.8244 3.60197 9.83391 3.60595C9.84343 3.60993 9.85208 3.61577 9.85937 3.62313C9.86665 3.63049 9.87243 3.63923 9.87637 3.64885C9.88031 3.65847 9.88234 3.66878 9.88234 3.67919V32.3205C9.88234 32.3309 9.88031 32.3412 9.87637 32.3508C9.87243 32.3605 9.86665 32.3692 9.85937 32.3766C9.85208 32.3839 9.84343 32.3898 9.83391 32.3937C9.8244 32.3977 9.8142 32.3998 9.80389 32.3998C9.11361 32.3998 8.4516 32.1227 7.96349 31.6294C7.47539 31.1362 7.20117 30.4672 7.20117 29.7697V6.23003C7.20117 5.53248 7.47539 4.8635 7.96349 4.37026C8.4516 3.87702 9.11361 3.59991 9.80389 3.59991Z"
      fill="#FD426A"
    />
    <path
      d="M28.7076 30.5506H29.6927V32.4H10.0348C9.96725 32.4 9.90121 32.4 9.83617 32.393C9.12075 32.3421 8.45103 32.019 7.96204 31.4888C7.47305 30.9586 7.20116 30.2608 7.20117 29.5361V28.6912C7.20113 27.9583 7.47909 27.2533 7.97785 26.7213C8.4766 26.1893 9.1582 25.8708 9.88234 25.8312C9.93547 25.8272 9.98512 25.8272 10.0348 25.8272H29.6927V27.7409H28.7076V30.5506Z"
      fill="#D3006C"
    />
    <path
      d="M10.1606 27.7336H28.7485V30.5433H10.1606C9.8494 30.5433 9.55097 30.4184 9.33094 30.1961C9.11092 29.9737 8.9873 29.6722 8.9873 29.3577V28.9187C8.98744 28.6043 9.11111 28.3029 9.33112 28.0807C9.55114 27.8584 9.84948 27.7336 10.1606 27.7336Z"
      fill="white"
    />
    <path
      d="M18.1227 19.5679C17.9888 19.5681 17.8573 19.5313 17.7424 19.4616L12.9759 16.5921C12.8701 16.5285 12.7816 16.4393 12.7183 16.3326C12.655 16.2259 12.6189 16.105 12.6131 15.9807C12.6074 15.8565 12.6322 15.7327 12.6854 15.6205C12.7385 15.5083 12.8184 15.4112 12.9178 15.3378L17.6843 11.8216C17.8439 11.7039 18.0431 11.6551 18.2383 11.6858C18.4335 11.7166 18.6085 11.8245 18.725 11.9857C18.8415 12.1469 18.8898 12.3482 18.8594 12.5455C18.8289 12.7427 18.7222 12.9196 18.5627 13.0373L14.7062 15.8822L18.5046 18.1691C18.6448 18.2534 18.7537 18.382 18.8146 18.535C18.8755 18.6879 18.885 18.8569 18.8417 19.0159C18.7983 19.1749 18.7044 19.315 18.5745 19.4149C18.4447 19.5147 18.2859 19.5686 18.1227 19.5684V19.5679Z"
      fill="#F8FCFF"
    />
    <path
      d="M21.6988 19.5678C21.5356 19.568 21.3769 19.5141 21.247 19.4143C21.1171 19.3144 21.0233 19.1742 20.9799 19.0153C20.9365 18.8563 20.946 18.6873 21.0069 18.5343C21.0678 18.3814 21.1768 18.2528 21.317 18.1685L25.1153 15.8815L21.2589 13.0367C21.1799 12.9784 21.1131 12.905 21.0622 12.8206C21.0112 12.7362 20.9773 12.6425 20.9622 12.5448C20.9471 12.4472 20.9512 12.3475 20.9743 12.2514C20.9973 12.1554 21.0389 12.0648 21.0966 11.985C21.1542 11.9052 21.2269 11.8376 21.3104 11.7862C21.3939 11.7347 21.4866 11.7004 21.5833 11.6852C21.6799 11.6699 21.7786 11.6741 21.8736 11.6974C21.9687 11.7207 22.0583 11.7627 22.1372 11.8209L26.9038 15.3372C27.0032 15.4105 27.0831 15.5077 27.1362 15.6199C27.1894 15.7321 27.2142 15.8558 27.2085 15.9801C27.2027 16.1044 27.1666 16.2253 27.1033 16.332C27.04 16.4387 26.9515 16.5278 26.8457 16.5915L22.0792 19.461C21.9644 19.5309 21.8329 19.5678 21.6988 19.5678Z"
      fill="white"
    />
    <path
      d="M14.6302 3.59991C14.8323 3.59991 14.9957 3.73037 14.9957 3.89143V11.7572C14.9957 11.9449 14.7032 12.0241 14.5657 11.8741L13.531 10.7482C13.3219 10.5209 12.8964 10.5209 12.6869 10.7482L11.6522 11.8741C11.5146 12.0246 11.2217 11.9449 11.2217 11.7572V3.89143C11.2217 3.73037 11.3855 3.59991 11.5876 3.59991H14.6302Z"
      fill="#D3006C"
    />
    <path
      d="M28.7574 29.1455V30.5504H10.1694C10.0725 30.5504 9.97591 30.5384 9.88188 30.5148C9.71017 30.471 9.55057 30.3883 9.41515 30.2729C9.38884 30.2509 9.3655 30.2278 9.33919 30.2032C9.2302 30.0932 9.14379 29.9624 9.08491 29.8186C9.02604 29.6747 8.99585 29.5205 8.9961 29.3648V29.2273L9.88188 29.2238L28.7494 29.1455H28.7574Z"
      fill="#D9ECFD"
    />
  </svg>
);

export const TeacherBookColorIcon = (props: IconProps) => (
  <svg
    {...props}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M30.9036 10.6039H5.09705C4.03435 10.6039 3.17285 11.4654 3.17285 12.5281V28.7047C3.17285 29.7674 4.03435 30.6289 5.09705 30.6289H30.9036C31.9664 30.6289 32.8278 29.7674 32.8278 28.7047V12.5281C32.8278 11.4654 31.9664 10.6039 30.9036 10.6039Z"
      fill="#FD7C59"
    />
    <path d="M19.08 10.6039H16.9199V30.6289H19.08V10.6039Z" fill="#FD426A" />
    <path
      d="M32.8276 12.5281V28.7047C32.8277 28.9574 32.7779 29.2076 32.6813 29.4411C32.5846 29.6746 32.4429 29.8868 32.2642 30.0654C32.0855 30.2441 31.8733 30.3859 31.6398 30.4825C31.4064 30.5792 31.1561 30.629 30.9034 30.6289H30.8701V10.6039H30.9034C31.1561 10.6038 31.4064 10.6536 31.6398 10.7502C31.8733 10.8469 32.0855 10.9886 32.2642 11.1673C32.4429 11.346 32.5846 11.5582 32.6813 11.7917C32.7779 12.0251 32.8277 12.2754 32.8276 12.5281V12.5281Z"
      fill="#FD426A"
    />
    <path
      d="M30.5305 9.97369H5.49248C5.02525 9.97369 4.64648 10.3525 4.64648 10.8197V28.3427C4.64648 28.8099 5.02525 29.1887 5.49248 29.1887H30.5305C30.9977 29.1887 31.3765 28.8099 31.3765 28.3427V10.8197C31.3765 10.3525 30.9977 9.97369 30.5305 9.97369Z"
      fill="#8EA5B5"
    />
    <path
      d="M17.6105 28.5137C14.7715 27.0287 9.23513 26.7997 5.04743 28.0637C4.9411 28.0637 4.83911 28.0215 4.76392 27.9463C4.68873 27.8711 4.64648 27.7691 4.64648 27.6628V9.2947C4.64648 9.18836 4.68873 9.08638 4.76392 9.01118C4.83911 8.93599 4.9411 8.89375 5.04743 8.89375C9.23513 7.55605 13.4228 7.55605 17.6105 8.89375C17.7169 8.89375 17.8189 8.93599 17.894 9.01118C17.9692 9.08638 18.0115 9.18836 18.0115 9.2947V28.1128C18.0115 28.2191 17.9692 28.3211 17.894 28.3963C17.8189 28.4715 17.7169 28.5137 17.6105 28.5137V28.5137Z"
      fill="#DCD8D4"
    />
    <path
      d="M17.7016 28.0638C16.0321 25.7238 11.2158 23.6497 7.97261 24.9138C7.80116 24.9138 7.66211 24.7338 7.66211 24.5128V6.14473C7.66211 5.92333 7.80116 5.74378 7.97261 5.74378C11.2158 4.40593 16.3471 6.95878 17.7016 8.89378C17.8731 8.89378 18.0121 9.07378 18.0121 9.29473V27.6628C18.0121 27.8842 17.8731 28.0638 17.7016 28.0638Z"
      fill="white"
    />
    <path
      d="M31.3767 9.29464V27.6627C31.3767 27.7691 31.3345 27.8711 31.2593 27.9463C31.1841 28.0214 31.0821 28.0637 30.9758 28.0637C26.7881 26.7996 21.6117 26.9387 18.4127 28.5137C18.3063 28.5137 18.2043 28.4714 18.1292 28.3963C18.054 28.3211 18.0117 28.2191 18.0117 28.1127V9.29464C18.0117 9.1883 18.054 9.08632 18.1292 9.01113C18.2043 8.93593 18.3063 8.89369 18.4127 8.89369C19.1675 8.65197 19.935 8.45172 20.7117 8.29384C24.1317 7.60084 27.5531 7.80079 30.9758 8.89369C31.0821 8.89369 31.1841 8.93593 31.2593 9.01113C31.3345 9.08632 31.3767 9.1883 31.3767 9.29464Z"
      fill="#DCD8D4"
    />
  </svg>
);

export const CalendarColorIcon = (props: IconProps) => (
  <svg
    {...props}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M31 8.35095V10H18V6H28.8313C30.0294 6 31 7.05317 31 8.35095Z"
      fill="#D3006C"
    />
    <path
      d="M18 6V10H5V8.35095C5 7.05317 5.97245 6 7.17219 6H18Z"
      fill="#E9456B"
    />
    <path
      d="M18 10V30H28.8313C29.729 30 30.4986 29.3271 30.8284 28.3687C30.9393 28.047 31 27.6939 31 27.3228V10H18Z"
      fill="#FD426A"
    />
    <path
      d="M28 13.7883V26.2117C28 26.647 27.7009 27 27.3315 27H18V13H27.3315C27.7009 13 28 13.353 28 13.7883Z"
      fill="#DAECFB"
    />
    <path
      d="M8.5 14L9 26.5C8.58497 26.5 9 27.0113 9 26.5H18V30H7.17219C5.97245 30 5 28.8009 5 27.3232V10H18V13.5L9 14C8.58497 14 8.5 13.4887 8.5 14Z"
      fill="#FD7C59"
    />
    <path
      d="M8 13.7883V26.2117C8 26.647 8.29909 27 8.66849 27H18V13H8.66849C8.29909 13 8 13.353 8 13.7883Z"
      fill="white"
    />
    <path
      d="M8 17.5L19 17.5"
      stroke="#DAECFB"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M8 22.5L18 22.5"
      stroke="#DAECFB"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M14.7998 13V27"
      stroke="#DAECFB"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M21.5 13L21.5 27"
      stroke="#AEDCFF"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M18 17.5H28"
      stroke="#AEDCFF"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M18 22.5H28"
      stroke="#AEDCFF"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
    />
    <path
      d="M16.7002 14.6916L17.9542 15.9457L19.8999 14"
      stroke="#E9456B"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M10 24.6916L11.254 25.9457L13.1997 24"
      stroke="#E9456B"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M10 19.6912L11.254 20.9457L13.1997 19"
      stroke="#E9456B"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
    <path
      d="M23.2998 19.6912L24.5538 20.9457L26.4995 19"
      stroke="#E9456B"
      strokeWidth="0.673782"
      strokeMiterlimit="10"
      strokeLinecap="round"
    />
  </svg>
);

export const RocketQuickColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.7041 29.3721C9.6684 29.4093 9.63285 29.4457 9.59745 29.4814L7.8753 30.5263L7.85865 30.5308C7.29345 30.6739 6.77865 30.6046 6.44475 30.286C6.11085 29.9674 6.0141 29.4589 6.12975 28.8869C6.12975 28.8806 6.12975 28.8748 6.13335 28.869L7.08825 27.0937C7.12155 27.0559 7.1562 27.0186 7.19175 26.9817C8.2929 25.822 9.74505 25.4111 10.4435 26.0587L10.4538 26.0677C10.457 26.0706 10.46 26.0738 10.4628 26.0771C11.141 26.7418 10.8053 28.2151 9.7041 29.3721Z"
      fill="#FFDF40"
    />
    <path
      d="M10.4409 26.0586L10.4512 26.0676C10.4544 26.0705 10.4574 26.0736 10.4602 26.077C11.1429 26.7417 10.8072 28.2127 9.70601 29.3719C9.67031 29.4091 9.63476 29.4456 9.59936 29.4813L7.87586 30.5262L7.85921 30.5307C7.29401 30.6738 6.77921 30.6045 6.44531 30.2859"
      fill="#FFBE40"
    />
    <path
      d="M7.5312 25.592L6.05835 25.2482C4.5558 25.0003 4.4676 24.0476 5.598 23.4455L7.92405 22.3034L4.986 21.9335C4.1283 21.9875 3.8835 21.1343 4.65345 20.7635L12.5856 17.9173L15.5421 20.7248L10.4679 26.0857L7.93125 28.765L6.1083 28.873"
      fill="#FFDF40"
    />
    <path
      d="M7.86328 30.5379L7.89838 28.7339L10.4436 26.0627L15.5354 20.7199L18.4914 23.5279L16.0569 31.5959C15.7271 32.3838 14.8626 32.1836 14.8725 31.3259L14.3519 28.4113L13.3308 30.7926C12.7876 31.9527 11.8318 31.9136 11.5069 30.4259L11.0875 28.9724"
      fill="#FFBE40"
    />
    <path
      d="M15.5874 20.77L15.4876 20.6752C14.8575 20.0766 13.8614 20.1022 13.2629 20.7323L10.9365 23.1812C10.3379 23.8113 10.3635 24.8073 10.9936 25.4059L11.0934 25.5007C11.7235 26.0993 12.7196 26.0738 13.3182 25.4436L15.6445 22.9948C16.2431 22.3647 16.2175 21.3686 15.5874 20.77Z"
      fill="#FF9F40"
    />
    <path
      d="M11.0445 25.4537C10.387 24.8291 10.3186 23.8337 10.8919 23.2298L13.3093 20.6846C13.8826 20.0812 14.8807 20.0996 15.5382 20.7229"
      fill="#FFBE40"
    />
    <path
      d="M31.7989 4.62467L30.2788 12.6522C30.2586 12.7591 30.2098 12.8585 30.1376 12.9399C30.0655 13.0212 29.9726 13.0815 29.8689 13.1144C29.7652 13.1472 29.6545 13.1513 29.5487 13.1262C29.4428 13.1012 29.3457 13.048 29.2677 12.9722L22.8098 6.70772C22.7318 6.63196 22.6757 6.53649 22.6475 6.43144C22.6193 6.32639 22.6201 6.21568 22.6498 6.11104C22.6795 6.00641 22.7369 5.91175 22.816 5.83712C22.8951 5.76248 22.9929 5.71065 23.0991 5.68712L31.0771 3.92447C31.1744 3.90302 31.2756 3.90603 31.3714 3.93325C31.4673 3.96046 31.5549 4.01102 31.6264 4.0804C31.6979 4.14978 31.7511 4.23581 31.7812 4.3308C31.8113 4.42578 31.8174 4.52675 31.7989 4.62467V4.62467Z"
      fill="#FD426A"
    />
    <path
      d="M31.6194 4.07338C31.6938 4.1425 31.7493 4.22941 31.7808 4.32593C31.8123 4.42245 31.8187 4.5254 31.7994 4.62508L30.2798 12.6526C30.2596 12.7596 30.2109 12.859 30.1387 12.9404C30.0666 13.0219 29.9737 13.0822 29.87 13.1151C29.7663 13.1479 29.6556 13.1521 29.5497 13.1271C29.4438 13.1021 29.3467 13.0488 29.2686 12.973L26.0439 9.84509"
      fill="#D3006C"
    />
    <path
      d="M28.7812 12.5102L23.415 7.30644L12.0648 19.0108L17.431 24.2146L28.7812 12.5102Z"
      fill="#FD426A"
    />
    <path
      d="M28.7985 12.5224L26.0322 9.83791L14.678 21.5383L17.4442 24.2228L28.7985 12.5224Z"
      fill="#D3006C"
    />
    <path
      d="M26.0169 15.4075L20.625 10.1596L23.4082 7.28227L28.8006 12.5297L26.0169 15.4075Z"
      fill="#D9ECFD"
    />
    <path
      d="M26.0162 15.4071L23.2676 12.7233L26.0531 9.84779L28.8017 12.5316L26.0162 15.4071Z"
      fill="#BED7EA"
    />
    <path
      d="M20.2801 21.3162L14.8887 16.0691L17.6724 13.1914L23.0638 18.4384L20.2801 21.3162Z"
      fill="#D9ECFD"
    />
    <path
      d="M7.64303 18.6178L11.2745 22.0014C11.5144 22.2264 11.9045 22.1976 12.1426 21.9406L15.8753 17.932C16.1147 17.6755 16.1138 17.2867 15.8753 17.0639L14.8538 16.0541C14.7815 15.9884 14.6938 15.9419 14.5988 15.9189C14.5038 15.8959 14.4046 15.8972 14.3102 15.9227L7.96523 17.5571C7.47968 17.6804 7.29383 18.292 7.64303 18.6178Z"
      fill="#FD426A"
    />
    <path
      d="M20.2793 21.316L17.5303 18.6318L20.3158 15.7562L23.0648 18.4405L20.2793 21.316Z"
      fill="#BED7EA"
    />
    <path
      d="M18.0301 28.5457L14.4643 25.0929C14.2285 24.8652 14.2357 24.4755 14.48 24.223L18.2915 20.2887C18.5354 20.0367 18.9215 20.0187 19.1587 20.2459L20.1887 21.2463C20.258 21.3153 20.3088 21.4007 20.3366 21.4944C20.3643 21.5882 20.3681 21.6875 20.3476 21.7831L19.0723 28.17C18.9742 28.6609 18.3734 28.8778 18.0301 28.5457Z"
      fill="#D3006C"
    />
    <path
      d="M17.5133 19.5897C17.7615 19.3284 17.7509 18.9154 17.4897 18.6672C17.2284 18.419 16.8154 18.4296 16.5672 18.6909L12.786 22.6713C12.5378 22.9325 12.5484 23.3455 12.8097 23.5937C13.071 23.8419 13.484 23.8313 13.7322 23.5701L17.5133 19.5897Z"
      fill="#D3006C"
    />
    <path
      d="M12.8116 23.5945C12.7495 23.5355 12.6996 23.4648 12.6647 23.3865C12.6299 23.3082 12.6108 23.2239 12.6086 23.1382C12.6064 23.0525 12.6211 22.9673 12.6518 22.8873C12.6826 22.8073 12.7287 22.7341 12.7878 22.672L16.5678 18.6913C16.6268 18.6292 16.6974 18.5793 16.7757 18.5444C16.854 18.5096 16.9384 18.4905 17.0241 18.4883C17.1097 18.4861 17.195 18.5008 17.275 18.5315C17.355 18.5623 17.4281 18.6084 17.4902 18.6675V18.6675"
      fill="#FD426A"
    />
  </svg>
);

export const RocketKidsColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.7041 29.3721C9.6684 29.4093 9.63285 29.4457 9.59745 29.4814L7.8753 30.5263L7.85865 30.5308C7.29345 30.6739 6.77865 30.6046 6.44475 30.286C6.11085 29.9674 6.0141 29.4589 6.12975 28.8869C6.12975 28.8806 6.12975 28.8748 6.13335 28.869L7.08825 27.0937C7.12155 27.0559 7.1562 27.0186 7.19175 26.9817C8.2929 25.822 9.74505 25.4111 10.4435 26.0587L10.4538 26.0677C10.457 26.0706 10.46 26.0738 10.4628 26.0771C11.141 26.7418 10.8053 28.2151 9.7041 29.3721Z"
      fill="#FFDF40"
    />
    <path
      d="M10.4409 26.0586L10.4512 26.0676C10.4544 26.0705 10.4574 26.0736 10.4602 26.077C11.1429 26.7417 10.8072 28.2127 9.70601 29.3719C9.67031 29.4091 9.63476 29.4456 9.59936 29.4813L7.87586 30.5262L7.85921 30.5307C7.29401 30.6738 6.77921 30.6045 6.44531 30.2859"
      fill="#FFBE40"
    />
    <path
      d="M7.5312 25.592L6.05835 25.2482C4.5558 25.0003 4.4676 24.0476 5.598 23.4455L7.92405 22.3034L4.986 21.9335C4.1283 21.9875 3.8835 21.1343 4.65345 20.7635L12.5856 17.9173L15.5421 20.7248L10.4679 26.0857L7.93125 28.765L6.1083 28.873"
      fill="#FFDF40"
    />
    <path
      d="M7.86328 30.5379L7.89838 28.7339L10.4436 26.0627L15.5354 20.7199L18.4914 23.5279L16.0569 31.5959C15.7271 32.3838 14.8626 32.1836 14.8725 31.3259L14.3519 28.4113L13.3308 30.7926C12.7876 31.9527 11.8318 31.9136 11.5069 30.4259L11.0875 28.9724"
      fill="#FFBE40"
    />
    <path
      d="M15.5874 20.77L15.4876 20.6752C14.8575 20.0766 13.8614 20.1022 13.2629 20.7323L10.9365 23.1812C10.3379 23.8113 10.3635 24.8073 10.9936 25.4059L11.0934 25.5007C11.7235 26.0993 12.7196 26.0738 13.3182 25.4436L15.6445 22.9948C16.2431 22.3647 16.2175 21.3686 15.5874 20.77Z"
      fill="#FF9F40"
    />
    <path
      d="M11.0445 25.4537C10.387 24.8291 10.3186 23.8337 10.8919 23.2298L13.3093 20.6846C13.8826 20.0812 14.8807 20.0996 15.5382 20.7229"
      fill="#FFBE40"
    />
    <path
      d="M31.7989 4.62467L30.2788 12.6522C30.2586 12.7591 30.2098 12.8585 30.1376 12.9399C30.0655 13.0212 29.9726 13.0815 29.8689 13.1144C29.7652 13.1472 29.6545 13.1513 29.5487 13.1262C29.4428 13.1012 29.3457 13.048 29.2677 12.9722L22.8098 6.70772C22.7318 6.63196 22.6757 6.53649 22.6475 6.43144C22.6193 6.32639 22.6201 6.21568 22.6498 6.11104C22.6795 6.00641 22.7369 5.91175 22.816 5.83712C22.8951 5.76248 22.9929 5.71065 23.0991 5.68712L31.0771 3.92447C31.1744 3.90302 31.2756 3.90603 31.3714 3.93325C31.4673 3.96046 31.5549 4.01102 31.6264 4.0804C31.6979 4.14978 31.7511 4.23581 31.7812 4.3308C31.8113 4.42578 31.8174 4.52675 31.7989 4.62467V4.62467Z"
      fill="#FD426A"
    />
    <path
      d="M31.6194 4.07338C31.6938 4.1425 31.7493 4.22941 31.7808 4.32593C31.8123 4.42245 31.8187 4.5254 31.7994 4.62508L30.2798 12.6526C30.2596 12.7596 30.2109 12.859 30.1387 12.9404C30.0666 13.0219 29.9737 13.0822 29.87 13.1151C29.7663 13.1479 29.6556 13.1521 29.5497 13.1271C29.4438 13.1021 29.3467 13.0488 29.2686 12.973L26.0439 9.84509"
      fill="#D3006C"
    />
    <path
      d="M28.7812 12.5102L23.415 7.30644L12.0648 19.0108L17.431 24.2146L28.7812 12.5102Z"
      fill="#FD426A"
    />
    <path
      d="M28.7985 12.5224L26.0322 9.83791L14.678 21.5383L17.4442 24.2228L28.7985 12.5224Z"
      fill="#D3006C"
    />
    <path
      d="M26.0169 15.4075L20.625 10.1596L23.4082 7.28227L28.8006 12.5297L26.0169 15.4075Z"
      fill="#D9ECFD"
    />
    <path
      d="M26.0162 15.4071L23.2676 12.7233L26.0531 9.84779L28.8017 12.5316L26.0162 15.4071Z"
      fill="#BED7EA"
    />
    <path
      d="M20.2801 21.3162L14.8887 16.0691L17.6724 13.1914L23.0638 18.4384L20.2801 21.3162Z"
      fill="#D9ECFD"
    />
    <path
      d="M7.64303 18.6178L11.2745 22.0014C11.5144 22.2264 11.9045 22.1976 12.1426 21.9406L15.8753 17.932C16.1147 17.6755 16.1138 17.2867 15.8753 17.0639L14.8538 16.0541C14.7815 15.9884 14.6938 15.9419 14.5988 15.9189C14.5038 15.8959 14.4046 15.8972 14.3102 15.9227L7.96523 17.5571C7.47968 17.6804 7.29383 18.292 7.64303 18.6178Z"
      fill="#FD426A"
    />
    <path
      d="M20.2793 21.316L17.5303 18.6318L20.3158 15.7562L23.0648 18.4405L20.2793 21.316Z"
      fill="#BED7EA"
    />
    <path
      d="M18.0301 28.5457L14.4643 25.0929C14.2285 24.8652 14.2357 24.4755 14.48 24.223L18.2915 20.2887C18.5354 20.0367 18.9215 20.0187 19.1587 20.2459L20.1887 21.2463C20.258 21.3153 20.3088 21.4007 20.3366 21.4944C20.3643 21.5882 20.3681 21.6875 20.3476 21.7831L19.0723 28.17C18.9742 28.6609 18.3734 28.8778 18.0301 28.5457Z"
      fill="#D3006C"
    />
    <path
      d="M17.5133 19.5897C17.7615 19.3284 17.7509 18.9154 17.4897 18.6672C17.2284 18.419 16.8154 18.4296 16.5672 18.6909L12.786 22.6713C12.5378 22.9325 12.5484 23.3455 12.8097 23.5937C13.071 23.8419 13.484 23.8313 13.7322 23.5701L17.5133 19.5897Z"
      fill="#D3006C"
    />
    <path
      d="M12.8116 23.5945C12.7495 23.5355 12.6996 23.4648 12.6647 23.3865C12.6299 23.3082 12.6108 23.2239 12.6086 23.1382C12.6064 23.0525 12.6211 22.9673 12.6518 22.8873C12.6826 22.8073 12.7287 22.7341 12.7878 22.672L16.5678 18.6913C16.6268 18.6292 16.6974 18.5793 16.7757 18.5444C16.854 18.5096 16.9384 18.4905 17.0241 18.4883C17.1097 18.4861 17.195 18.5008 17.275 18.5315C17.355 18.5623 17.4281 18.6084 17.4902 18.6675V18.6675"
      fill="#FD426A"
    />
  </svg>
);

export const RocketTeensColorIcon = ({ className = '' }: IconProps) => (
  <svg
    width="36"
    height="36"
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20.3669 30.2355L17.9909 25.8871C17.8694 25.7589 17.9207 25.5415 18.1012 25.4205L23.5718 22.0329C23.808 21.874 24.0987 21.9793 24.0938 22.2201C24.0488 24.4008 22.7204 27.1575 20.9506 30.1122C20.8228 30.3255 20.5141 30.3907 20.3669 30.2355Z"
      fill="#D3006C"
    />
    <path
      d="M5.77561 15.6447L10.1258 18.0203C10.2536 18.1418 10.4709 18.0905 10.5924 17.91L13.9796 12.4394C14.1389 12.2031 14.0331 11.9129 13.7924 11.9178C11.6117 11.9628 8.85496 13.2908 5.90026 15.0606C5.68561 15.1884 5.62081 15.4971 5.77561 15.6447Z"
      fill="#FD426A"
    />
    <path
      d="M6.43559 30.6802C9.91559 30.9682 12.5214 29.9041 14.253 27.4879C14.6017 27.3457 14.6265 26.8651 14.3011 26.5497L9.43349 21.8296C9.10814 21.5146 8.62844 21.5533 8.49659 21.9065C6.30824 23.954 5.24984 26.5546 5.54729 29.8185C5.60939 30.3522 5.82134 30.6136 6.43559 30.6802Z"
      fill="#FFDF40"
    />
    <path
      d="M5.78418 30.4246C5.92188 30.565 6.12843 30.6469 6.43578 30.6802C9.91577 30.9682 12.5216 29.9041 14.2532 27.4879C14.6019 27.3457 14.6267 26.8651 14.3013 26.5496L11.8416 24.1646"
      fill="#FFBE40"
    />
    <path
      d="M12.4087 25.3145C12.8371 24.8728 12.8263 24.1674 12.3847 23.7391C11.943 23.3108 11.2377 23.3217 10.8093 23.7635L8.23325 26.4199C7.80487 26.8617 7.81563 27.567 8.2573 27.9953C8.69896 28.4236 9.40427 28.4127 9.83266 27.9709L12.4087 25.3145Z"
      fill="#FF9F40"
    />
    <path
      d="M8.25873 27.9964C7.78533 27.5041 7.78533 26.7841 8.41263 26.2373L10.6325 23.9477C11.159 23.4046 11.9442 23.3114 12.3875 23.7398"
      fill="#FFBE40"
    />
    <path
      d="M29.2272 15.8725C29.2196 15.8887 29.2115 15.9044 29.2034 15.9202C26.8233 20.5943 21.4881 24.3176 15.8672 27.6053C15.5795 27.7718 15.2919 27.9384 15.0045 28.1052C14.782 28.2336 14.5237 28.2862 14.2686 28.2549C14.0136 28.2237 13.7756 28.1103 13.5906 27.932L10.9023 25.3251L13.1492 23.0076L23.0847 12.7612L24.5054 11.2951L29.5323 6.10971C29.675 6.26136 29.7573 6.46566 29.8401 6.71676C30.9651 10.1525 30.5903 13.1711 29.2272 15.8725Z"
      fill="#BED7EA"
    />
    <path
      d="M29.5316 6.10928L24.5046 11.2951L23.0822 12.7621L13.1475 23.0077L10.9007 25.3252L8.21194 22.7179C8.02743 22.5385 7.90642 22.3039 7.86728 22.0495C7.82814 21.7952 7.873 21.535 7.99504 21.3085C11.2314 15.2555 14.925 9.35514 19.7823 6.71454C19.8593 6.67314 19.9358 6.63219 20.0132 6.59214C22.6142 5.24213 25.5482 4.83038 28.9155 5.81994C29.1689 5.89509 29.3759 5.97113 29.5316 6.10928Z"
      fill="#E6F3FF"
    />
    <path
      d="M16.2458 27.3484C15.9035 27.5713 15.5601 27.7931 15.2157 28.0139C14.6685 28.3658 14.0255 28.3532 13.6407 27.9784L10.9043 25.3234L12.4824 23.6958L16.2458 27.3484Z"
      fill="#D3006C"
    />
    <path
      d="M8.69441 20.0454C8.48441 20.3934 8.27441 20.742 8.06441 21.0912C7.73276 21.647 7.76741 22.2887 8.15441 22.6662L10.8994 25.3271L12.469 23.7071L8.69441 20.0454Z"
      fill="#FD426A"
    />
    <path
      d="M21.4058 14.4901C20.8806 13.9807 20.216 13.6385 19.4963 13.5068C18.7765 13.3751 18.0339 13.4598 17.3623 13.7501C16.6906 14.0405 16.1202 14.5235 15.7231 15.1381C15.326 15.7526 15.12 16.4712 15.1313 17.2028C15.1426 17.9344 15.3705 18.6462 15.7864 19.2483C16.2022 19.8504 16.7873 20.3156 17.4675 20.5851C18.1478 20.8547 18.8927 20.9164 19.608 20.7626C20.3234 20.6088 20.977 20.2463 21.4864 19.7209C22.1691 19.0165 22.5442 18.0698 22.5291 17.0889C22.514 16.108 22.11 15.1732 21.4058 14.4901V14.4901ZM17.1758 18.8947C16.8383 18.5673 16.6054 18.1472 16.5066 17.6874C16.4079 17.2277 16.4476 16.7489 16.6209 16.3118C16.7942 15.8746 17.0932 15.4986 17.4802 15.2314C17.8671 14.9642 18.3246 14.8177 18.7948 14.8105C19.265 14.8032 19.7268 14.9356 20.1218 15.1908C20.5167 15.446 20.8272 15.8125 21.0138 16.2442C21.2005 16.6758 21.255 17.1531 21.1704 17.6156C21.0858 18.0782 20.866 18.5053 20.5387 18.843C20.3213 19.0673 20.062 19.2466 19.7753 19.3706C19.4887 19.4947 19.1804 19.5611 18.8681 19.5659C18.5559 19.5708 18.2457 19.5142 17.9553 19.3991C17.6649 19.2841 17.4001 19.113 17.1758 18.8956V18.8947Z"
      fill="#D3006C"
    />
    <path
      d="M20.4651 15.4608L21.4056 14.4902C21.0594 14.1404 20.6468 13.8633 20.1921 13.675C19.7375 13.4867 19.2498 13.391 18.7576 13.3935C18.2655 13.396 17.7788 13.4967 17.3261 13.6897C16.8734 13.8826 16.4637 14.164 16.121 14.5172C15.7784 14.8705 15.5097 15.2886 15.3307 15.747C15.1516 16.2054 15.0658 16.6949 15.0783 17.1869C15.0909 17.6789 15.2014 18.1634 15.4035 18.6122C15.6056 19.0609 15.8952 19.4648 16.2554 19.8002L17.1554 18.8727C16.7026 18.4337 16.4427 17.8328 16.433 17.2022C16.4232 16.5715 16.6644 15.9629 17.1034 15.5101C17.5424 15.0573 18.1433 14.7974 18.7739 14.7877C19.4045 14.7779 20.0132 15.0191 20.466 15.4581L20.4651 15.4608Z"
      fill="#FD426A"
    />
    <path
      d="M29.5315 6.10928L24.5045 11.2951L19.7822 6.71453C19.8592 6.67313 19.9357 6.63218 20.0131 6.59213C22.6141 5.24213 25.5481 4.83039 28.9155 5.81993C29.1688 5.89508 29.3758 5.97113 29.5315 6.10928Z"
      fill="#FD426A"
    />
    <path
      d="M29.2267 15.8725L24.5049 11.2951L29.5318 6.10971C29.6745 6.26136 29.7568 6.46566 29.8396 6.71676C30.9646 10.1525 30.5898 13.1711 29.2267 15.8725Z"
      fill="#D3006C"
    />
  </svg>
);

export const RocketYoungColorIcon = ({ className = '' }: IconProps) => (
  <svg
    width="36"
    height="36"
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.1991 7.87168C11.0641 7.86898 10.9291 7.94602 10.8238 8.10327C9.61782 9.9303 9.02382 12.1345 8.88477 14.618H11.1987"
      fill="#AEDCFF"
    />
    <path
      d="M11.1992 7.87171C11.3342 7.86855 11.4692 7.94605 11.5745 8.10284C12.7814 9.92943 13.3745 12.1331 13.5158 14.6166H11.2019"
      fill="#99CCFF"
    />
    <path
      d="M13.5083 14.6181H8.89941V28.0819H13.5083V14.6181Z"
      fill="#AEDCFF"
    />
    <path
      d="M13.5086 14.6181H11.2041V28.0819H13.5086V14.6181Z"
      fill="#99CCFF"
    />
    <path
      d="M24.598 7.87168C24.463 7.86898 24.328 7.94602 24.2272 8.10327C23.0361 9.9303 22.4497 12.1345 22.3125 14.618H24.5976"
      fill="#AEDCFF"
    />
    <path
      d="M24.5977 7.87171C24.7327 7.86855 24.8677 7.94605 24.9685 8.10284C26.1601 9.92943 26.7469 12.1331 26.885 14.6166H24.5999"
      fill="#99CCFF"
    />
    <path
      d="M26.8779 14.6181H22.3262V28.0819H26.8779V14.6181Z"
      fill="#AEDCFF"
    />
    <path
      d="M26.8786 14.6181H24.6025V28.0819H26.8786V14.6181Z"
      fill="#99CCFF"
    />
    <path
      d="M18.1957 32.3702C19.7709 31.4258 20.4309 30.3271 20.1756 29.0739C20.2656 28.9265 20.0789 28.7724 19.8156 28.7724H15.8557C15.5911 28.7724 15.4057 28.9265 15.4957 29.0739C15.4106 30.3499 16.0235 31.4619 17.4757 32.3702C17.716 32.5153 17.9099 32.5306 18.1957 32.3702Z"
      fill="#FFDF40"
    />
    <path
      d="M17.8452 32.4855C17.9824 32.4826 18.1162 32.4427 18.2327 32.3702C19.8509 31.4258 20.5286 30.327 20.2658 29.0739C20.3558 28.9265 20.1664 28.7724 19.8946 28.7724H17.8398"
      fill="#FFBE40"
    />
    <path
      d="M24.8631 30.4824C26.1737 29.6159 26.7227 28.6078 26.51 27.4583C26.5834 27.3231 26.4295 27.1821 26.2094 27.1821H22.9159C22.6959 27.1821 22.5415 27.3235 22.6153 27.4583C22.5442 28.6297 23.0545 29.6471 24.2619 30.4824C24.4639 30.6154 24.6264 30.6311 24.8631 30.4824Z"
      fill="#FFDF40"
    />
    <path
      d="M24.5495 30.5883C24.6627 30.5851 24.7723 30.5482 24.8645 30.4824C26.1752 29.6159 26.7242 28.6078 26.5115 27.4583C26.5848 27.3231 26.4309 27.1821 26.2109 27.1821H24.5459"
      fill="#FFBE40"
    />
    <path
      d="M24.5495 30.5883C24.6627 30.5851 24.7723 30.5482 24.8645 30.4824C26.1752 29.6159 26.7242 28.6078 26.5115 27.4583C26.5848 27.3231 26.4309 27.1821 26.2109 27.1821H24.5459"
      fill="#FFBE40"
    />
    <path
      d="M11.4755 30.4824C12.7862 29.6159 13.3351 28.6078 13.1221 27.4583C13.1954 27.3231 13.0415 27.1821 12.8215 27.1821H9.52613C9.30608 27.1821 9.15218 27.3235 9.22553 27.4583C9.15488 28.6297 9.66518 29.6471 10.8725 30.4824C11.0746 30.6154 11.237 30.6311 11.4755 30.4824Z"
      fill="#FFDF40"
    />
    <path
      d="M11.1603 30.5883C11.2735 30.5851 11.3831 30.5482 11.4753 30.4824C12.786 29.6159 13.3348 28.6078 13.1218 27.4583C13.1952 27.3231 13.0413 27.1821 12.8212 27.1821H11.1562"
      fill="#FFBE40"
    />
    <path
      d="M11.4755 30.4824C12.7862 29.6159 13.3351 28.6078 13.1221 27.4583C13.1954 27.3231 13.0415 27.1821 12.8215 27.1821H9.52613C9.30608 27.1821 9.15218 27.3235 9.22553 27.4583C9.15488 28.6297 9.66518 29.6471 10.8725 30.4824C11.0746 30.6154 11.237 30.6311 11.4755 30.4824Z"
      fill="#FFDF40"
    />
    <path
      d="M11.1603 30.5883C11.2735 30.5851 11.3831 30.5482 11.4753 30.4824C12.786 29.6159 13.3348 28.6078 13.1218 27.4583C13.1952 27.3231 13.0413 27.1821 12.8212 27.1821H11.1562"
      fill="#FFBE40"
    />
    <path
      d="M17.2729 3.7523C15.2376 5.62979 14.2354 7.89477 14.001 10.4467H21.8098C21.525 8.00515 20.1867 5.4365 18.5284 3.77077C18.1936 3.43465 17.6226 3.42969 17.2729 3.7523Z"
      fill="#FD426A"
    />
    <path
      d="M17.9061 3.51438C17.6725 3.51007 17.446 3.59515 17.2729 3.75227C15.2376 5.62977 14.2354 7.89475 14.001 10.4467H17.9052"
      fill="#FD426A"
    />
    <path
      d="M17.9062 3.51438C18.1401 3.51 18.3668 3.5949 18.5403 3.75183C20.5766 5.62887 21.5796 7.89295 21.8159 10.4449H17.9112"
      fill="#D3006C"
    />
    <path
      d="M21.8032 10.4468H14.0254V24.2814H21.8032V10.4468Z"
      fill="#FD426A"
    />
    <path d="M21.803 10.4468H17.9141V24.2814H21.803V10.4468Z" fill="#D3006C" />
    <path
      d="M17.889 9.95735V16.0138H14.5352C14.6517 14.7284 14.9852 13.5263 15.585 12.4246C16.0467 11.5815 16.6413 10.8185 17.3459 10.1651C17.4936 10.0287 17.6881 9.95431 17.889 9.95735V9.95735Z"
      fill="#E6F3FF"
    />
    <path
      d="M21.2476 16.0125H17.8929L17.8906 12.4233V9.95604C18.092 9.9527 18.287 10.0271 18.4351 10.1637C19.1401 10.8171 19.7351 11.5801 20.1973 12.4233C20.7945 13.5254 21.1302 14.727 21.2476 16.0125Z"
      fill="#BED7EA"
    />
    <path
      d="M9.04254 28.0547H15.1499C15.3203 28.0547 15.4836 27.987 15.604 27.8664C15.7244 27.7458 15.7921 27.5823 15.7921 27.4118V20.3091C15.7921 20.1875 15.7578 20.0685 15.693 19.9657C15.6282 19.8629 15.5356 19.7806 15.426 19.7283C15.3164 19.6761 15.1942 19.656 15.0737 19.6704C14.9531 19.6849 14.8392 19.7332 14.7449 19.8099L8.63754 24.7782C8.56359 24.8385 8.50394 24.9145 8.46292 25.0007C8.42191 25.0869 8.40055 25.1811 8.40039 25.2766V27.4109C8.40027 27.4954 8.4168 27.5791 8.44901 27.6572C8.48123 27.7353 8.52851 27.8063 8.58815 27.8661C8.6478 27.9259 8.71863 27.9733 8.79659 28.0057C8.87456 28.0381 8.95814 28.0547 9.04254 28.0547V28.0547Z"
      fill="#F0F7FC"
    />
    <path
      d="M26.9464 28.1002H20.8614C20.6881 28.1001 20.5219 28.0311 20.3994 27.9083C20.2768 27.7856 20.208 27.6191 20.208 27.4456V20.3776C20.208 20.2539 20.243 20.1328 20.3089 20.0282C20.3749 19.9236 20.4691 19.8399 20.5806 19.7867C20.6922 19.7336 20.8165 19.7132 20.9391 19.7279C21.0618 19.7426 21.1778 19.7918 21.2736 19.8698L27.3599 24.8201C27.4355 24.8814 27.4964 24.9588 27.5382 25.0466C27.58 25.1345 27.6017 25.2306 27.6016 25.3279V27.4456C27.6016 27.5317 27.5846 27.6169 27.5517 27.6964C27.5187 27.7759 27.4704 27.8482 27.4096 27.909C27.3487 27.9698 27.2765 28.018 27.197 28.0508C27.1175 28.0836 27.0323 28.1004 26.9464 28.1002V28.1002Z"
      fill="#F0F7FC"
    />
    <path d="M21.2363 16.014H14.5547V28.1004H21.2363V16.014Z" fill="#E6F3FF" />
    <path d="M21.2363 16.014H17.8955V28.1004H21.2363V16.014Z" fill="#BED7EA" />
    <path
      d="M20.1956 12.4248H15.584C16.0456 11.5816 16.6402 10.8186 17.3448 10.1652C17.4946 10.0314 17.6884 9.95747 17.8891 9.95747C18.0898 9.95747 18.2836 10.0314 18.4334 10.1652C19.1383 10.8186 19.7334 11.5816 20.1956 12.4248V12.4248Z"
      fill="#5E5C7A"
    />
    <path
      d="M17.8887 9.95737C18.0901 9.95403 18.285 10.0284 18.4332 10.1651C19.1381 10.8185 19.7332 11.5815 20.1953 12.4247H17.8887"
      fill="#25254F"
    />
    <path
      d="M14.9506 28.1003H20.784V29.0915C20.784 29.1857 20.7466 29.276 20.6801 29.3426C20.6136 29.4092 20.5234 29.4466 20.4294 29.4466H15.3048C15.2107 29.4466 15.1206 29.4092 15.0541 29.3426C14.9876 29.276 14.9502 29.1857 14.9502 29.0915V28.1003H14.9506Z"
      fill="#5E5C7A"
    />
    <path
      d="M17.8672 28.1003H20.7836V29.0704C20.7836 29.1695 20.7443 29.2647 20.6742 29.3348C20.6042 29.4049 20.5092 29.4443 20.4101 29.4443H17.8672V28.1003Z"
      fill="#25254F"
    />
    <path
      d="M17.9142 14.7614C17.5671 14.7614 17.2278 14.8644 16.9391 15.0575C16.6505 15.2506 16.4256 15.525 16.2928 15.8461C16.1599 16.1672 16.1252 16.5205 16.1929 16.8614C16.2606 17.2022 16.4278 17.5153 16.6732 17.7611C16.9186 18.0068 17.2313 18.1742 17.5718 18.242C17.9122 18.3098 18.2651 18.275 18.5858 18.142C18.9064 18.009 19.1805 17.7838 19.3734 17.4948C19.5662 17.2058 19.6691 16.8661 19.6691 16.5185C19.6691 16.0525 19.4842 15.6056 19.1551 15.276C18.826 14.9465 18.3796 14.7614 17.9142 14.7614V14.7614ZM17.9281 17.6616C17.705 17.6616 17.4869 17.5954 17.3014 17.4713C17.1158 17.3471 16.9712 17.1707 16.8858 16.9643C16.8005 16.7579 16.7781 16.5308 16.8217 16.3117C16.8652 16.0926 16.9726 15.8913 17.1304 15.7333C17.2882 15.5754 17.4892 15.4678 17.708 15.4242C17.9269 15.3806 18.1537 15.403 18.3598 15.4885C18.566 15.574 18.7422 15.7188 18.8661 15.9045C18.9901 16.0903 19.0563 16.3087 19.0563 16.5321C19.0564 16.6805 19.0273 16.8274 18.9706 16.9645C18.914 17.1017 18.8309 17.2263 18.7261 17.3312C18.6213 17.4362 18.4969 17.5194 18.36 17.5762C18.2231 17.6329 18.0763 17.6621 17.9281 17.6621V17.6616Z"
      fill="#25254F"
    />
    <path
      d="M17.9142 15.403V14.7614C17.4487 14.7614 17.0023 14.9465 16.6732 15.276C16.3441 15.6056 16.1592 16.0525 16.1592 16.5185C16.1592 16.9846 16.3441 17.4315 16.6732 17.7611C17.0023 18.0906 17.4487 18.2757 17.9142 18.2757V17.6621C17.7642 17.6651 17.6151 17.6381 17.4756 17.5827C17.3361 17.5273 17.2091 17.4446 17.1019 17.3394C16.9948 17.2342 16.9097 17.1087 16.8515 16.9702C16.7934 16.8317 16.7635 16.683 16.7635 16.5327C16.7635 16.3825 16.7934 16.2338 16.8515 16.0953C16.9097 15.9568 16.9948 15.8313 17.1019 15.7261C17.2091 15.6209 17.3361 15.5382 17.4756 15.4828C17.6151 15.4274 17.7642 15.4004 17.9142 15.4034V15.403Z"
      fill="#5E5C7A"
    />
  </svg>
);

export const ChallengeColorIcon = ({ className = '' }: IconProps) => (
  <svg
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M17.953 27.391h-5.66s-.082-1.112 1.485-1.483c1.566-.372 2.752-.304 2.76-2.186.005-.893 0-2.184 0-2.184-.01-.436-.1-1.38-1.03-1.69-.914-.305-3.91-2.063-5.152-6.142-1.24-4.076-1.442-7.072-1.442-8.739 0 0-.083-.842 1.525-.842h7.514v23.266z"
      fill="#EF7A5C"
    />
    <path
      d="M10.94 15.217c-5.311-1.71-5.304-5.566-5.289-7.512 0-.151.124-.275.278-.275h3.118a25.228 25.228 0 0 1-.12-1.649.311.311 0 0 0-.112-.022H5.77c-.975 0-1.766.79-1.77 1.766-.009 3.037.745 8.579 8.073 9.562a10.318 10.318 0 0 1-1.134-1.87z"
      fill="#EF7A5C"
    />
    <path
      d="M17.952 27.391h5.659s.082-1.112-1.484-1.483c-1.566-.372-2.752-.304-2.761-2.186-.004-.893 0-2.184 0-2.184.011-.436.101-1.38 1.03-1.69.915-.305 3.91-2.063 5.152-6.142 1.242-4.076 1.443-7.072 1.443-8.739 0 0 .082-.842-1.525-.842h-7.514v23.266z"
      fill="#E9456B"
    />
    <path
      d="M24.965 15.217c5.311-1.71 5.304-5.566 5.289-7.512a.276.276 0 0 0-.278-.275h-3.119c.065-.619.102-1.17.12-1.649a.311.311 0 0 1 .113-.022h3.044c.975 0 1.766.79 1.77 1.766.008 3.037-.745 8.579-8.073 9.562.409-.535.798-1.156 1.134-1.87z"
      fill="#E9456B"
    />
    <path
      d="M17.952 8.186v5.472a.109.109 0 0 0-.05.013l-1.944 1.023a.11.11 0 0 1-.16-.115l.37-2.165a.111.111 0 0 0-.031-.098l-1.574-1.535a.11.11 0 0 1 .061-.187l2.173-.316a.112.112 0 0 0 .084-.06l.97-1.97a.117.117 0 0 1 .1-.062z"
      fill="#fff"
    />
    <path
      d="M19.769 12.316a.108.108 0 0 0-.031.098l.37 2.165c.016.09-.08.156-.16.115l-1.944-1.023a.111.111 0 0 0-.054-.013V8.186a.12.12 0 0 1 .101.062l.972 1.97a.112.112 0 0 0 .08.06l2.176.316a.11.11 0 0 1 .061.187l-1.571 1.535z"
      fill="#DAECFB"
    />
    <path
      d="M12.284 27.391v2.904h-.845a.832.832 0 0 0-.833.832v.006c0 .43.35.777.778.777h6.569v-4.519h-5.67z"
      fill="#E9456B"
    />
    <path
      d="M23.62 27.391v2.904h.846c.46 0 .832.372.832.832v.006c0 .43-.348.777-.777.777h-6.569v-4.519h5.669z"
      fill="#D3006C"
    />
    <path
      d="M17.952 27.391h-5.66s-.082-1.112 1.485-1.483c1.566-.372 1.752-.304 1.76-2.186.005-.893 0-2.184 0-2.184-.01-.436.9-1.38-.03-1.69-.914-.305-3.91-2.063-5.152-6.142-1.24-4.076-1.442-7.072-1.442-8.739 0 0-.083-.842 1.525-.842h7.514v23.266z"
      fill="#EF7A5C"
    />
    <path
      d="M10.939 15.217c-5.311-1.71-5.304-5.566-5.289-7.512 0-.151.124-.275.278-.275h3.118a25.228 25.228 0 0 1-.12-1.649.311.311 0 0 0-.112-.022H5.77c-.975 0-1.766.79-1.77 1.766-.009 3.037.745 8.579 8.073 9.562a10.392 10.392 0 0 1-1.134-1.87z"
      fill="#EF7A5C"
    />
    <path
      d="M17.951 27.391h5.659s.082-1.112-1.484-1.483c-1.566-.372-1.752-.304-1.761-2.186-.004-.893 0-2.184 0-2.184.011-.436-.899-1.38.03-1.69.915-.305 3.91-2.063 5.152-6.142C26.79 9.63 26.99 6.634 26.99 4.967c0 0 .082-.842-1.525-.842H17.95v23.266z"
      fill="#E9456B"
    />
    <path
      d="M24.964 15.217c5.311-1.71 5.304-5.566 5.289-7.512a.276.276 0 0 0-.278-.275h-3.119c.065-.619.102-1.17.12-1.649a.311.311 0 0 1 .113-.022h3.044c.975 0 1.766.79 1.77 1.766.008 3.037-.745 8.579-8.073 9.562.409-.535.798-1.156 1.134-1.87z"
      fill="#E9456B"
    />
    <path
      d="M17.95 8.186v5.472a.108.108 0 0 0-.05.013l-1.943 1.023a.11.11 0 0 1-.16-.115l.37-2.165a.112.112 0 0 0-.031-.098l-1.573-1.534a.11.11 0 0 1 .061-.187l2.173-.316a.112.112 0 0 0 .084-.059l.97-1.971a.113.113 0 0 1 .1-.063z"
      fill="#fff"
    />
    <path
      d="M19.768 12.316a.108.108 0 0 0-.031.098l.37 2.165c.016.09-.079.156-.16.115l-1.944-1.023a.112.112 0 0 0-.054-.013V8.186a.12.12 0 0 1 .101.062l.972 1.97a.112.112 0 0 0 .081.06l2.175.316a.11.11 0 0 1 .061.187l-1.571 1.535z"
      fill="#DAECFB"
    />
    <path
      d="M12.283 27.391v2.904h-.845a.832.832 0 0 0-.833.832v.006c0 .43.35.777.778.777h6.569v-4.519h-5.669z"
      fill="#E9456B"
    />
    <path
      d="M23.62 27.391v2.904h.846c.46 0 .832.372.832.832v.006c0 .43-.348.777-.777.777h-6.569v-4.519h5.669z"
      fill="#D3006C"
    />
  </svg>
);

export const ProgrammingIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M30.4048 9.76931V11.5985H18.001V7.34399H27.6774C29.184 7.34399 30.4048 8.42975 30.4048 9.76931Z"
      fill="#CCE7FE"
    />
    <path
      d="M18.0005 7.34399V11.5985H5.59668V9.76931C5.59668 8.42975 6.81744 7.34399 8.32404 7.34399H18.0005Z"
      fill="white"
    />
    <path
      d="M30.4048 11.5985V25.4027C30.4048 26.7422 29.184 27.828 27.6774 27.828H18.001V11.5985H30.4048Z"
      fill="#006DD1"
    />
    <path
      d="M18.0005 11.5985V27.828H8.32404C6.81744 27.828 5.59668 26.7422 5.59668 25.4027V11.5985H18.0005Z"
      fill="#2492F5"
    />
    <path
      d="M27.6769 7.34399H18.0005H8.32404C6.81744 7.34399 5.59668 8.42975 5.59668 9.76931V11.5985V25.4027C5.59668 26.7422 6.81744 27.828 8.32404 27.828H18.0005H27.6769C29.1835 27.828 30.4043 26.7422 30.4043 25.4027V11.5985V9.76931C30.4043 8.42975 29.1835 7.34399 27.6769 7.34399Z"
      stroke="#2492F5"
      strokeWidth="0.36"
      stroke-miterlimit="10"
    />
    <mask
      id="mask0_2222_91982"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="24"
    >
      <path
        d="M30.9562 5.39999V25.0888C30.9562 26.9996 29.6811 28.548 28.1079 28.548H18.002V5.39999H30.9562Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2222_91982)">
      <path
        d="M27.6779 7.34399H18.0015H8.32502C6.81842 7.34399 5.59766 8.42975 5.59766 9.76931V11.5985V25.4027C5.59766 26.7422 6.81842 27.828 8.32502 27.828H18.0015H27.6779C29.1845 27.828 30.4053 26.7422 30.4053 25.4027V11.5985V9.76931C30.4053 8.42975 29.1845 7.34399 27.6779 7.34399Z"
        stroke="#006DD1"
        strokeWidth="0.36"
        strokeMiterlimit="10"
      />
    </g>
    <path
      d="M10.8364 10.44C11.2837 10.44 11.6464 10.0774 11.6464 9.63001C11.6464 9.18266 11.2837 8.82001 10.8364 8.82001C10.389 8.82001 10.0264 9.18266 10.0264 9.63001C10.0264 10.0774 10.389 10.44 10.8364 10.44Z"
      fill="#2492F5"
    />
    <path
      d="M8.10687 10.44C8.55423 10.44 8.91687 10.0774 8.91687 9.63004C8.91687 9.18269 8.55423 8.82004 8.10687 8.82004C7.65952 8.82004 7.29688 9.18269 7.29688 9.63004C7.29688 10.0774 7.65952 10.44 8.10687 10.44Z"
      fill="#2492F5"
    />
    <path
      d="M13.5659 10.4401C14.0132 10.4401 14.3759 10.0774 14.3759 9.63007C14.3759 9.18272 14.0132 8.82007 13.5659 8.82007C13.1185 8.82007 12.7559 9.18272 12.7559 9.63007C12.7559 10.0774 13.1185 10.4401 13.5659 10.4401Z"
      fill="#2492F5"
    />
    <path
      d="M14.3352 22.3967L8.60563 19.5846C8.53628 19.5506 8.49121 19.4738 8.49121 19.3896V18.6811C8.49121 18.6005 8.53219 18.5265 8.59712 18.4903L14.3267 15.3058C14.4541 15.235 14.6041 15.3381 14.6041 15.4966V16.6592C14.6041 16.7424 14.5606 16.8178 14.4932 16.8528L10.7915 18.7618C10.6399 18.84 10.6446 19.082 10.7994 19.1525L14.4856 20.8374C14.5575 20.8703 14.6044 20.9482 14.6044 21.0348V22.2017C14.6044 22.3556 14.4619 22.4591 14.3352 22.3967Z"
      fill="white"
    />
    <path
      d="M20.7941 14.0638L17.1254 23.9055C17.0933 23.9911 17.0116 24.048 16.9202 24.048H15.4399C15.2872 24.048 15.1814 23.8957 15.2347 23.7525L18.9034 13.9108C18.9355 13.8251 19.0172 13.7682 19.1086 13.7682H20.5889C20.7419 13.7682 20.8478 13.9205 20.7941 14.0638Z"
      fill="white"
    />
    <path
      d="M21.5153 20.8375L25.2015 19.1526C25.3563 19.0817 25.361 18.8401 25.2094 18.7619L21.5078 16.8529C21.44 16.8178 21.3968 16.7424 21.3968 16.6593V15.4966C21.3968 15.3381 21.5468 15.235 21.6742 15.3058L27.4038 18.4904C27.4687 18.5265 27.5097 18.6005 27.5097 18.6812V19.3897C27.5097 19.4742 27.4649 19.5506 27.3953 19.5847L21.6657 22.3968C21.539 22.4589 21.3965 22.3558 21.3965 22.2018V21.0349C21.3962 20.9483 21.4431 20.8704 21.5153 20.8375Z"
      fill="white"
    />
  </svg>
);

export const Color3DIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <mask
      id="mask0_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="26"
    >
      <path d="M30.1305 5.63544H18.001V30.1475H30.1305V5.63544Z" fill="white" />
    </mask>
    <g mask="url(#mask0_2222_91870)">
      <path
        d="M30.1448 23.9969L18.0013 17.987L5.85742 23.9893"
        stroke="#07331F"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="2.52 2.52"
      />
    </g>
    <mask
      id="mask1_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="26"
    >
      <path d="M30.1305 5.63544H18.001V30.1475H30.1305V5.63544Z" fill="white" />
    </mask>
    <g mask="url(#mask1_2222_91870)">
      <path
        d="M18.001 5.93134V17.978"
        stroke="#07331F"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="2.52 2.52"
      />
    </g>
    <mask
      id="mask2_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="26"
    >
      <path d="M30.1305 5.63544H18.001V30.1475H30.1305V5.63544Z" fill="white" />
    </mask>
    <g mask="url(#mask2_2222_91870)">
      <path
        d="M24.0722 14.9296V20.9927L18.0005 24.0761L11.9287 20.9927V14.9296L18.0005 17.9957L24.0722 14.9296Z"
        fill="#60E275"
      />
      <path
        d="M24.0722 14.9296L18.0005 17.9957L11.9287 14.9296L18.0005 11.9326L24.0722 14.9296Z"
        fill="#60E275"
      />
    </g>
    <mask
      id="mask3_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="26"
    >
      <path d="M30.1305 5.63544H18.001V30.1475H30.1305V5.63544Z" fill="white" />
    </mask>
    <g mask="url(#mask3_2222_91870)">
      <path
        d="M24.0722 14.9296V20.9927L18.0005 24.0761L11.9287 20.9927V14.9296L18.0005 17.9957L24.0722 14.9296Z"
        stroke="#07331F"
        strokeWidth="0.36"
        stroke-miterlimit="10"
      />
      <path
        d="M24.0722 14.9296L18.0005 17.9957L11.9287 14.9296L18.0005 11.9326L24.0722 14.9296Z"
        stroke="#07331F"
        strokeWidth="0.36"
        stroke-miterlimit="10"
      />
    </g>
    <mask
      id="mask4_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="5"
      y="5"
      width="14"
      height="26"
    >
      <path
        d="M18.0013 5.64877H5.63672V30.3779H18.0013V5.64877Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask4_2222_91870)">
      <path
        d="M30.1448 23.9969L18.0013 17.9871L5.85742 23.9893"
        stroke="#2EBC6C"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-dasharray="2.52 2.52"
      />
    </g>
    <mask
      id="mask5_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="5"
      y="5"
      width="14"
      height="26"
    >
      <path
        d="M18.0013 5.64877H5.63672V30.3779H18.0013V5.64877Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask5_2222_91870)">
      <path
        d="M18.001 5.93137V17.978"
        stroke="#2EBC6C"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-dasharray="2.52 2.52"
      />
    </g>
    <mask
      id="mask6_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="5"
      y="5"
      width="14"
      height="26"
    >
      <path
        d="M18.0013 5.64877H5.63672V30.3779H18.0013V5.64877Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask6_2222_91870)">
      <path
        d="M24.0732 14.9296V20.9927L18.0014 24.0761L11.9297 20.9927V14.9296L18.0014 17.9957L24.0732 14.9296Z"
        fill="#B0F4B6"
      />
      <path
        d="M24.0732 14.9296L18.0014 17.9957L11.9297 14.9296L18.0014 11.9326L24.0732 14.9296Z"
        fill="#B0F4B6"
      />
    </g>
    <mask
      id="mask7_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="5"
      y="5"
      width="14"
      height="26"
    >
      <path
        d="M18.0013 5.64877H5.63672V30.3779H18.0013V5.64877Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask7_2222_91870)">
      <path
        d="M24.0732 14.9296V20.9927L18.0014 24.0761L11.9297 20.9927V14.9296L18.0014 17.9957L24.0732 14.9296Z"
        stroke="#2EBC6C"
        strokeWidth="0.468"
        stroke-miterlimit="10"
      />
      <path
        d="M24.0732 14.9296L18.0014 17.9957L11.9297 14.9296L18.0014 11.9326L24.0732 14.9296Z"
        stroke="#2EBC6C"
        strokeWidth="0.468"
        stroke-miterlimit="10"
      />
    </g>
    <mask
      id="mask8_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="5"
      width="13"
      height="26"
    >
      <path d="M30.601 5.38199H18.001V30.6183H30.601V5.38199Z" fill="white" />
    </mask>
    <g mask="url(#mask8_2222_91870)">
      <path
        d="M18 17.9524V30.0383"
        stroke="#07331F"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linejoin="round"
      />
      <path
        d="M30.1438 11.8544V20.3083V23.981L18.0003 30.1481L5.85645 23.981V11.8544L18.0003 17.987L30.1438 11.8544Z"
        stroke="#07331F"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linejoin="round"
      />
      <path
        d="M5.85645 11.8544L18.0003 17.987L30.1438 11.8544L18.0003 5.86075L5.85645 11.8544Z"
        stroke="#07331F"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linejoin="round"
      />
    </g>
    <mask
      id="mask9_2222_91870"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="5"
      y="5"
      width="14"
      height="26"
    >
      <path
        d="M18.0014 5.38196H5.40137V30.6183H18.0014V5.38196Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask9_2222_91870)">
      <path
        d="M18.001 17.9525V30.0384"
        stroke="#2EBC6C"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M30.1448 11.8544V20.3083V23.981L18.0013 30.1482L5.85742 23.981V11.8544L18.0013 17.987L30.1448 11.8544Z"
        stroke="#2EBC6C"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.85742 11.8544L18.0013 17.987L30.1448 11.8544L18.0013 5.86078L5.85742 11.8544Z"
        stroke="#2EBC6C"
        strokeWidth="0.9"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
  </svg>
);

export const AnimationIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M30.241 10.2599H18.001V25.5239H30.241V10.2599Z" fill="#FDE1FF" />
    <path
      d="M5.72498 25.524H18.001L18.001 10.26L5.72498 10.26L5.72498 25.524Z"
      fill="white"
    />
    <path
      d="M5.85039 6.29993C5.60199 6.29993 5.40039 6.50153 5.40039 6.74993C5.40039 6.87413 5.45079 6.98681 5.53215 7.06817C5.61351 7.14953 5.72619 7.19993 5.85039 7.19993H6.82239C7.07079 7.19993 7.27239 7.40153 7.27239 7.64993V9.44993C7.27239 9.69833 7.07079 9.89993 6.82239 9.89993H5.85039C5.60199 9.89993 5.40039 10.1015 5.40039 10.3499C5.40039 10.4741 5.45079 10.5868 5.53215 10.6682C5.61351 10.7495 5.72619 10.7999 5.85039 10.7999H18.0004V6.29993H5.85039ZM15.5661 9.44993C15.5661 9.69833 15.3645 9.89993 15.1161 9.89993H12.2717C12.0233 9.89993 11.8221 9.69833 11.8221 9.44993V7.64993C11.8221 7.40153 12.0233 7.19993 12.2717 7.19993H15.1161C15.3645 7.19993 15.5661 7.40153 15.5661 7.64993V9.44993Z"
      fill="#7B0382"
    />
    <path
      d="M30.151 9.89993H29.179C28.9306 9.89993 28.729 9.69833 28.729 9.44993V7.64993C28.729 7.40153 28.9306 7.19993 29.179 7.19993H30.151C30.3994 7.19993 30.601 6.99833 30.601 6.74993C30.601 6.62573 30.5506 6.51305 30.4692 6.43169C30.3879 6.35033 30.2752 6.29993 30.151 6.29993H18.001V10.7999H30.151C30.3994 10.7999 30.601 10.5983 30.601 10.3499C30.601 10.2257 30.5506 10.113 30.4692 10.0317C30.3879 9.95033 30.2752 9.89993 30.151 9.89993ZM24.1793 9.44993C24.1793 9.69833 23.9781 9.89993 23.7297 9.89993H20.8853C20.6369 9.89993 20.4353 9.69833 20.4353 9.44993V7.64993C20.4353 7.40153 20.6369 7.19993 20.8853 7.19993H23.7297C23.9781 7.19993 24.1793 7.40153 24.1793 7.64993V9.44993Z"
      fill="#410156"
    />
    <path
      d="M5.85039 25.2C5.60199 25.2 5.40039 25.4016 5.40039 25.65C5.40039 25.7742 5.45079 25.8868 5.53215 25.9682C5.61351 26.0496 5.72619 26.1 5.85039 26.1H6.82239C7.07079 26.1 7.27239 26.3016 7.27239 26.55V28.35C7.27239 28.5984 7.07079 28.8 6.82239 28.8H5.85039C5.60199 28.8 5.40039 29.0016 5.40039 29.25C5.40039 29.3742 5.45079 29.4868 5.53215 29.5682C5.61351 29.6496 5.72619 29.7 5.85039 29.7H18.0004V25.2H5.85039ZM15.5661 28.35C15.5661 28.5984 15.3645 28.8 15.1161 28.8H12.2717C12.0233 28.8 11.8221 28.5984 11.8221 28.35V26.55C11.8221 26.3016 12.0233 26.1 12.2717 26.1H15.1161C15.3645 26.1 15.5661 26.3016 15.5661 26.55V28.35Z"
      fill="#7B0382"
    />
    <path
      d="M30.151 28.8H29.179C28.9306 28.8 28.729 28.5984 28.729 28.35V26.55C28.729 26.3016 28.9306 26.1 29.179 26.1H30.151C30.3994 26.1 30.601 25.8984 30.601 25.65C30.601 25.5258 30.5506 25.4131 30.4692 25.3317C30.3879 25.2504 30.2752 25.2 30.151 25.2H18.001V29.7H30.151C30.3994 29.7 30.601 29.4984 30.601 29.25C30.601 29.1258 30.5506 29.0131 30.4692 28.9317C30.3879 28.8504 30.2752 28.8 30.151 28.8ZM24.1793 28.35C24.1793 28.5984 23.9781 28.8 23.7297 28.8H20.8853C20.6369 28.8 20.4353 28.5984 20.4353 28.35V26.55C20.4353 26.3016 20.6369 26.1 20.8853 26.1H23.7297C23.9781 26.1 24.1793 26.3016 24.1793 26.55V28.35Z"
      fill="#410156"
    />
    <path
      d="M25.1486 17.2238C26.6044 17.2238 27.7845 16.0436 27.7845 14.5879C27.7845 13.1321 26.6044 11.9519 25.1486 11.9519C23.6928 11.9519 22.5127 13.1321 22.5127 14.5879C22.5127 16.0436 23.6928 17.2238 25.1486 17.2238Z"
      fill="#D600E2"
    />
    <path
      d="M25.64 14.8499C25.64 14.8499 18.3666 16.3594 18.3666 22.1219C18.3666 22.1219 17.8104 16.7939 12.248 17.4779"
      stroke="#D600E2"
      strokeWidth="1.188"
      stroke-miterlimit="10"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <path
      d="M10.2691 18.9719C10.627 18.9719 10.9171 18.6818 10.9171 18.3239C10.9171 17.9661 10.627 17.6759 10.2691 17.6759C9.91121 17.6759 9.62109 17.9661 9.62109 18.3239C9.62109 18.6818 9.91121 18.9719 10.2691 18.9719Z"
      fill="#D600E2"
    />
    <path
      d="M8.6848 19.908C8.94327 19.908 9.1528 19.6984 9.1528 19.44C9.1528 19.1815 8.94327 18.972 8.6848 18.972C8.42633 18.972 8.2168 19.1815 8.2168 19.44C8.2168 19.6984 8.42633 19.908 8.6848 19.908Z"
      fill="#D600E2"
    />
  </svg>
);

export const AppsIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.9997 6.66V29.34H13.1984C12.5198 29.34 11.9697 28.7899 11.9697 28.1113V7.88868C11.9697 7.21008 12.5198 6.66 13.1984 6.66H17.9997Z"
      fill="white"
    />
    <path
      d="M24.03 7.88868V28.1113C24.03 28.7899 23.4799 29.34 22.8013 29.34H18V6.66H22.8013C23.4799 6.66 24.03 7.21008 24.03 7.88868Z"
      fill="#D9ECFD"
    />
    <path
      d="M13.1985 6.65999H14.672C14.9042 6.65999 15.1119 6.80507 15.1914 7.02359L15.356 7.47431C15.469 7.78535 15.7649 7.99199 16.0958 7.99199H17.9998V5.39999H12.5735C11.594 5.39999 10.7998 6.19415 10.7998 7.17371V28.8263C10.7998 29.8058 11.594 30.6 12.5735 30.6H17.9998V29.34H13.1985C12.5199 29.34 11.9698 28.7899 11.9698 28.1113V7.88867C11.9698 7.21007 12.5199 6.65999 13.1985 6.65999Z"
      fill="#FD426A"
    />
    <path
      d="M25.2 7.17371V28.8263C25.2 29.8058 24.4058 30.6 23.4263 30.6H18V29.34H22.8013C23.4799 29.34 24.03 28.7899 24.03 28.1113V7.88867C24.03 7.21007 23.4799 6.65999 22.8013 6.65999H21.3188C21.092 6.65999 20.8894 6.80183 20.8116 7.01495L20.6438 7.47431C20.5308 7.78535 20.2349 7.99199 19.904 7.99199H18V5.39999H23.4263C24.4058 5.39999 25.2 6.19415 25.2 7.17371Z"
      fill="#D3006C"
    />
    <path
      d="M18.846 27.018C18.846 27.4853 18.4673 27.864 18 27.864V26.172C18.4673 26.172 18.846 26.5507 18.846 27.018Z"
      fill="#D3006C"
    />
    <path
      d="M18.0003 26.172V27.864C17.533 27.864 17.1543 27.4853 17.1543 27.018C17.1543 26.5507 17.533 26.172 18.0003 26.172Z"
      fill="#FD426A"
    />
    <path
      d="M16.5143 14.9041H14.1513C13.7502 14.9041 13.4248 14.579 13.4248 14.1776V11.8146C13.4248 11.4135 13.7499 11.0881 14.1513 11.0881H16.5143C16.9154 11.0881 17.2408 11.4132 17.2408 11.8146V14.1776C17.2408 14.579 16.9154 14.9041 16.5143 14.9041Z"
      fill="#FD7C59"
    />
    <path
      d="M21.8493 14.904H19.4862C19.0852 14.904 18.7598 14.5789 18.7598 14.1775V11.8145C18.7598 11.4134 19.0848 11.088 19.4862 11.088H21.8493C22.2503 11.088 22.5758 11.4131 22.5758 11.8145V14.1775C22.5758 14.5789 22.2507 14.904 21.8493 14.904Z"
      fill="#FD426A"
    />
    <path
      d="M16.5143 20.268H14.1513C13.7502 20.268 13.4248 19.9429 13.4248 19.5415V17.1785C13.4248 16.7774 13.7499 16.452 14.1513 16.452H16.5143C16.9154 16.452 17.2408 16.7771 17.2408 17.1785V19.5415C17.2408 19.9429 16.9154 20.268 16.5143 20.268Z"
      fill="#FD426A"
    />
    <path
      d="M21.8493 20.268H19.4862C19.0852 20.268 18.7598 19.9429 18.7598 19.5415V17.1785C18.7598 16.7775 19.0848 16.452 19.4862 16.452H21.8493C22.2503 16.452 22.5758 16.7771 22.5758 17.1785V19.5415C22.5758 19.9429 22.2507 20.268 21.8493 20.268Z"
      fill="#D3006C"
    />
  </svg>
);

export const GamesIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M27 8.12735V27.8726C27 29.3789 25.7789 30.6 24.2726 30.6H18V5.39999H24.2726C25.7789 5.39999 27 6.62111 27 8.12735Z"
      fill="#6852E8"
    />
    <path
      d="M18 5.39999V30.6H11.7274C10.2211 30.6 9 29.3789 9 27.8726V8.12735C9 6.62111 10.2211 5.39999 11.7274 5.39999H18Z"
      fill="#8D78FF"
    />
    <path
      d="M25.2099 9.35785V14.9386C25.2099 15.7651 24.5403 16.4347 23.7137 16.4347H18.0088V7.86169H23.7141C24.5403 7.86169 25.2099 8.53165 25.2099 9.35785Z"
      fill="#240475"
    />
    <path
      d="M18.0091 7.86169V16.4347H12.3038C11.4772 16.4347 10.8076 15.7651 10.8076 14.9386V9.35785C10.8076 8.53129 11.4772 7.86169 12.3038 7.86169H18.0091Z"
      fill="#4F2A9E"
    />
    <path
      d="M23.714 7.86166H18.0091H12.3038C11.4772 7.86166 10.8076 8.53126 10.8076 9.35782V14.9385C10.8076 15.7651 11.4772 16.4347 12.3038 16.4347H18.0091H23.7143C24.5409 16.4347 25.2105 15.7651 25.2105 14.9385V9.35782C25.2101 8.53162 24.5405 7.86166 23.714 7.86166Z"
      stroke="#240475"
      strokeWidth="0.72"
      stroke-miterlimit="10"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
    <mask
      id="mask0_2222_91959"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="18"
      y="7"
      width="8"
      height="10"
    >
      <path d="M25.5549 7.41605H18.0107V16.812H25.5549V7.41605Z" fill="white" />
    </mask>
    <g mask="url(#mask0_2222_91959)">
      <path
        d="M23.715 7.86172H18.01H12.3048C11.4782 7.86172 10.8086 8.53132 10.8086 9.35788V14.9386C10.8086 15.7652 11.4782 16.4348 12.3048 16.4348H18.01H23.7153C24.5419 16.4348 25.2115 15.7652 25.2115 14.9386V9.35788C25.2111 8.53168 24.5415 7.86172 23.715 7.86172Z"
        stroke="#170A44"
        strokeWidth="0.72"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </g>
    <path
      d="M13.3916 20.412V25.092"
      stroke="#F9B635"
      strokeWidth="1.44"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M15.7318 22.752H11.0518"
      stroke="#F9B635"
      strokeWidth="1.44"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M20.7533 26.46C21.4989 26.46 22.1033 25.8556 22.1033 25.11C22.1033 24.3644 21.4989 23.76 20.7533 23.76C20.0077 23.76 19.4033 24.3644 19.4033 25.11C19.4033 25.8556 20.0077 26.46 20.7533 26.46Z"
      fill="#FC8722"
    />
    <path
      d="M23.5971 23.112C24.3427 23.112 24.9471 22.5076 24.9471 21.762C24.9471 21.0164 24.3427 20.412 23.5971 20.412C22.8515 20.412 22.2471 21.0164 22.2471 21.762C22.2471 22.5076 22.8515 23.112 23.5971 23.112Z"
      fill="#FC8722"
    />
  </svg>
);

export const RoboticsIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.0136 18.9194C12.0136 18.9194 8.11299 18.7301 7.56543 22.5698"
      stroke="#742CE5"
      strokeWidth="0.9"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M6.35742 23.4367C6.49098 22.8903 7.00542 22.522 7.55478 22.5699C8.12394 22.6192 8.57574 23.1016 8.5869 23.6844"
      stroke="#742CE5"
      strokeWidth="0.9"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M23.9863 18.9194C23.9863 18.9194 27.8869 18.7301 28.4345 22.5698"
      stroke="#4E0DCC"
      strokeWidth="0.9"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M29.6426 23.4367C29.509 22.8903 28.9946 22.522 28.4452 22.5699C27.876 22.6192 27.4242 23.1016 27.4131 23.6844"
      stroke="#4E0DCC"
      strokeWidth="0.9"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M25.3901 19.3071V26.46H18V17.244H23.6153C24.5956 17.244 25.3901 18.1677 25.3901 19.3071Z"
      fill="#FFA143"
    />
    <path
      d="M17.9995 17.244V26.46H10.6094V19.3071C10.6094 18.1677 11.4039 17.244 12.3842 17.244H17.9995Z"
      fill="#FFC152"
    />
    <path
      d="M10.8985 25.992C9.83758 25.992 8.97754 26.8524 8.97754 27.913V28.6787C8.97754 29.7396 9.83758 30.5996 10.8985 30.5996H18.0006V25.992H10.8985Z"
      fill="#A38EFF"
    />
    <path
      d="M27.024 27.913V28.6787C27.024 29.7396 26.164 30.5996 25.1031 30.5996H18.001V29.8984H25.0217C25.7892 29.8984 26.4109 29.2763 26.4109 28.5088V28.0818C26.4109 27.3143 25.7892 26.6926 25.0217 26.6926H18.001V25.992H25.1031C26.164 25.992 27.024 26.8524 27.024 27.913Z"
      fill="#826DFF"
    />
    <path
      d="M26.4106 28.0825V28.5095C26.4106 29.277 25.7889 29.8991 25.0213 29.8991H18.001V26.6937H25.0217C25.7889 26.6933 26.4106 27.3154 26.4106 28.0825Z"
      fill="#4E0DCC"
    />
    <path
      d="M18.0008 26.6933V29.8987H10.9801C10.2125 29.8987 9.59082 29.2767 9.59082 28.5091V28.0822C9.59082 27.3147 10.2125 26.6929 10.9801 26.6929H18.0008V26.6933Z"
      fill="#742CE5"
    />
    <path
      d="M19.1623 28.2928C19.1623 28.9339 18.6425 29.4541 18.001 29.4541V27.1314C18.6425 27.1314 19.1623 27.6512 19.1623 28.2928Z"
      fill="#826DFF"
    />
    <path
      d="M18.0012 27.1314V29.4541C17.3597 29.4541 16.8398 28.9339 16.8398 28.2928C16.8398 27.6512 17.3597 27.1314 18.0012 27.1314Z"
      fill="#A38EFF"
    />
    <path
      d="M23.2698 29.4541C23.9112 29.4541 24.4311 28.9342 24.4311 28.2928C24.4311 27.6514 23.9112 27.1314 23.2698 27.1314C22.6284 27.1314 22.1084 27.6514 22.1084 28.2928C22.1084 28.9342 22.6284 29.4541 23.2698 29.4541Z"
      fill="#826DFF"
    />
    <path
      d="M12.7317 29.4718C13.3731 29.4718 13.893 28.9518 13.893 28.3104C13.893 27.669 13.3731 27.149 12.7317 27.149C12.0903 27.149 11.5703 27.669 11.5703 28.3104C11.5703 28.9518 12.0903 29.4718 12.7317 29.4718Z"
      fill="#A38EFF"
    />
    <path
      d="M20.8555 20.2406V22.2221C20.8555 22.7135 20.457 23.112 19.9656 23.112H18V22.7639H19.867C20.2205 22.7639 20.507 22.4773 20.507 22.1238V20.3396C20.507 19.9861 20.2205 19.6996 19.867 19.6996H18V19.3514H19.9656C20.457 19.3511 20.8555 19.7496 20.8555 20.2406Z"
      fill="#E87528"
    />
    <path
      d="M20.5074 20.3396V22.1238C20.5074 22.4773 20.2208 22.7639 19.8673 22.7639H18V19.6992H19.867C20.2205 19.6992 20.5074 19.9861 20.5074 20.3396Z"
      fill="#4E0DCC"
    />
    <path
      d="M15.4927 20.3396V22.1238C15.4927 22.4773 15.7792 22.7639 16.1327 22.7639H18.0001V23.112H16.0345C15.5431 23.112 15.1445 22.7135 15.1445 22.2221V20.2406C15.1445 19.7492 15.5431 19.3511 16.0345 19.3511H18.0001V19.6992H16.1331C15.7796 19.6992 15.4927 19.9861 15.4927 20.3396Z"
      fill="#FFA143"
    />
    <path
      d="M18.0002 19.6992V22.7635H16.1332C15.7797 22.7635 15.4932 22.4769 15.4932 22.1234V20.3393C15.4932 19.9857 15.7797 19.6992 16.1332 19.6992H18.0002Z"
      fill="#742CE5"
    />
    <path d="M18.1508 6.91092H18V8.17992H18.1508V6.91092Z" fill="#4E0DCC" />
    <path
      d="M18.0004 6.91092H17.8496V8.17992H18.0004V6.91092Z"
      fill="#742CE5"
    />
    <path
      d="M18.9972 6.39719C18.9972 6.94799 18.5508 7.39439 18 7.39439V5.39999C18.5508 5.39999 18.9972 5.84639 18.9972 6.39719Z"
      fill="#4E0DCC"
    />
    <path
      d="M18.0001 5.39999V7.39439C17.4493 7.39439 17.0029 6.94799 17.0029 6.39719C17.0029 5.84639 17.4493 5.39999 18.0001 5.39999Z"
      fill="#742CE5"
    />
    <path
      d="M10.9035 14.4839C11.9688 14.4839 12.8324 13.6203 12.8324 12.555C12.8324 11.4897 11.9688 10.6261 10.9035 10.6261C9.8382 10.6261 8.97461 11.4897 8.97461 12.555C8.97461 13.6203 9.8382 14.4839 10.9035 14.4839Z"
      fill="#FFA143"
    />
    <path
      d="M25.0968 14.4839C26.1621 14.4839 27.0257 13.6203 27.0257 12.555C27.0257 11.4897 26.1621 10.6261 25.0968 10.6261C24.0316 10.6261 23.168 11.4897 23.168 12.555C23.168 13.6203 24.0316 14.4839 25.0968 14.4839Z"
      fill="#E87528"
    />
    <path
      d="M25.1453 10.6596V14.4295C25.1453 15.8649 23.9821 17.028 22.5468 17.028H18V8.06149H22.5468C23.9821 8.06149 25.1453 9.22501 25.1453 10.6596Z"
      fill="#FFA143"
    />
    <path
      d="M18.0002 8.06149V17.028H13.4998C12.0649 17.028 10.9014 15.8648 10.9014 14.4295V10.6596C10.9014 9.22465 12.0649 8.06113 13.4998 8.06113H18.0002V8.06149Z"
      fill="#FFC152"
    />
    <path
      d="M14.9266 13.4104C15.6299 13.4104 16.2 12.8403 16.2 12.1371C16.2 11.4339 15.6299 10.8638 14.9266 10.8638C14.2234 10.8638 13.6533 11.4339 13.6533 12.1371C13.6533 12.8403 14.2234 13.4104 14.9266 13.4104Z"
      fill="#742CE5"
    />
    <path
      d="M22.2969 11.2753L20.5732 12.9993"
      stroke="#4E0DCC"
      strokeWidth="0.54"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M20.5664 11.2519L22.3437 13.0292"
      stroke="#4E0DCC"
      strokeWidth="0.54"
      stroke-miterlimit="10"
      stroke-linecap="round"
    />
    <path
      d="M20.6094 15.2831V15.9498C20.6094 16.0553 20.5237 16.141 20.4182 16.141H17.999V14.6833H19.8764C20.2811 14.6837 20.6094 14.9519 20.6094 15.2831Z"
      fill="#4E0DCC"
    />
    <path
      d="M17.999 14.6837V16.1414H15.5798C15.4744 16.1414 15.3887 16.0557 15.3887 15.9502V15.2835C15.3887 14.9523 15.717 14.6841 16.1216 14.6841H17.999V14.6837Z"
      fill="#742CE5"
    />
  </svg>
);

export const Coin = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="23"
    height="23"
    viewBox="0 0 23 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M11.3403 22.3271C17.1393 22.3271 21.8403 17.6261 21.8403 11.8271C21.8403 6.02816 17.1393 1.32715 11.3403 1.32715C5.54132 1.32715 0.840332 6.02816 0.840332 11.8271C0.840332 17.6261 5.54132 22.3271 11.3403 22.3271Z"
      fill="#FFDA00"
      stroke="white"
    />
    <path
      d="M11.3404 19.9456C15.8238 19.9456 19.4582 16.3111 19.4582 11.8278C19.4582 7.34443 15.8238 3.70996 11.3404 3.70996C6.85711 3.70996 3.22266 7.34443 3.22266 11.8278C3.22266 16.3111 6.85711 19.9456 11.3404 19.9456Z"
      fill="#F4A800"
    />
    <path
      d="M11.3404 19.9456C15.8238 19.9456 19.4582 16.3111 19.4582 11.8278C19.4582 7.34443 15.8238 3.70996 11.3404 3.70996C6.85711 3.70996 3.22266 7.34443 3.22266 11.8278C3.22266 16.3111 6.85711 19.9456 11.3404 19.9456Z"
      stroke="#F29400"
      strokeWidth="0.35293"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.6588 15.5651C10.9428 15.5651 10.349 15.4166 9.87723 15.1197C9.40549 14.8227 9.05348 14.4013 8.81881 13.8577C8.58413 13.3117 8.46919 12.6652 8.46919 11.9157C8.46919 11.3529 8.54342 10.8477 8.68949 10.3975C8.83557 9.94967 9.04629 9.56413 9.32407 9.24565C9.59945 8.92716 9.9371 8.68291 10.3346 8.51528C10.7321 8.34766 11.1823 8.26385 11.6852 8.26385C12.0994 8.26385 12.4922 8.31653 12.8633 8.42189C13.1914 8.51528 13.5027 8.62304 13.7948 8.74277C13.8595 8.76911 13.9313 8.74038 13.9601 8.67812L14.3576 7.82563C14.3887 7.75858 14.3576 7.67956 14.2905 7.65082C13.9337 7.49996 13.5458 7.37783 13.1291 7.28923C12.746 7.20781 12.3533 7.15992 11.9534 7.14555C11.8839 7.14316 11.8289 7.08569 11.8289 7.01624V5.80216C11.8289 5.73032 11.7714 5.67285 11.6995 5.67285H10.8949C10.8231 5.67285 10.7656 5.73032 10.7656 5.80216V7.09526C10.7656 7.15752 10.7201 7.2126 10.6579 7.22218C10.3178 7.27965 10.0017 7.36825 9.70721 7.48559C9.13011 7.71547 8.64639 8.04354 8.24888 8.46978C7.85377 8.89603 7.55205 9.3989 7.3485 9.9808C7.14496 10.5627 7.04199 11.2045 7.04199 11.9061C7.04199 12.8759 7.20483 13.7164 7.5281 14.43C7.85377 15.1436 8.34467 15.6968 9.00559 16.0895C9.47014 16.3673 10.0185 16.5469 10.6507 16.6283C10.7153 16.6355 10.7632 16.6906 10.7632 16.7576V17.8472C10.7632 17.919 10.8207 17.9765 10.8926 17.9765H11.6971C11.769 17.9765 11.8265 17.919 11.8265 17.8472V16.8007C11.8265 16.7313 11.8815 16.6738 11.951 16.6714C12.2958 16.657 12.6095 16.6283 12.8873 16.5828C13.2537 16.5229 13.6128 16.4319 13.9577 16.3122C14.0104 16.2931 14.0439 16.2452 14.0439 16.1901V15.3376C14.0439 15.2514 13.9601 15.1891 13.8786 15.2131C13.5458 15.3089 13.2129 15.3903 12.8801 15.4525C12.4922 15.5292 12.0851 15.5651 11.6588 15.5651Z"
      fill="#FFDA00"
      stroke="#FFDA00"
      strokeWidth="0.705858"
      strokeMiterlimit="10"
    />
    <mask
      id="mask0_2303_92565"
      maskUnits="userSpaceOnUse"
      x="11"
      y="1"
      width="11"
      height="22"
    >
      <path
        d="M11.3408 21.9243V1.82617H21.3408V22.0608L11.3408 21.9243Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2303_92565)">
      <path
        d="M11.3413 21.8262C16.8641 21.8262 21.3412 17.349 21.3412 11.8262C21.3412 6.30332 16.8641 1.82617 11.3413 1.82617C5.81844 1.82617 1.34131 6.30332 1.34131 11.8262C1.34131 17.349 5.81844 21.8262 11.3413 21.8262Z"
        fill="#FEBF17"
      />
      <path
        d="M11.3409 19.9446C15.8243 19.9446 19.4587 16.3101 19.4587 11.8268C19.4587 7.34345 15.8243 3.70898 11.3409 3.70898C6.8576 3.70898 3.22314 7.34345 3.22314 11.8268C3.22314 16.3101 6.8576 19.9446 11.3409 19.9446Z"
        fill="#F18C10"
      />
      <path
        d="M11.3409 19.9446C15.8243 19.9446 19.4587 16.3101 19.4587 11.8268C19.4587 7.34345 15.8243 3.70898 11.3409 3.70898C6.8576 3.70898 3.22314 7.34345 3.22314 11.8268C3.22314 16.3101 6.8576 19.9446 11.3409 19.9446Z"
        stroke="#EE7410"
        strokeWidth="0.35293"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.6598 15.5651C10.9438 15.5651 10.3499 15.4166 9.87821 15.1197C9.40646 14.8227 9.05445 14.4013 8.81978 13.8577C8.58511 13.3117 8.47017 12.6652 8.47017 11.9157C8.47017 11.3529 8.5444 10.8477 8.69047 10.3975C8.83654 9.94967 9.04727 9.56413 9.32505 9.24565C9.60043 8.92716 9.93807 8.68291 10.3356 8.51528C10.7331 8.34766 11.1833 8.26385 11.6861 8.26385C12.1004 8.26385 12.4931 8.31653 12.8643 8.42189C13.1924 8.51528 13.5037 8.62304 13.7958 8.74277C13.8605 8.76911 13.9323 8.74038 13.961 8.67812L14.3586 7.82563C14.3897 7.75858 14.3586 7.67956 14.2915 7.65082C13.9347 7.49996 13.5468 7.37783 13.1301 7.28923C12.747 7.20781 12.3542 7.15992 11.9543 7.14555C11.8849 7.14316 11.8298 7.08569 11.8298 7.01624V5.80216C11.8298 5.73032 11.7724 5.67285 11.7005 5.67285H10.8959C10.8241 5.67285 10.7666 5.73032 10.7666 5.80216V7.09526C10.7666 7.15752 10.7211 7.2126 10.6589 7.22218C10.3188 7.27965 10.0027 7.36825 9.70819 7.48559C9.13108 7.71547 8.64737 8.04354 8.24986 8.46978C7.85475 8.89603 7.55302 9.3989 7.34948 9.9808C7.14594 10.5627 7.04297 11.2045 7.04297 11.9061C7.04297 12.8759 7.2058 13.7164 7.52908 14.43C7.85475 15.1436 8.34565 15.6968 9.00656 16.0895C9.47112 16.3673 10.0195 16.5469 10.6517 16.6283C10.7163 16.6355 10.7642 16.6906 10.7642 16.7576V17.8472C10.7642 17.919 10.8217 17.9765 10.8935 17.9765H11.6981C11.77 17.9765 11.8274 17.919 11.8274 17.8472V16.8007C11.8274 16.7313 11.8825 16.6738 11.952 16.6714C12.2968 16.657 12.6105 16.6283 12.8883 16.5828C13.2546 16.5229 13.6138 16.4319 13.9586 16.3122C14.0113 16.2931 14.0449 16.2452 14.0449 16.1901V15.3376C14.0449 15.2514 13.961 15.1891 13.8796 15.2131C13.5468 15.3089 13.2139 15.3903 12.8811 15.4525C12.4931 15.5292 12.0861 15.5651 11.6598 15.5651Z"
        fill="#FEBF17"
        stroke="#FEBF17"
        strokeWidth="0.705858"
        strokeMiterlimit="10"
      />
    </g>
  </svg>
);

export const Gem = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="20"
    height="23"
    viewBox="0 0 20 23"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10.2503 1.1109L10.0003 0.966639L9.75038 1.1109L1.08746 6.1109L0.837402 6.25523V6.54395V16.5439V16.8327L1.08746 16.977L9.75038 21.977L10.0003 22.1213L10.2503 21.977L18.9132 16.977L19.1632 16.8327V16.5439V6.54395V6.25523L18.9132 6.1109L10.2503 1.1109Z"
      fill="#077775"
      stroke="white"
    />
    <path
      d="M10.0003 1.95117L1.69092 6.74652V16.3419L10.0003 21.1372L18.3098 16.3419V6.74652L10.0003 1.95117Z"
      fill="#2CD3BE"
    />
    <path
      d="M10.0003 11.5447L1.69092 16.3424V6.74707L10.0003 11.5447Z"
      fill="#2CD3BE"
    />
    <path
      d="M10.0003 11.5439V21.137L1.69092 16.3416L10.0003 11.5439Z"
      fill="#00B5A7"
    />
    <path
      d="M18.3099 16.3416L10.0005 21.137V11.5439L18.3099 16.3416Z"
      fill="#077775"
    />
    <path
      d="M18.3099 6.74707V16.3424L10.0005 11.5447L18.3099 6.74707Z"
      fill="#00B29A"
    />
    <path
      d="M18.3099 6.74652L10.0005 11.5442V1.95117L18.3099 6.74652Z"
      fill="#0CD0AF"
    />
    <path
      d="M10.0003 1.95117V11.5442L1.69092 6.74652L10.0003 1.95117Z"
      fill="#4BEFCF"
    />
    <path
      d="M10 5.41895V17.6701L7.85582 15.2585L4.69531 14.6073L5.71161 11.5445L4.69531 8.48174L7.85582 7.83057L10 5.41895Z"
      fill="#9FFCE3"
    />
    <path
      d="M14.2884 11.5445L15.3047 14.6073L12.1442 15.2585L10 17.6701V5.41895L12.1442 7.83057L15.3047 8.48174L14.2884 11.5445Z"
      fill="#38F4D0"
    />
  </svg>
);
export const FeedbackColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M21.4886 8.7742H4.94264C3.31741 8.7742 2 10.0904 2 11.714V22.8935C2 24.5172 3.31741 25.8334 4.94264 25.8334H9.22834V30.3429C9.22834 30.7587 9.70476 30.9952 10.0365 30.7437L16.5138 25.8334H21.4886C23.1139 25.8334 24.4313 24.5172 24.4313 22.8935V11.714C24.4313 10.0904 23.1139 8.7742 21.4886 8.7742Z"
      fill="#EC8362"
    />
    <path
      d="M20.9667 16.3315H5.36255C4.8824 16.3315 4.49316 16.7204 4.49316 17.2001C4.49316 17.6798 4.8824 18.0686 5.36255 18.0686H20.9667C21.4468 18.0686 21.836 17.6798 21.836 17.2001C21.836 16.7204 21.4468 16.3315 20.9667 16.3315Z"
      fill="#E9526D"
    />
    <path
      d="M20.9667 12.2313H5.36255C4.8824 12.2313 4.49316 12.6202 4.49316 13.0999C4.49316 13.5796 4.8824 13.9684 5.36255 13.9684H20.9667C21.4468 13.9684 21.836 13.5796 21.836 13.0999C21.836 12.6202 21.4468 12.2313 20.9667 12.2313Z"
      fill="#E9526D"
    />
    <path
      d="M20.9667 20.4317H5.36255C4.8824 20.4317 4.49316 20.8206 4.49316 21.3003C4.49316 21.7799 4.8824 22.1688 5.36255 22.1688H20.9667C21.4468 22.1688 21.836 21.7799 21.836 21.3003C21.836 20.8206 21.4468 20.4317 20.9667 20.4317Z"
      fill="#E9526D"
    />
    <mask
      id="mask0_2705_3181"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="13"
      y="7"
      width="12"
      height="24"
    >
      <path
        d="M13.2852 8.50698V30.02H24.4309V7.08182H13.2852V8.50698Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask0_2705_3181)">
      <path
        d="M21.4886 8.77417H4.94264C3.31741 8.77417 2 10.0903 2 11.714V22.8935C2 24.5172 3.31741 25.8333 4.94264 25.8333H9.22834V31.3563L16.5138 25.8333H21.4886C23.1139 25.8333 24.4313 24.5172 24.4313 22.8935V11.714C24.4313 10.0903 23.1139 8.77417 21.4886 8.77417Z"
        fill="#E9526D"
      />
      <path
        d="M20.9667 16.3315H5.36255C4.8824 16.3315 4.49316 16.7204 4.49316 17.2001C4.49316 17.6798 4.8824 18.0686 5.36255 18.0686H20.9667C21.4468 18.0686 21.836 17.6798 21.836 17.2001C21.836 16.7204 21.4468 16.3315 20.9667 16.3315Z"
        fill="#C1286B"
      />
      <path
        d="M20.9667 12.2313H5.36255C4.8824 12.2313 4.49316 12.6202 4.49316 13.0999C4.49316 13.5796 4.8824 13.9684 5.36255 13.9684H20.9667C21.4468 13.9684 21.836 13.5796 21.836 13.0999C21.836 12.6202 21.4468 12.2313 20.9667 12.2313Z"
        fill="#C1286B"
      />
      <path
        d="M20.9667 20.4317H5.36255C4.8824 20.4317 4.49316 20.8206 4.49316 21.3003C4.49316 21.7799 4.8824 22.1688 5.36255 22.1688H20.9667C21.4468 22.1688 21.836 21.7799 21.836 21.3003C21.836 20.8206 21.4468 20.4317 20.9667 20.4317Z"
        fill="#C1286B"
      />
    </g>
    <path
      d="M27.4393 2.49134L16.6739 13.2465L15.902 16.3561C15.8107 16.7234 16.1434 17.0559 16.5114 16.965L19.6241 16.1938L30.3895 5.43869C31.2041 4.6248 31.2041 3.30522 30.3895 2.49134C29.5748 1.67745 28.254 1.67745 27.4393 2.49134Z"
      fill="#E9526D"
    />
    <mask
      id="mask1_2705_3181"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="15"
      y="1"
      width="16"
      height="16"
    >
      <path
        d="M27.4393 2.49134L16.6739 13.2465L15.902 16.3561C15.8107 16.7234 16.1434 17.0559 16.5114 16.965L19.6241 16.1938L30.3895 5.43869C31.2041 4.6248 31.2041 3.30522 30.3895 2.49134C29.5748 1.67745 28.254 1.67745 27.4393 2.49134Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask1_2705_3181)">
      <path
        d="M18.8156 11.1061L14.5078 15.4099L15.9828 16.8834L20.2906 12.5797L18.8156 11.1061Z"
        fill="#5E5C77"
      />
      <path
        d="M20.2903 12.5798L15.9824 16.8835L17.4574 18.3571L21.7652 14.0533L20.2903 12.5798Z"
        fill="#25254D"
      />
      <path
        d="M28.9141 1.01785L16.6738 13.2465L18.1488 14.72L30.3891 2.4914L28.9141 1.01785Z"
        fill="#5E5C77"
      />
      <path
        d="M30.3887 2.49129L18.1484 14.7199L19.6234 16.1935L31.8637 3.96485L30.3887 2.49129Z"
        fill="#25254D"
      />
    </g>
  </svg>
);

export const HandDragging = ({ className = '' }: IconProps) => {
  return (
    <svg
      className={className}
      width={20}
      height={20}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.38885 18.122C5.24485 18.9027 4.67285 20.1953 4.67285 22C4.67285 24.7067 8.00019 29.3333 11.0009 29.3333H18.6775C21.6142 29.3333 23.3975 26.7667 23.3975 24.7067V16.4C23.3975 15.826 23.17 15.2754 22.7647 14.8689C22.3594 14.4624 21.8095 14.2331 21.2355 14.2313C20.9522 14.2305 20.6716 14.2854 20.4095 14.393C20.1475 14.5006 19.9092 14.6588 19.7083 14.8585C19.5074 15.0582 19.3479 15.2956 19.2387 15.557C19.1296 15.8184 19.073 16.0987 19.0722 16.382V16.4553"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M7.32129 19.63V5.10867C7.32129 4.53978 7.54728 3.99419 7.94954 3.59192C8.35181 3.18966 8.8974 2.96367 9.46629 2.96367C10.0352 2.96367 10.5808 3.18966 10.983 3.59192C11.3853 3.99419 11.6113 4.53978 11.6113 5.10867V15.7653"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M11.6113 16V13.2053C11.6113 12.9598 11.6597 12.7168 11.7536 12.49C11.8476 12.2632 11.9853 12.0571 12.1588 11.8835C12.3324 11.7099 12.5385 11.5722 12.7653 11.4783C12.9921 11.3844 13.2352 11.336 13.4807 11.336C13.7261 11.336 13.9692 11.3844 14.196 11.4783C14.4228 11.5722 14.6289 11.7099 14.8025 11.8835C14.9761 12.0571 15.1138 12.2632 15.2077 12.49C15.3016 12.7168 15.35 12.9598 15.35 13.2053V16.2853"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.334 16.4387V14.5387C15.334 14.0429 15.5309 13.5674 15.8815 13.2168C16.2321 12.8663 16.7075 12.6693 17.2033 12.6693C17.6991 12.6693 18.1746 12.8663 18.5251 13.2168C18.8757 13.5674 19.0726 14.0429 19.0726 14.5387V16.6693"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.33398 5.33333H27.334"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M24 8.33333L25.1113 7.33333L27.3333 5.33333L25.1113 3.33333L24 2.33333"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const DesktopIcon = (props: IconProps) => (
  <svg
    width="22"
    height="18"
    viewBox="0 0 22 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0.5 1.875C0.5 0.839466 1.33947 0 2.375 0H19.625C20.6605 0 21.5 0.839466 21.5 1.875V13.125C21.5 14.1605 20.6605 15 19.625 15H13.25V16.5H17C17.4142 16.5 17.75 16.8358 17.75 17.25C17.75 17.6642 17.4142 18 17 18H5C4.58579 18 4.25 17.6642 4.25 17.25C4.25 16.8358 4.58579 16.5 5 16.5H8.75V15H2.375C1.33947 15 0.5 14.1605 0.5 13.125V1.875ZM10.25 15V16.5H11.75V15H10.25ZM2.375 13.5C2.16789 13.5 2 13.3321 2 13.125V1.875C2 1.66789 2.16789 1.5 2.375 1.5H19.625C19.8321 1.5 20 1.66789 20 1.875V13.125C20 13.3321 19.8321 13.5 19.625 13.5H2.375Z"
      fill="currentColor"
    />
  </svg>
);

export const CoinIcon = ({ className = '', strokeWidth = 2.5 }: IconProps) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18.8284 19.3807C17.2663 21.9842 14.7337 21.9842 13.1716 19.3807C11.6095 16.7772 11.6095 15.2228 13.1716 12.6193C14.7337 10.0158 17.2663 10.0158 18.8284 12.6193M28 16C28 22.6274 22.6274 28 16 28C9.37258 28 4 22.6274 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const BackpackIcon = ({ ...rest }: IconProps) => (
  <svg
    {...rest}
    width="25"
    height="25"
    viewBox="0 0 25 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.8066 2.5C11.8685 2.49995 10.9644 2.85155 10.2727 3.48543C9.58111 4.1193 9.1522 4.98939 9.07064 5.924C7.78327 6.60377 6.70582 7.62199 5.95443 8.86891C5.20303 10.1158 4.80618 11.5442 4.80664 13V14.5H20.8066V13C20.8071 11.5442 20.4103 10.1158 19.6589 8.86891C18.9075 7.62199 17.83 6.60377 16.5426 5.924C16.4611 4.98939 16.0322 4.1193 15.3405 3.48543C14.6489 2.85155 13.7448 2.49995 12.8066 2.5ZM20.8066 16.214H10.3066V17.75C10.3066 17.9489 10.2276 18.1397 10.087 18.2803C9.94632 18.421 9.75555 18.5 9.55664 18.5C9.35773 18.5 9.16696 18.421 9.02631 18.2803C8.88566 18.1397 8.80664 17.9489 8.80664 17.75V16.214H4.80664V19.25C4.80664 19.6768 4.8907 20.0994 5.05403 20.4937C5.21736 20.888 5.45675 21.2463 5.75854 21.5481C6.06033 21.8499 6.41861 22.0893 6.81292 22.2526C7.20723 22.4159 7.62985 22.5 8.05664 22.5H17.5566C17.9834 22.5 18.4061 22.4159 18.8004 22.2526C19.1947 22.0893 19.5529 21.8499 19.8547 21.5481C20.1565 21.2463 20.3959 20.888 20.5592 20.4937C20.7226 20.0994 20.8066 19.6768 20.8066 19.25V16.214ZM12.8066 5C12.1086 5 11.4326 5.09 10.7866 5.257C10.972 4.87927 11.2595 4.56107 11.6165 4.33852C11.9736 4.11598 12.3859 3.99801 12.8066 3.99801C13.2274 3.99801 13.6397 4.11598 13.9968 4.33852C14.3538 4.56107 14.6413 4.87927 14.8266 5.257C14.167 5.08578 13.4882 4.99942 12.8066 5ZM8.80664 10.917C8.80664 10.276 9.06129 9.6612 9.51456 9.20792C9.96784 8.75465 10.5826 8.5 11.2236 8.5H14.3896C15.0307 8.5 15.6454 8.75465 16.0987 9.20792C16.552 9.6612 16.8066 10.276 16.8066 10.917C16.8066 11.79 16.0976 12.5 15.2236 12.5H10.3896C9.9698 12.5 9.56716 12.3332 9.27029 12.0364C8.97342 11.7395 8.80664 11.3368 8.80664 10.917ZM11.2236 10C10.9804 10 10.7472 10.0966 10.5752 10.2686C10.4033 10.4406 10.3066 10.6738 10.3066 10.917C10.3066 10.963 10.3436 11 10.3896 11H15.2236C15.2457 11 15.2668 10.9913 15.2823 10.9757C15.2979 10.9601 15.3066 10.939 15.3066 10.917C15.3066 10.6738 15.21 10.4406 15.0381 10.2686C14.8661 10.0966 14.6328 10 14.3896 10H11.2236Z"
      fill="currentColor"
    />
  </svg>
);

export function GradientCheckIcon({ strokeDashoffset }: IconProps) {
  return (
    <svg
      x={-10}
      y={-8}
      width="21"
      height="17"
      viewBox="0 0 21 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.96362 9.4981L7.26908 13.8036L18.0327 3.03992"
        stroke="url(#check_gradient_op15)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M2.96362 9.4981L7.26908 13.8036L18.0327 3.03992"
        stroke="url(#check_gradient)"
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={21.31096649169922}
        strokeDashoffset={strokeDashoffset}
        style={{ transition: 'stroke-dashoffset 0.3s ease-in-out' }}
      />

      <defs>
        <linearGradient
          id="check_gradient"
          x1="2.96362"
          y1="5.85896"
          x2="14.1431"
          y2="-5.21256"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--color-secondary)" />
          <stop offset="1" stopColor="var(--color-primary)" />
        </linearGradient>

        <linearGradient
          id="check_gradient_op15"
          x1="2.96362"
          y1="5.85896"
          x2="14.1431"
          y2="-5.21256"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="rgb(130, 90, 224, 0.15)" />
          <stop offset="1" stopColor="rgb(27, 159, 255, 0.15)" />
        </linearGradient>
      </defs>
    </svg>
  );
}
export const BugIcon = ({ className = '', ...rest }: IconProps) => (
  <svg
    {...rest}
    className={className}
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0112 12.75zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 01-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 002.248-2.354M12 12.75a2.25 2.25 0 01-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 00-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 01.4-2.253M12 8.25a2.25 2.25 0 00-2.248 2.146M12 8.25a2.25 2.25 0 012.248 2.146M8.683 5a6.032 6.032 0 01-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0115.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 00-.575-1.752M4.921 6a24.048 24.048 0 00-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 01-5.223 1.082"
    />
  </svg>
);

export const EmojiNeutralIcon = ({
  className = '',
  strokeWidth = 2.5,
}: IconProps) => (
  <svg
    className={className}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M19.7712 20.7712C16.8257 20.7712 12.2288 20.7712 12.2288 20.7712M12 13.3333H12.0133M20 13.3333H20.0133M28 16C28 22.6274 22.6274 28 16 28C9.37258 28 4 22.6274 4 16C4 9.37258 9.37258 4 16 4C22.6274 4 28 9.37258 28 16Z"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MessageDCheckIcon = (props: IconProps) => (
  <svg
    {...props}
    width="37"
    height="32"
    viewBox="0 0 37 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.75 22H3.25C2.00736 22 1 20.9926 1 19.75V9.25M20.5 9.25C20.5 8.00736 19.4926 7 18.25 7H3.25C2.00736 7 1 8.00736 1 9.25M20.5 9.25V9.49271C20.5 10.274 20.0947 10.9994 19.4292 11.4089L11.9292 16.0243C11.2061 16.4694 10.2939 16.4694 9.57078 16.0243L2.07078 11.4089C1.40535 10.9994 1 10.274 1 9.49271V9.25M20.5 9.25V16.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M28.4545 11.6535C28.784 11.9045 28.8476 12.3751 28.5966 12.7046L20.5966 23.2046C20.4654 23.3767 20.2663 23.4838 20.0503 23.4983C19.8344 23.5129 19.6227 23.4334 19.4697 23.2804L14.9697 18.7804C14.6768 18.4875 14.6768 18.0126 14.9697 17.7197C15.2626 17.4268 15.7374 17.4268 16.0303 17.7197L19.9235 21.6129L27.4034 11.7955C27.6545 11.466 28.1251 11.4024 28.4545 11.6535Z"
      fill="currentColor"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M33.4545 11.6535C33.784 11.9045 33.8476 12.3751 33.5966 12.7046L25.5966 23.2046C25.4654 23.3767 25.2663 23.4838 25.0503 23.4983C24.8344 23.5129 24.6227 23.4334 24.4697 23.2804L19.9697 18.7804C19.6768 18.4875 19.6768 18.0126 19.9697 17.7197C20.2626 17.4268 20.7374 17.4268 21.0303 17.7197L24.9235 21.6129L32.4034 11.7955C32.6545 11.466 33.1251 11.4024 33.4545 11.6535Z"
      fill="currentColor"
    />
  </svg>
);

export const MessageCheckIcon = (props: IconProps) => (
  <svg
    {...props}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M13.75 22H3.25C2.00736 22 1 20.9926 1 19.75V9.25M20.5 9.25C20.5 8.00736 19.4926 7 18.25 7H3.25C2.00736 7 1 8.00736 1 9.25M20.5 9.25V9.49271C20.5 10.274 20.0947 10.9994 19.4292 11.4089L11.9292 16.0243C11.2061 16.4694 10.2939 16.4694 9.57078 16.0243L2.07078 11.4089C1.40535 10.9994 1 10.274 1 9.49271V9.25M20.5 9.25V15.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fill-rule="evenodd"
      clip-rule="evenodd"
      d="M28.4545 11.6535C28.784 11.9045 28.8476 12.3751 28.5966 12.7046L20.5966 23.2046C20.4654 23.3767 20.2663 23.4838 20.0503 23.4983C19.8344 23.5129 19.6227 23.4334 19.4697 23.2804L14.9697 18.7804C14.6768 18.4875 14.6768 18.0126 14.9697 17.7197C15.2626 17.4268 15.7374 17.4268 16.0303 17.7197L19.9235 21.6129L27.4034 11.7955C27.6545 11.466 28.1251 11.4024 28.4545 11.6535Z"
      fill="currentColor"
    />
  </svg>
);

export function StartIcon(props: IconProps) {
  return (
    <svg
      width={15}
      height={16}
      viewBox="0 0 15 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M13.526 5.994c1.544.892 1.544 3.12 0 4.011l-9.552 5.516C2.43 16.412.5 15.298.5 13.515V2.485C.5.702 2.43-.412 3.974.479l9.552 5.515z"
        fill="currentColor"
      />
    </svg>
  );
}

export const CookieIcon = ({ ...props }: IconProps) => (
  <svg
    {...props}
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_3546_3556)">
      <path
        d="M16.0003 2.66667C16.843 2.66667 17.6057 2.93467 18.1243 3.64C18.4377 4.06667 18.6123 4.576 18.6563 5.08133L18.667 5.33333C18.6669 5.72652 18.7538 6.11486 18.9214 6.47054C19.0889 6.82622 19.3331 7.14044 19.6364 7.39069C19.9396 7.64095 20.2945 7.82104 20.6755 7.91808C21.0565 8.01512 21.4543 8.0267 21.8403 7.952L22.0817 7.89467C22.305 7.82945 22.5405 7.81715 22.7695 7.85873C22.9984 7.90032 23.2146 7.99465 23.4007 8.13424C23.5869 8.27384 23.738 8.45488 23.8421 8.663C23.9462 8.87113 24.0003 9.10063 24.0003 9.33333C24.0001 10.0061 24.2542 10.6541 24.7117 11.1474C25.1691 11.6407 25.7961 11.9429 26.467 11.9933L26.667 12C27.595 12 28.967 12.5493 29.1857 14.0067C29.283 14.6573 29.3337 15.3227 29.3337 16C29.3337 23.364 23.3643 29.3333 16.0003 29.3333C8.63633 29.3333 2.66699 23.364 2.66699 16C2.66699 8.636 8.63633 2.66667 16.0003 2.66667ZM16.0003 5.33333C13.8337 5.33353 11.7186 5.9935 9.93631 7.22541C8.15404 8.45732 6.78916 10.2028 6.0233 12.2295C5.25744 14.2562 5.12691 16.468 5.64908 18.5708C6.17124 20.6735 7.32135 22.5673 8.94636 24.0003C10.5714 25.4333 12.5942 26.3375 14.7457 26.5925C16.8973 26.8475 19.0754 26.4413 20.9904 25.428C22.9054 24.4146 24.4664 22.8421 25.4657 20.9197C26.465 18.9974 26.8551 16.8163 26.5843 14.6667C25.4157 14.6482 24.2854 14.2466 23.3672 13.5235C22.449 12.8003 21.7936 11.7957 21.5017 10.664C18.5337 10.756 16.0003 8.304 16.0003 5.33333ZM12.667 18.6667C13.1974 18.6667 13.7061 18.8774 14.0812 19.2525C14.4563 19.6275 14.667 20.1362 14.667 20.6667C14.667 21.1971 14.4563 21.7058 14.0812 22.0809C13.7061 22.456 13.1974 22.6667 12.667 22.6667C12.1366 22.6667 11.6279 22.456 11.2528 22.0809C10.8777 21.7058 10.667 21.1971 10.667 20.6667C10.667 20.1362 10.8777 19.6275 11.2528 19.2525C11.6279 18.8774 12.1366 18.6667 12.667 18.6667ZM20.667 16C21.1974 16 21.7061 16.2107 22.0812 16.5858C22.4563 16.9609 22.667 17.4696 22.667 18C22.667 18.5304 22.4563 19.0391 22.0812 19.4142C21.7061 19.7893 21.1974 20 20.667 20C20.1366 20 19.6279 19.7893 19.2528 19.4142C18.8777 19.0391 18.667 18.5304 18.667 18C18.667 17.4696 18.8777 16.9609 19.2528 16.5858C19.6279 16.2107 20.1366 16 20.667 16ZM12.667 10.6667C13.1974 10.6667 13.7061 10.8774 14.0812 11.2525C14.4563 11.6275 14.667 12.1362 14.667 12.6667C14.667 13.1971 14.4563 13.7058 14.0812 14.0809C13.7061 14.456 13.1974 14.6667 12.667 14.6667C12.1366 14.6667 11.6279 14.456 11.2528 14.0809C10.8777 13.7058 10.667 13.1971 10.667 12.6667C10.667 12.1362 10.8777 11.6275 11.2528 11.2525C11.6279 10.8774 12.1366 10.6667 12.667 10.6667Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_3546_3556">
        <rect width="32" height="32" fill="transparent" />
      </clipPath>
    </defs>
  </svg>
);

export function MoveAreaIcon({ strokeWidth = 1.5, ...props }: IconProps) {
  return (
    <svg
      width={20}
      height={18}
      viewBox="0 0 20 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10.833 7.083L13.75 10m0 0l-2.917 2.917M13.75 10h-7.5M2 4v10c0 .53.187 1.04.52 1.414.334.375.786.586 1.258.586h12.444c.472 0 .924-.21 1.257-.586.334-.375.521-.884.521-1.414V6c0-.53-.187-1.04-.52-1.414A1.684 1.684 0 0016.221 4H10.89L9.11 2H3.778c-.472 0-.924.21-1.257.586C2.187 2.96 2 3.47 2 4z"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function WhatsAppIcon(props: IconProps) {
  return (
    <svg
      width={32}
      height={32}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#clip0_4042_45084)">
        <mask
          id="a"
          style={{
            maskType: 'luminance',
          }}
          maskUnits="userSpaceOnUse"
          x={4}
          y={4}
          width={24}
          height={24}
        >
          <path d="M4 4h24v24H4V4z" fill="#fff" />
        </mask>
        <g mask="url(#a)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M21.415 18.382c-.298-.149-1.759-.867-2.031-.967-.272-.1-.47-.148-.669.15-.198.297-.767.966-.94 1.164-.174.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.019-.458.13-.606.134-.133.297-.347.446-.52.149-.173.198-.298.297-.497.1-.198.05-.371-.025-.52-.074-.149-.668-1.612-.916-2.207-.241-.579-.486-.5-.668-.51-.174-.008-.372-.01-.57-.01-.198 0-.52.074-.792.372-.273.297-1.04 1.016-1.04 2.479 0 1.462 1.064 2.875 1.213 3.074.149.199 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.57-.085 1.758-.719 2.006-1.413.248-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.422 7.403h-.004a9.869 9.869 0 01-5.032-1.378l-.36-.214-3.742.982.999-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.002-5.45 4.436-9.884 9.889-9.884a9.8 9.8 0 016.988 2.899 9.82 9.82 0 012.892 6.992c-.002 5.45-4.436 9.885-9.884 9.885zm8.412-18.297A11.82 11.82 0 0015.992 4C9.438 4 4.102 9.335 4.1 15.892a11.86 11.86 0 001.587 5.945L4 28l6.304-1.654a11.9 11.9 0 005.684 1.448h.005c6.554 0 11.89-5.335 11.892-11.893a11.82 11.82 0 00-3.48-8.413"
            fill="currentColor"
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_4042_45084">
          <path fill="#fff" transform="translate(4 4)" d="M0 0H24V24H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function ArrowUpTrayIcon({ strokeWidth = 2, ...props }: IconProps) {
  return (
    <svg
      width={20}
      height={20}
      viewBox="0 0 20 20"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      {...props}
    >
      <path
        d="M5.99994 14.9999C4.7529 15.0008 3.55056 14.5356 2.62882 13.6956C1.70709 12.8557 1.1325 11.7016 1.01779 10.4599C0.903082 9.21811 1.25654 7.97831 2.00881 6.98373C2.76107 5.98914 3.85785 5.31156 5.08394 5.08394C5.2993 3.93761 5.90849 2.90258 6.80617 2.15784C7.70384 1.41311 8.83356 1.00549 9.99994 1.00549C11.1663 1.00549 12.296 1.41311 13.1937 2.15784C14.0914 2.90258 14.7006 3.93761 14.9159 5.08394C16.1379 5.31619 17.2295 5.99541 17.9778 6.98906C18.726 7.98271 19.0772 9.21949 18.9628 10.4581C18.8484 11.6967 18.2766 12.8482 17.359 13.688C16.4414 14.5277 15.2438 14.9955 13.9999 14.9999M6.99994 9.99994L9.99994 6.99994M9.99994 6.99994L12.9999 9.99994M9.99994 6.99994V18.9999"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const HomeworkColorIcon = ({ className = '' }: IconProps) => (
  <svg
    className={className}
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.0725 4.78003H8.15288C5.96987 4.78003 4.2002 6.54971 4.2002 8.73272V26.8624C4.2002 29.0454 5.96987 30.8151 8.15288 30.8151H22.0725C24.2555 30.8151 26.0252 29.0454 26.0252 26.8624V8.73272C26.0252 6.54971 24.2555 4.78003 22.0725 4.78003Z"
      fill="#FD7C59"
    />
    <path
      d="M14.9434 4.73169H22.1504C23.1781 4.73169 24.1637 5.13992 24.8903 5.86659C25.617 6.59325 26.0252 7.57881 26.0252 8.60647V26.9404C26.0252 27.4492 25.925 27.9531 25.7303 28.4232C25.5355 28.8933 25.2501 29.3205 24.8903 29.6803C24.5305 30.0401 24.1034 30.3255 23.6332 30.5202C23.1631 30.7149 22.6593 30.8152 22.1504 30.8152H14.9434V4.73169Z"
      fill="#FD426A"
    />
    <path
      d="M9.04538 7.26602H21.234C21.85 7.26602 22.4408 7.51075 22.8765 7.94636C23.3121 8.38198 23.5568 8.9728 23.5568 9.58886V25.9619C23.5568 26.578 23.3121 27.1688 22.8765 27.6044C22.4408 28.04 21.85 28.2848 21.234 28.2848H9.04538C8.74579 28.2848 8.44913 28.2257 8.17236 28.1111C7.89558 27.9964 7.64412 27.8283 7.43232 27.6164C7.22052 27.4045 7.05254 27.153 6.93798 26.8762C6.82342 26.5993 6.76452 26.3027 6.76465 26.0031V9.54675C6.76465 8.94187 7.00494 8.36175 7.43266 7.93403C7.86038 7.50631 8.44049 7.26602 9.04538 7.26602V7.26602Z"
      fill="white"
    />
    <path
      d="M14.9434 7.26602H21.2165C21.8373 7.26602 22.4326 7.51263 22.8716 7.95161C23.3106 8.39058 23.5572 8.98596 23.5572 9.60676V25.9431C23.5572 26.5639 23.3106 27.1592 22.8716 27.5982C22.4326 28.0372 21.8373 28.2838 21.2165 28.2838H14.9434V7.26602Z"
      fill="#D9ECFD"
    />
    <path
      d="M18.6446 2.94113H11.3383C10.7367 2.94113 10.249 3.42883 10.249 4.03045V6.69107C10.249 7.29268 10.7367 7.78039 11.3383 7.78039H18.6446C19.2463 7.78039 19.734 7.29268 19.734 6.69107V4.03045C19.734 3.42883 19.2463 2.94113 18.6446 2.94113Z"
      fill="#EDEDED"
    />
    <path
      d="M14.9922 2.94113H18.6216C18.9168 2.94113 19.1999 3.0584 19.4087 3.26713C19.6174 3.47586 19.7347 3.75897 19.7347 4.05416V6.66736C19.7347 6.96255 19.6174 7.24565 19.4087 7.45439C19.1999 7.66312 18.9168 7.78039 18.6216 7.78039H14.9922V2.94113Z"
      fill="#DCD8D4"
    />
    <path
      d="M20.9719 11.3129H12.7374C12.3253 11.3129 11.9912 11.647 11.9912 12.0591V12.4056C11.9912 12.8177 12.3253 13.1518 12.7374 13.1518H20.9719C21.384 13.1518 21.7181 12.8177 21.7181 12.4056V12.0591C21.7181 11.647 21.384 11.3129 20.9719 11.3129Z"
      fill="#FD426A"
    />
    <path
      d="M20.9719 17.0234H12.7374C12.3253 17.0234 11.9912 17.3575 11.9912 17.7696V18.1161C11.9912 18.5282 12.3253 18.8623 12.7374 18.8623H20.9719C21.384 18.8623 21.7181 18.5282 21.7181 18.1161V17.7696C21.7181 17.3575 21.384 17.0234 20.9719 17.0234Z"
      fill="#FD426A"
    />
    <path
      d="M20.9719 22.8304H12.7374C12.3253 22.8304 11.9912 23.1644 11.9912 23.5766V23.9231C11.9912 24.3352 12.3253 24.6693 12.7374 24.6693H20.9719C21.384 24.6693 21.7181 24.3352 21.7181 23.9231V23.5766C21.7181 23.1644 21.384 22.8304 20.9719 22.8304Z"
      fill="#FD426A"
    />
    <path
      d="M9.62063 13.1518C10.1284 13.1518 10.5401 12.7402 10.5401 12.2324C10.5401 11.7246 10.1284 11.3129 9.62063 11.3129C9.11283 11.3129 8.70117 11.7246 8.70117 12.2324C8.70117 12.7402 9.11283 13.1518 9.62063 13.1518Z"
      fill="#FD426A"
    />
    <path
      d="M9.62063 18.8623C10.1284 18.8623 10.5401 18.4507 10.5401 17.9429C10.5401 17.4351 10.1284 17.0234 9.62063 17.0234C9.11283 17.0234 8.70117 17.4351 8.70117 17.9429C8.70117 18.4507 9.11283 18.8623 9.62063 18.8623Z"
      fill="#FD426A"
    />
    <path
      d="M9.62063 24.6693C10.1284 24.6693 10.5401 24.2576 10.5401 23.7498C10.5401 23.242 10.1284 22.8304 9.62063 22.8304C9.11283 22.8304 8.70117 23.242 8.70117 23.7498C8.70117 24.2576 9.11283 24.6693 9.62063 24.6693Z"
      fill="#FD426A"
    />
    <path
      d="M14.9434 11.3129H21.0955C21.2607 11.3129 21.4191 11.3785 21.5359 11.4953C21.6527 11.6121 21.7183 11.7706 21.7183 11.9357V12.5285C21.7184 12.6104 21.7023 12.6914 21.671 12.767C21.6398 12.8427 21.5939 12.9114 21.536 12.9692C21.4782 13.0271 21.4095 13.0731 21.3339 13.1044C21.2583 13.1357 21.1773 13.1518 21.0955 13.1518H14.9434V11.3129Z"
      fill="#D3006C"
    />
    <path
      d="M14.9434 17.0234H21.0955C21.2607 17.0234 21.4191 17.089 21.5359 17.2058C21.6527 17.3226 21.7183 17.481 21.7183 17.6462V18.239C21.7184 18.3209 21.7023 18.4019 21.671 18.4775C21.6398 18.5531 21.5939 18.6218 21.536 18.6797C21.4782 18.7376 21.4095 18.7835 21.3339 18.8149C21.2583 18.8462 21.1773 18.8623 21.0955 18.8623H14.9434V17.0234Z"
      fill="#D3006C"
    />
    <path
      d="M14.8945 22.8304H21.0926C21.2584 22.8304 21.4175 22.8962 21.5347 23.0135C21.652 23.1307 21.7179 23.2898 21.7179 23.4556V24.044C21.7179 24.1261 21.7017 24.2074 21.6703 24.2833C21.6388 24.3592 21.5928 24.4281 21.5347 24.4861C21.4767 24.5442 21.4077 24.5903 21.3319 24.6217C21.256 24.6531 21.1747 24.6693 21.0926 24.6693H14.8945V22.8304Z"
      fill="#D3006C"
    />
    <path d="M14.5 31V23.5M23 18L26.5 19.5" stroke="white" strokeWidth="0.3" />
    <path
      d="M14 22.5V24H14.5M23 18L14.5 21.5"
      stroke="white"
      strokeWidth="0.3"
    />
    <path
      d="M32 22.7256V23.7857H23V18L30.4986 21.2143C31.3077 21.5357 32 21.8913 32 22.7256Z"
      fill="#D3006C"
    />
    <path
      d="M23 18V23.7857H14V22.7256C14 21.8913 14.6923 21.5357 15.5038 21.2143L23 18Z"
      fill="#E9456B"
    />
    <path
      d="M23 23.7857V32.7857H29.963C30.5401 32.7857 31.0348 32.4829 31.2468 32.0516C31.3181 31.9069 31.3571 31.748 31.3571 31.581V23.7857H23Z"
      fill="#FD426A"
    />
    <path
      d="M22.9997 27.8357V30.0857C22.7329 30.0857 22.9997 30.3158 22.9997 30.0857V31.2107V32.7857H16.039C15.2677 32.7857 14.6426 32.2461 14.6426 31.5812V23.7857H22.9997V25.3607V26.2607C22.9997 26.7107 22.9997 27.6056 22.9997 27.8357Z"
      fill="#FD7C59"
    />
    <path
      d="M19.786 25.8952V28.7475C19.786 28.8475 19.7283 28.9285 19.6571 28.9285H17.8574V25.7142H19.6571C19.7283 25.7142 19.786 25.7953 19.786 25.8952Z"
      fill="white"
    />
    <path
      d="M16.5717 25.8952V28.7475C16.5717 28.8475 16.6102 28.9285 16.6577 28.9285H17.8574V25.7142H16.6577C16.6102 25.7142 16.5717 25.7953 16.5717 25.8952Z"
      fill="white"
    />
    <path
      d="M29.4295 25.8952V28.7476C29.4295 28.8475 29.3719 28.9285 29.3006 28.9285H27.501V25.7143H29.3006C29.3719 25.7143 29.4295 25.7953 29.4295 25.8952Z"
      fill="#DAECFB"
    />
    <path
      d="M26.2153 25.8952V28.7476C26.2153 28.8475 26.2537 28.9285 26.3012 28.9285H27.501V25.7143H26.3012C26.2537 25.7143 26.2153 25.7953 26.2153 25.8952Z"
      fill="#DAECFB"
    />
    <path
      d="M24.4855 25.7143H21.5143C21.0713 25.7143 21.0713 26.0137 21.0713 26.8928V31.1939V32.7857H24.9284V31.1939V26.8928C24.9284 25.8903 24.9284 25.7143 24.4855 25.7143Z"
      fill="#EDEDED"
    />
    <path
      d="M24.7071 25.7143H23L23 26.8928V31.1939V32.7857H24.9286V31.1939V26.8928V26.8928C24.9286 25.8903 24.9286 25.7143 24.7071 25.7143Z"
      fill="#DCD8D4"
    />
  </svg>
);

export function ResourceColorIcon(props: IconProps) {
  return (
    <svg
      {...props}
      width={36}
      height={36}
      viewBox="0 0 36 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.2168 8.08698V30H28.2731C29.0237 30 29.6671 29.2628 29.9429 28.2126C30.0356 27.8603 30.0864 27.4734 30.0864 27.0668V10.087C30.0864 8.98241 29.1909 8.08698 28.0864 8.08698H19.2168Z"
        fill="#D3006C"
      />
      <path
        d="M19.6522 15.6V21V25.8V30H6.61535C5.17084 30 4 28.561 4 26.7879V8C4 6.89543 4.89588 6 6.00045 6C8.5817 6 10.7179 6 12.9441 6C14.0209 6 15.1187 8.08696 16.2981 8.08696C17.3388 8.08696 18.4429 8.08696 19.6522 8.08696L19.6522 10.2V13.2V15.6Z"
        fill="#E9456B"
      />
      <path
        d="M6.0867 12.0074V25.7024C6.0867 26.1823 6.39879 26.5714 6.78425 26.5714H16.5215V11.1385H6.78425C6.39879 11.1385 6.0867 11.5276 6.0867 12.0074Z"
        fill="#DAECFB"
      />
      <path
        d="M27.9502 13.8278V25.8109C27.9502 26.2308 27.6233 26.5713 27.2195 26.5713H17.0186V13.0674H27.2195C27.6233 13.0674 27.9502 13.4079 27.9502 13.8278Z"
        fill="#DAECFB"
      />
      <path
        d="M7.1306 11.0972V25.6482C7.1306 26.158 7.50511 26.5714 7.96767 26.5714H19.6523V10.1739H7.96767C7.50511 10.1739 7.1306 10.5873 7.1306 11.0972Z"
        fill="white"
      />
      <path
        d="M28.0002 10.9377V22.9753C28.0002 23.3971 27.7505 23.7391 27.4421 23.7391H19.6523V10.1739H27.4421C27.7505 10.1739 28.0002 10.5159 28.0002 10.9377Z"
        fill="#DAECFB"
      />
      <path
        d="M18.6084 12.2609V30H29.9106C30.8474 30 31.6504 29.4032 31.9946 28.5531C32.1102 28.2678 32.1736 27.9546 32.1736 27.6255V14.2609C32.1736 13.1563 31.2782 12.2609 30.1736 12.2609H18.6084Z"
        fill="#FD426A"
      />
      <path
        d="M19.6521 19.3565V23.3478V26.8957V30H6.08691C7.8563 30 9.03582 28.9364 9.03582 27.6258V14.2609C9.03582 13.1563 9.93125 12.2609 11.0358 12.2609H19.6521V15.3652V17.5826V19.3565Z"
        fill="#FD7C59"
      />
    </svg>
  );
}

export function TrophyGold(props: IconProps) {
  return (
    <svg
      width={43}
      height={44}
      viewBox="0 0 43 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#clip0_8133_23520)">
        <path
          d="M11.815 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728h11.353V33.703h-9.798z"
          fill="#F59E0B"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#E38F00"
        />
        <path
          d="M21.498 34H11.482s-.146-1.626 2.626-2.168c2.773-.543 3.102-.444 3.118-3.193.006-1.306 0-3.193 0-3.193-.02-.636 1.59-2.017-.053-2.47-1.62-.446-6.921-3.014-9.12-8.974C5.856 8.044 5.499 3.665 5.499 1.232 5.5 1.231 5.353 0 8.2 0h13.299v34z"
          fill="#FDBB4C"
        />
        <path
          d="M10.815 17.494c-7.896-2.87-7.884-9.335-7.862-12.6 0-.253.184-.46.412-.46h4.636a47.657 47.657 0 01-.178-2.766.417.417 0 00-.167-.037H3.13c-1.45 0-2.625 1.324-2.63 2.962C.486 9.687 1.607 18.98 12.5 20.63a17.967 17.967 0 01-1.685-3.137z"
          fill="#FDBB4C"
        />
        <path
          d="M21.498 34h10.016s.146-1.626-2.626-2.168c-2.772-.543-3.102-.444-3.118-3.193-.006-1.306 0-3.193 0-3.193.02-.636-1.59-2.017.053-2.47 1.62-.446 6.921-3.014 9.12-8.974 2.198-5.957 2.554-10.336 2.554-12.77 0 0 .146-1.232-2.7-1.232H21.498v34z"
          fill="#F59E0B"
        />
        <path
          d="M32.185 17.494c7.896-2.87 7.884-9.335 7.862-12.6 0-.253-.184-.46-.412-.46h-4.636c.096-1.038.15-1.963.178-2.766a.418.418 0 01.167-.037h4.525c1.45 0 2.625 1.324 2.63 2.962.014 5.094-1.107 14.388-11.999 16.038a17.848 17.848 0 001.685-3.137zM11.813 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728H21.61V33.703h-9.798z"
          fill="#F59E0B"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#E38F00"
        />
      </g>
      <defs>
        <clipPath id="clip0_8133_23520">
          <path fill="#fff" transform="translate(.5)" d="M0 0H42V43.7444H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function TrophySilver(props: IconProps) {
  return (
    <svg
      width={43}
      height={44}
      viewBox="0 0 43 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#clip0_8133_23520)">
        <path
          d="M11.815 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728h11.353V33.703h-9.798z"
          fill="#A7B2C0"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#727272"
        />
        <path
          d="M21.498 34H11.482s-.146-1.626 2.626-2.168c2.773-.543 3.102-.444 3.118-3.193.006-1.306 0-3.193 0-3.193-.02-.636 1.59-2.017-.053-2.47-1.62-.446-6.921-3.014-9.12-8.974C5.856 8.044 5.499 3.665 5.499 1.232 5.5 1.231 5.353 0 8.2 0h13.299v34z"
          fill="#C1C7D4"
        />
        <path
          d="M10.815 17.494c-7.896-2.87-7.884-9.335-7.862-12.6 0-.253.184-.46.412-.46h4.636a47.657 47.657 0 01-.178-2.766.417.417 0 00-.167-.037H3.13c-1.45 0-2.625 1.324-2.63 2.962C.486 9.687 1.607 18.98 12.5 20.63a17.967 17.967 0 01-1.685-3.137z"
          fill="#C1C7D4"
        />
        <path
          d="M21.498 34h10.016s.146-1.626-2.626-2.168c-2.772-.543-3.102-.444-3.118-3.193-.006-1.306 0-3.193 0-3.193.02-.636-1.59-2.017.053-2.47 1.62-.446 6.921-3.014 9.12-8.974 2.198-5.957 2.554-10.336 2.554-12.77 0 0 .146-1.232-2.7-1.232H21.498v34z"
          fill="#A7B2C0"
        />
        <path
          d="M32.185 17.494c7.896-2.87 7.884-9.335 7.862-12.6 0-.253-.184-.46-.412-.46h-4.636c.096-1.038.15-1.963.178-2.766a.418.418 0 01.167-.037h4.525c1.45 0 2.625 1.324 2.63 2.962.014 5.094-1.107 14.388-11.999 16.038a17.848 17.848 0 001.685-3.137zM11.813 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728H21.61V33.703h-9.798z"
          fill="#A7B2C0"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#727272"
        />
      </g>
      <defs>
        <clipPath id="clip0_8133_23520">
          <path fill="#fff" transform="translate(.5)" d="M0 0H42V43.7444H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function TrophyBronze(props: IconProps) {
  return (
    <svg
      width={43}
      height={44}
      viewBox="0 0 43 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g clipPath="url(#clip0_8133_23520)">
        <path
          d="M11.815 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728h11.353V33.703h-9.798z"
          fill="#CD7F32"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#945414"
        />
        <path
          d="M21.498 34H11.482s-.146-1.626 2.626-2.168c2.773-.543 3.102-.444 3.118-3.193.006-1.306 0-3.193 0-3.193-.02-.636 1.59-2.017-.053-2.47-1.62-.446-6.921-3.014-9.12-8.974C5.856 8.044 5.499 3.665 5.499 1.232 5.5 1.231 5.353 0 8.2 0h13.299v34z"
          fill="#D4873B"
        />
        <path
          d="M10.815 17.494c-7.896-2.87-7.884-9.335-7.862-12.6 0-.253.184-.46.412-.46h4.636a47.657 47.657 0 01-.178-2.766.417.417 0 00-.167-.037H3.13c-1.45 0-2.625 1.324-2.63 2.962C.486 9.687 1.607 18.98 12.5 20.63a17.967 17.967 0 01-1.685-3.137z"
          fill="#D4873B"
        />
        <path
          d="M21.498 34h10.016s.146-1.626-2.626-2.168c-2.772-.543-3.102-.444-3.118-3.193-.006-1.306 0-3.193 0-3.193.02-.636-1.59-2.017.053-2.47 1.62-.446 6.921-3.014 9.12-8.974 2.198-5.957 2.554-10.336 2.554-12.77 0 0 .146-1.232-2.7-1.232H21.498v34z"
          fill="#CD7F32"
        />
        <path
          d="M32.185 17.494c7.896-2.87 7.884-9.335 7.862-12.6 0-.253-.184-.46-.412-.46h-4.636c.096-1.038.15-1.963.178-2.766a.418.418 0 01.167-.037h4.525c1.45 0 2.625 1.324 2.63 2.962.014 5.094-1.107 14.388-11.999 16.038a17.848 17.848 0 001.685-3.137zM11.813 33.703v6.453h-1.46c-.795 0-1.439.827-1.439 1.85v.01c0 .956.603 1.728 1.344 1.728H21.61V33.703h-9.798z"
          fill="#CD7F32"
        />
        <path
          d="M31.411 33.703v6.453h1.46c.796 0 1.44.827 1.44 1.85v.01c0 .956-.603 1.728-1.344 1.728H21.613V33.703h9.798z"
          fill="#945414"
        />
      </g>
      <defs>
        <clipPath id="clip0_8133_23520">
          <path fill="#fff" transform="translate(.5)" d="M0 0H42V43.7444H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function BotIcon(props: IconProps) {
  return (
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M4 15.5a2 2 0 110-4m16 4a2 2 0 000-4M7 7V4m10 3V4M7 4a1 1 0 100-2 1 1 0 000 2zM17 4a1 1 0 100-2 1 1 0 000 2zM13.5 7h-3c-2.828 0-4.243 0-5.121.909-.878.909-.879 2.372-.879 5.298s0 4.389.879 5.298c.878.909 2.293.909 5.121.909h1.025c.792 0 1.071.163 1.617.757.603.657 1.537 1.534 2.382 1.738 1.201.29 1.336-.111 1.068-1.256-.076-.326-.267-.847-.066-1.151.113-.17.3-.212.675-.296.591-.132 1.079-.348 1.42-.701.879-.91.879-2.372.879-5.298s0-4.389-.879-5.298C17.743 7 16.328 7 13.5 7z"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 15c.57.607 1.478 1 2.5 1s1.93-.393 2.5-1m-5.491-4H9m6.009 0H15"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ArrowUturnLeftIcon(props: IconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth="1.5"
      stroke="currentColor"
      className="size-6"
      {...props}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"
      />
    </svg>
  );
}

export function ArrowTopRightOnSquareIcon(props: IconProps) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
      />
    </svg>
  );
}

export const ShuffleIcon = (props: IconProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...props}
  >
    <g clipPath="url(#clip0_shuffle)">
      <path
        d="M18 4L21 7L18 10"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18 20L21 17L18 14"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 7H6C7.32608 7 8.59785 7.52678 9.53553 8.46447C10.4732 9.40215 11 10.6739 11 12C11 13.3261 11.5268 14.5979 12.4645 15.5355C13.4021 16.4732 14.6739 17 16 17H21"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M21 7.00001H16C14.9179 6.99841 13.8647 7.34946 13 8.00001M9 16C8.13506 16.6502 7.08204 17.0012 6 17H3"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_shuffle">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);
